import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import StatementCutRow from '../components/StatementCutRow'; import AndroidReceiptMatchBadge, { useAndroidReceiptMatches } from '../components/AndroidReceiptMatchBadge';
import { cutFitsBetween, groupStatementCuts } from '../components/statementCutTimeline';
import { useRecoverableRead, isReadCancelled } from '../hooks/useRecoverableRead';
import ReadRecoveryNotice from '../components/ReadRecoveryNotice';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { ClientChannelsDialog } from '../components/client-channels/ClientChannelsDialog';
import { ClientCommandsControl } from '../components/client-channels/ClientCommandsControl';
import { compareReceiptsNewest, receiptSortValue } from '../components/receipt-ingest/ordering';
import { ReceiptInbox } from '../components/receipt-ingest/ReceiptInbox'; import { RecoverFailed } from '../components/receipt-ingest/RecoverFailed';
import { ReceiptDateCell } from '../components/receipt-ingest/ReceiptDateCell';
import { applyManualReconciliation } from '../components/receipt-ingest/manualReconciliation';
import { LogOut, Plus, Upload, Download, Wallet, Calendar, DollarSign, FileText, Trash2, X, RotateCcw, FolderPlus, Edit, FileSpreadsheet, Image as ImageIcon, Eye, EyeOff, ChevronDown, ChevronUp, PlusCircle, Link2, Copy, Loader2, Building2, User, Clock, Bell, Calculator, CheckCircle, Check, Settings, Key, Activity, LogOut as LogOutIcon, AlertCircle, Shield, ExternalLink, FileDown, Info, Search, LayoutGrid, List, MessageCircle, Send, Megaphone , BarChart3, AlertTriangle, RefreshCw, FileQuestion, Heart } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { usePortfolio } from '../contexts/PortfolioContext';
import useReceiptsWebSocket from '../hooks/useReceiptsWebSocket';
import * as XLSX from 'xlsx';

export default function Dashboard({ onLogout }) {
  const { activePortfolio: activeCartera } = usePortfolio(); // Alias para nueva estructura
  const [user, setUser] = useState(null);
  
  // Obtener rol del usuario desde localStorage
  const storedUser = localStorage.getItem('user');
  const userRole = storedUser ? JSON.parse(storedUser).role : 'admin';
  
  // Permisos según rol
  const isReadOnly = userRole === 'auditor' || userRole === 'cliente';
  const canWrite = !isReadOnly;
  const [clientes, setClientes] = useState([]);

  // Helper para formatear montos en formato argentino
  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return '$0,00';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // ========== SISTEMA DE CACHÉ PARA DASHBOARD ==========
  // Funciones auxiliares para manejar localStorage caché
  // v2 (2026-08-02): se sube la versión de la clave para descartar las cachés viejas que
  // pueden estar marcadas como al día sin contener los pagos /pago (ver FIX en handleNewReceipt).
  // v3 (2026-08-06): descarta cachés envenenadas por handleReceiptReconciled (timestamp
  // fresco con lista incompleta cuando el evento new_receipt no llegó).
  const getCacheKey = (clienteId) => `receipts_cache_v4_${clienteId}`;
  
  const getFromCache = (clienteId) => {
    try {
      const cacheKey = getCacheKey(clienteId);
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        console.log(`📦 Cache HIT para cliente ${clienteId} - ${parsed.receipts.length} receipts`);
        return parsed;
      }
      console.log(`📦 Cache MISS para cliente ${clienteId}`);
      return null;
    } catch (error) {
      console.error('❌ Error leyendo caché:', error);
      return null;
    }
  };
  
  const saveToCache = (clienteId, receipts, timestamp) => {
    try {
      const cacheKey = getCacheKey(clienteId);
      const data = {
        receipts,
        timestamp,
        cachedAt: new Date().toISOString()
      };
      localStorage.setItem(cacheKey, JSON.stringify(data));
      console.log(`💾 Cache SAVED para cliente ${clienteId} - ${receipts.length} receipts - timestamp: ${timestamp}`);
    } catch (error) {
      console.error('❌ Error guardando en caché:', error);
      // Si localStorage está lleno, limpiar cachés antiguos
      if (error.name === 'QuotaExceededError') {
        clearOldCaches();
        // Intentar de nuevo
        try {
          const cacheKey = getCacheKey(clienteId);
          const data = { receipts, timestamp, cachedAt: new Date().toISOString() };
          localStorage.setItem(cacheKey, JSON.stringify(data));
        } catch (retryError) {
          console.error('❌ No se pudo guardar en caché después de limpiar:', retryError);
        }
      }
    }
  };
  
  const clearOldCaches = () => {
    try {
      // Limpiar cachés de más de 7 días
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter(key => key.startsWith('receipts_cache_'));
      const now = new Date();
      
      cacheKeys.forEach(key => {
        try {
          const cached = JSON.parse(localStorage.getItem(key));
          const cachedDate = new Date(cached.cachedAt);
          const daysDiff = (now - cachedDate) / (1000 * 60 * 60 * 24);
          
          if (daysDiff > 7) {
            localStorage.removeItem(key);
            console.log(`🗑️ Caché antiguo eliminado: ${key}`);
          }
        } catch (err) {
          // Si hay error parseando, eliminar ese caché corrupto
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('❌ Error limpiando cachés antiguos:', error);
    }
  };
  
  const invalidateCache = (clienteId) => {
    try {
      const cacheKey = getCacheKey(clienteId);
      localStorage.removeItem(cacheKey);
      console.log(`🗑️ Cache INVALIDADO para cliente ${clienteId}`);
    } catch (error) {
      console.error('❌ Error invalidando caché:', error);
    }
  };

  // Actualiza el caché con una nueva lista de receipts + timestamp.
  // Usado por los handlers de WebSocket para persistir los cambios en vivo
  // y evitar que al volver al cliente el caché quede stale.
  const updateCacheReceipts = (clienteId, receiptsList, timestamp) => {
    try {
      const cacheKey = getCacheKey(clienteId);
      const existing = localStorage.getItem(cacheKey);
      // Si NO hay caché previo, no creamos uno nuevo desde aquí: el usuario
      // todavía no cargó este cliente, y crear un caché parcial podría dejar
      // una lista incompleta que enmascare un fetch legítimo.
      if (!existing) {
        console.log(`📦 Cache UPDATE skip (no cache previo) para cliente ${clienteId}`);
        return;
      }
      const data = {
        receipts: receiptsList,
        timestamp,
        cachedAt: new Date().toISOString()
      };
      localStorage.setItem(cacheKey, JSON.stringify(data));
      console.log(`🔄 Cache UPDATED para cliente ${clienteId} - ${receiptsList.length} receipts - timestamp: ${timestamp}`);
    } catch (error) {
      console.error('❌ Error actualizando caché (fallback a invalidar):', error);
      try { localStorage.removeItem(getCacheKey(clienteId)); } catch (_) {}
    }
  };
  // ========== FIN SISTEMA DE CACHÉ ==========

  const [receipts, setReceipts] = useState([]);
  // FASE BAL (2026-02): snapshot baseline del cliente seleccionado, usado para mostrar
  // la columna "Saldo" (running balance) al lado de cada movimiento POST-baseline.
  const [balanceSnapshot, setBalanceSnapshot] = useState(null);
  const [selectedCliente, setSelectedCliente] = useState(null); const androidMatches = useAndroidReceiptMatches(selectedCliente, userRole === 'admin');
  const clientRead = useRecoverableRead(activeCartera?.id);
  const receiptRead = useRecoverableRead(`${activeCartera?.id}:${selectedCliente}`);
  const [loading, setLoading] = useState(true);
  const [switchingCartera, setSwitchingCartera] = useState(false);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  
  // Estados para paginación de receipts
  const [receiptsOffset, setReceiptsOffset] = useState(0);
  const [cortesEstadoCuenta, setCortesEstadoCuenta] = useState([]);
  // Estado de auditoria por cliente: { [cliente_id]: { ultimo_corte, silencioso, auditado } }
  const [auditoriaByCliente, setAuditoriaByCliente] = useState({});
  const [hasMoreReceipts, setHasMoreReceipts] = useState(false);
  const [loadingMoreReceipts, setLoadingMoreReceipts] = useState(false);
  const RECEIPTS_PER_PAGE = 100;
  const [showClienteDialog, setShowClienteDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [newClienteName, setNewClienteName] = useState('');
  
  // Comprobantes perdidos (gaps) - recuperación manual
  const [gapsByClient, setGapsByClient] = useState({}); // { [cliente_id]: { gaps, since, cartera_id_default } }
  const [gapsModalCliente, setGapsModalCliente] = useState(null); // account object actualmente en modal
  const [recoveringGapId, setRecoveringGapId] = useState(null);

  // Caché simple para recibos ya cargados
  const receiptsCache = useRef({});
  const lastLoadedClienteId = useRef(null); // Nuevo: rastrear último cliente cargado
  const fileInputRef = useRef(null); // Ref para resetear el input
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [editClienteName, setEditClienteName] = useState('');
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewReceipt, setPreviewReceipt] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Estados para movimientos manuales
  const [showManualEntryDialog, setShowManualEntryDialog] = useState(false);
  // Modal de reubicacion cronologica para receipts recuperados via 🎯
  const [recoveryReloc, setRecoveryReloc] = useState(null); // { receipt, neighbors: [...] }
  const [manualEntryType, setManualEntryType] = useState('manual_credit');
  const [manualAmount, setManualAmount] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  const [manualDate, setManualDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [manualDestination, setManualDestination] = useState('');
  const [manualFile, setManualFile] = useState(null);
  const [manualLoading, setManualLoading] = useState(false);
  const [targetClienteForManual, setTargetClienteForManual] = useState(null);
  
  // Estados para editar movimiento manual
  const [showEditManualDialog, setShowEditManualDialog] = useState(false);
  const [editingManualReceipt, setEditingManualReceipt] = useState(null);
  const [editManualType, setEditManualType] = useState('manual_credit');
  const [editManualAmount, setEditManualAmount] = useState('');
  const [editManualDescription, setEditManualDescription] = useState('');
  const [editManualDate, setEditManualDate] = useState('');
  const [editManualDestination, setEditManualDestination] = useState('');
  const [editManualLoading, setEditManualLoading] = useState(false);
  const [retryingOcr, setRetryingOcr] = useState(null); // receipt_id siendo reprocesado
  const [processingOCR, setProcessingOCR] = useState(false);
  
  // Estados para clientes finales
  const [finalCustomers, setFinalCustomers] = useState({});
  
  // Estados para notificaciones en tiempo real
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [seenNotificationIds, setSeenNotificationIds] = useState(new Set());
  
  // Estado para vista simple
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('dashboard_view_mode') || 'grid';
  });
  
  // Estado para ordenamiento en vista simple
  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('dashboard_sort_order') || 'default';
  });
  
  // Estados para Financiera
  const [showFinancieraModal, setShowFinancieraModal] = useState(false);
  const [showInitialBalanceModal, setShowInitialBalanceModal] = useState(false);
  const [financieraSummary, setFinancieraSummary] = useState(null);
  const [financieraMovements, setFinancieraMovements] = useState([]);
  const [financieraLoading, setFinancieraLoading] = useState(false);
  const [initialBalanceInput, setInitialBalanceInput] = useState('');
  
  // Estados para solicitudes de eliminación
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [showDeletionRequestsDialog, setShowDeletionRequestsDialog] = useState(false);
  const [processingRequest, setProcessingRequest] = useState(null);
  
  // Estados para reconciliación manual
  const [showManualReconciliationDialog, setShowManualReconciliationDialog] = useState(false);
  const [receiptToReconcile, setReceiptToReconcile] = useState(null);
  const [manualReconciliationStatus, setManualReconciliationStatus] = useState('matched');
  const [manualReconciliationComment, setManualReconciliationComment] = useState('');
  const [updatingReconciliation, setUpdatingReconciliation] = useState(false);
  const reconciliationTriggerRef = useRef(null);
  
  // Estados para pagos a clientes
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDescription, setPaymentDescription] = useState('');
  const [targetCustomerForPayment, setTargetCustomerForPayment] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentNegativeConfirm, setPaymentNegativeConfirm] = useState(false);
  
  // Estados para filtro de fechas
  const [dateFilterEnabled, setDateFilterEnabled] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Estados para filtro de destinatarios (AGREGADO 2025-11-04)
  const [availableDestinatarios, setAvailableDestinatarios] = useState([]);
  const [selectedDestinatario, setSelectedDestinatario] = useState('all');
  const [loadingDestinatarios, setLoadingDestinatarios] = useState(false);
  
  // Ref al cliente seleccionado actual: lo usan los callbacks del WebSocket
  // (que tienen deps `[]` para no reiniciar el hook) para leer el valor más
  // reciente sin depender del closure.
  const selectedClienteRef = useRef(null);
  useEffect(() => {
    selectedClienteRef.current = selectedCliente;
  }, [selectedCliente]);

  // Callback para manejar nuevos comprobantes desde WebSocket
  // `handleNewReceipt`: actualiza UI cuando llega un comprobante por WebSocket.
  // Usamos functional updates + ref a selectedCliente para NO depender del
  // closure; con deps `[]` el callback nunca cambia de referencia → el hook no
  // reinicia el WS.
  const handleNewReceipt = useCallback((data) => {
    console.log('🎯 Procesando nuevo comprobante:', data);
    const incomingClienteId = data?.receipt?.cliente_id;
    if (!incomingClienteId) return;

    // selectedCliente es un STRING (id), no un objeto
    const currentClienteId = selectedClienteRef.current;
    const esMismoCliente = currentClienteId === incomingClienteId;

    const incomingBalance = Number(data?.balance_nuevo);
    if (Number.isFinite(incomingBalance)) {
      setClientes(prev => prev.map(cliente =>
        cliente.id === incomingClienteId
          ? { ...cliente, balance: incomingBalance }
          : cliente
      ));
      setFinalCustomers(prev => {
        const current = prev[incomingClienteId];
        if (!current) return prev;
        return {
          ...prev,
          [incomingClienteId]: { ...current, balance: incomingBalance }
        };
      });
    }

    // Un comprobante nuevo invalida la auditoria: la tarjeta vuelve al color normal
    setAuditoriaByCliente(prev => (
      prev[incomingClienteId]?.auditado
        ? { ...prev, [incomingClienteId]: { ...prev[incomingClienteId], auditado: false } }
        : prev
    ));

    if (esMismoCliente) {
      // Insertar receipt si no existe (functional update + persistir caché)
      setReceipts(prev => {
        const exists = prev.some(r => r.id === data.receipt.id);
        if (exists) {
          console.log('⚠️ Comprobante ya existe en la lista, ignorando duplicado');
          return prev;
        }
        const nextList = [data.receipt, ...prev];
        // 🐛 FIX 2026-08-02: antes esto persistia la lista en memoria como caché "fresca"
        // con el timestamp del receipt entrante. Si mientras tanto se habia creado un
        // movimiento SIN WebSocket (los pagos /pago no emiten evento), no estaba en `prev`
        // y la caché quedaba marcada como al dia sin contenerlo → el pago desaparecia de
        // la lista PARA SIEMPRE, incluso recargando. Invalidamos y que refetchee.
        invalidateCache(incomingClienteId);
        return nextList;
      });

      toast.success('💰 Nuevo comprobante recibido', {
        description: `${formatAmount(data.receipt.amount)} - ${data.receipt.origin_holder || 'N/A'}`,
        duration: 5000,
      });
    } else {
      // Si llegó un receipt de OTRO cliente, invalidar su caché para que
      // la próxima visita haga fetch fresco.
      invalidateCache(incomingClienteId);
    }
  }, []);
  
  // Callback para manejar comprobantes conciliados en tiempo real
  const handleReceiptReconciled = useCallback((data) => {
    console.log('✅ Comprobante conciliado en tiempo real:', data);

    const clienteId = data?.receipt?.cliente_id;
    const incomingTs =
      data?.receipt?.updated_at ||
      data?.receipt?.created_at ||
      new Date().toISOString();

    // Actualizar el comprobante en la lista existente y persistir caché
    setReceipts(prev => {
      const exists = prev.some(r => r.id === data.receipt.id);
      // 🐛 FIX 2026-08-06: si el receipt NO está en la lista (su evento
      // `new_receipt` se perdió), antes se persistía la lista incompleta con un
      // timestamp fresco → la caché quedaba "al día" sin el movimiento y este
      // desaparecía del Dashboard incluso recargando. Ahora lo insertamos e
      // invalidamos la caché para forzar un fetch limpio.
      if (!exists) {
        const nextList = [data.receipt, ...prev];
        if (clienteId) invalidateCache(clienteId);
        return nextList;
      }
      const nextList = prev.map(r =>
        r.id === data.receipt.id
          ? { ...r, ...data.receipt, reconciliation_status: data.receipt.reconciliation_status || 'push_matched' || 'santander_worker_realtime' }
          : r
      );
      if (clienteId) {
        updateCacheReceipts(clienteId, nextList, incomingTs);
      }
      return nextList;
    });

    // Notificacion visual
    toast.success('Comprobante conciliado automaticamente', {
      description: (data.notification_app || 'Push') + ' - ' + (data.reconciliation_type || 'auto'),
      duration: 5000
    });
  }, []);

  // Conectar WebSocket para el cliente seleccionado
  // selectedCliente es un STRING (cliente_id), NO un objeto
  const { isConnected } = useReceiptsWebSocket(
    selectedCliente,
    handleNewReceipt,
    handleReceiptReconciled
  );
  
  // Estado para re-procesamiento
  const [reprocessing, setReprocessing] = useState(false);
  const [deepReconciling, setDeepReconciling] = useState(false);
  const [recalculatingBalances, setRecalculatingBalances] = useState(false);

  // Estados para configuración de cliente (CBU recaudadora)
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [configCustomerId, setConfigCustomerId] = useState(null);
  const [configDestinationAccount, setConfigDestinationAccount] = useState('');
  const [configLoading, setConfigLoading] = useState(false);


  // Estados para gestión de PIN y sesiones
  const [showPINDialog, setShowPINDialog] = useState(false);
  const [regeneratedPIN, setRegeneratedPIN] = useState(null);
  const [showAccessLogsDialog, setShowAccessLogsDialog] = useState(false);
  const [accessLogs, setAccessLogs] = useState([]);
  const [selectedClientForLogs, setSelectedClientForLogs] = useState(null);
  
  // Estados para Sistema de Créditos
  const [creditRequests, setCreditRequests] = useState([]);
  const [showCreditRequestsDialog, setShowCreditRequestsDialog] = useState(false);
  const [selectedClienteForCredit, setSelectedClienteForCredit] = useState(null);
  const [updatingDescubierto, setUpdatingDescubierto] = useState(false);
  const [editingDescubierto, setEditingDescubierto] = useState(null); // cliente_id
  const [newDescubierto, setNewDescubierto] = useState('');
  
  // Estados para Sistema de Mesas - ELIMINADO
  const [selectedClienteForMesa, setSelectedClienteForMesa] = useState(null);

  // Estados para confirmación de regeneración de PIN
  const [showPINConfirmDialog, setShowPINConfirmDialog] = useState(false);
  const [customerForPINRegen, setCustomerForPINRegen] = useState(null);
  const [pinRegenLoading, setPinRegenLoading] = useState(false);

  // Estados para configuración de Telegram
  const [channelClient, setChannelClient] = useState(null);
  const [showTelegramConfigDialog, setShowTelegramConfigDialog] = useState(false);
  const [telegramConfigLoading, setTelegramConfigLoading] = useState(false);
  const [selectedClienteForTelegram, setSelectedClienteForTelegram] = useState(null);
  const [telegramConfig, setTelegramConfig] = useState({
    grupo_telegram_id: '',
    admin_financiera_telegram_id: '',
    telegram_users_autorizados: '',  // IDs separados por comas
    miniapp_tipo: null,
    club_id: null,
    agencia_id: null
  });

  // Estados para dropdowns de Mi Club
  const [clubsList, setClubsList] = useState([]);
  const [agenciasList, setAgenciasList] = useState([]);
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [loadingAgencias, setLoadingAgencias] = useState(false);

  // Estados para configuración de WhatsApp Bot (Nueva integración Baileys)
  const [showWhatsAppConfigDialog, setShowWhatsAppConfigDialog] = useState(false);
  const [whatsappConfigLoading, setWhatsappConfigLoading] = useState(false);
  const [selectedClienteForWhatsApp, setSelectedClienteForWhatsApp] = useState(null);
  const [whatsappBotStatus, setWhatsappBotStatus] = useState(null);
  const [whatsappMatchingGroups, setWhatsappMatchingGroups] = useState([]);
  const [whatsappSelectedGroup, setWhatsappSelectedGroup] = useState(null);
  const [whatsappGroupClients, setWhatsappGroupClients] = useState([]);
  // Estado para enviar recordatorios
  const [enviandoRecordatorio, setEnviandoRecordatorio] = useState({});
  const [enviandoEstadoCuenta, setEnviandoEstadoCuenta] = useState({});
  const [enviandoFlyers, setEnviandoFlyers] = useState({});
  const [showFlyerCronDialog, setShowFlyerCronDialog] = useState(false);
  const [showPppokerConfigModal, setShowPppokerConfigModal] = useState(false);
  const [pppokerConfigTarget, setPppokerConfigTarget] = useState(null);
  const [pppokerConfig, setPppokerConfig] = useState({ club_id: '', agentes: [{ uid: '', nombre: '', rakeback_pct: '15', spin_pct: '10' }] });
  const [pppokerConfigLoading, setPppokerConfigLoading] = useState(false);
  const [pppokerConfigSaving, setPppokerConfigSaving] = useState(false);
  const [pppokerPreview, setPppokerPreview] = useState(null);
  const [showPppokerPreview, setShowPppokerPreview] = useState(false);
  const [pppokerPreviewLoading, setPppokerPreviewLoading] = useState(false);
  const [flyerCronTarget, setFlyerCronTarget] = useState(null);
  const [flyerCronConfig, setFlyerCronConfig] = useState(null);
  const [flyerCronHour, setFlyerCronHour] = useState(9);
  const [flyerCronMinute, setFlyerCronMinute] = useState(0);
  const [flyerCronLoading, setFlyerCronLoading] = useState(false);
  const [flyerCronSaving, setFlyerCronSaving] = useState(false);
  const [flyerCronConflict, setFlyerCronConflict] = useState(null);
  const [allFlyerCrons, setAllFlyerCrons] = useState([]);
  const [whatsappConfig, setWhatsappConfig] = useState({
    grupo_id: '',
    grupo_name: '',
    numeros_autorizados: [],
    numero_whatsapp_cliente: ''
  });
  const [whatsappAllGroups, setWhatsappAllGroups] = useState([]);
  const [whatsappGroupSearch, setWhatsappGroupSearch] = useState('');
  const [whatsappStaffMembers, setWhatsappStaffMembers] = useState([]);
  const [whatsappConfigPreflight, setWhatsappConfigPreflight] = useState(null);
  const [whatsappConfigTesting, setWhatsappConfigTesting] = useState(false);



  // Función para exportar a Excel
  const exportAccountToExcel = async (clienteId, clienteName) => {
    try {
      toast.info("Generando archivo Excel...");
      
      const response = await axios.get(`${API}/clientes/${clienteId}/export-excel`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Historial_${clienteName.replace(/\s/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Historial de ${clienteName} exportado a Excel`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al exportar historial");
    }
  };


  // Funciones para configuración de Telegram
  // Función para cargar lista de clubs
  const loadClubsList = async () => {
    setLoadingClubs(true);
    try {
      const response = await axios.get(`${API}/poker/clubs/unique-list`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setClubsList(response.data);
    } catch (error) {
      console.error('Error cargando clubs:', error);
      toast.error('Error cargando lista de clubs');
    } finally {
      setLoadingClubs(false);
    }
  };

  // Función para cargar lista de agencias
  const loadAgenciasList = async () => {
    setLoadingAgencias(true);
    try {
      const response = await axios.get(`${API}/poker/agencias/unique-list`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAgenciasList(response.data);
    } catch (error) {
      console.error('Error cargando agencias:', error);
      toast.error('Error cargando lista de agencias');
    } finally {
      setLoadingAgencias(false);
    }
  };

  const openTelegramConfigDialog = async (cliente) => {
    setSelectedClienteForTelegram(cliente);
    setTelegramConfigLoading(true);
    
    try {
      // Cargar configuración existente
      const response = await axios.get(`${API}/clientes/${cliente.id}/telegram-config`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.configured) {
        // Cargar usuarios autorizados desde el cliente
        let usuariosAutorizados = '';
        if (cliente.telegram_users_autorizados && Array.isArray(cliente.telegram_users_autorizados)) {
          usuariosAutorizados = cliente.telegram_users_autorizados.join(', ');
        }
        
        setTelegramConfig({
          grupo_telegram_id: response.data.grupo_telegram_id,
          admin_financiera_telegram_id: response.data.admin_financiera_telegram_id,
          telegram_users_autorizados: usuariosAutorizados,
          miniapp_tipo: response.data.miniapp_tipo || null,
          club_id: response.data.club_id || null,
          agencia_id: response.data.agencia_id || null
        });
        
        // Cargar listas si ya tiene tipo configurado
        if (response.data.miniapp_tipo === 'club') {
          loadClubsList();
        } else if (response.data.miniapp_tipo === 'agencia') {
          loadAgenciasList();
        }
      } else {
        setTelegramConfig({
          grupo_telegram_id: '',
          admin_financiera_telegram_id: '',
          telegram_users_autorizados: '',
          miniapp_tipo: null,
          club_id: null,
          agencia_id: null
        });
      }
    } catch (error) {
      console.error('Error cargando configuración de Telegram:', error);
      setTelegramConfig({
        grupo_telegram_id: '',
        admin_financiera_telegram_id: '',
        telegram_users_autorizados: '',
        miniapp_tipo: null,
        club_id: null,
        agencia_id: null
      });
    } finally {
      setTelegramConfigLoading(false);
    }
    
    setShowTelegramConfigDialog(true);
  };
  
  const saveTelegramConfig = async () => {
    // Validar campos de miniapp si está configurado
    if (telegramConfig.miniapp_tipo === 'club' && !telegramConfig.club_id) {
      toast.error('Por favor selecciona un club');
      return;
    }
    
    if (telegramConfig.miniapp_tipo === 'agencia' && !telegramConfig.agencia_id) {
      toast.error('Por favor selecciona una agencia');
      return;
    }
    
    setTelegramConfigLoading(true);
    
    try {
      // Procesar usuarios autorizados: convertir string separado por comas a array
      let usuariosArray = [];
      if (telegramConfig.telegram_users_autorizados && telegramConfig.telegram_users_autorizados.trim()) {
        usuariosArray = telegramConfig.telegram_users_autorizados
          .split(',')
          .map(id => id.trim())
          .filter(id => id && /^\d+$/.test(id)); // Solo IDs numéricos válidos
      }
      
      // Preparar datos: convertir strings vacíos a null
      const dataToSend = {
        grupo_telegram_id: telegramConfig.grupo_telegram_id ? parseInt(telegramConfig.grupo_telegram_id) : null,
        grupo_telegram_name: telegramConfig.grupo_telegram_name || null,
        admin_financiera_telegram_id: telegramConfig.admin_financiera_telegram_id || null,
        telegram_users_autorizados: usuariosArray.length > 0 ? usuariosArray : null,
        miniapp_tipo: telegramConfig.miniapp_tipo || null,
        club_id: telegramConfig.club_id || null,
        agencia_id: telegramConfig.agencia_id || null
      };
      
      await axios.post(
        `${API}/clientes/${selectedClienteForTelegram.id}/telegram-config`,
        dataToSend,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast.success('Configuración guardada exitosamente');
      setShowTelegramConfigDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setTelegramConfigLoading(false);
    }
  };
  
  const deleteTelegramConfig = async () => {
    if (!confirm('¿Estás seguro de eliminar la configuración de Telegram?')) {
      return;
    }
    
    setTelegramConfigLoading(true);
    
    try {
      await axios.delete(
        `${API}/clientes/${selectedClienteForTelegram.id}/telegram-config`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      setTelegramConfig({
        grupo_telegram_id: '',
        admin_financiera_telegram_id: '',
        miniapp_tipo: null,
        club_id: null,
        agencia_id: null
      });
      toast.success('Configuración de Telegram eliminada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar configuración');
    } finally {
      setTelegramConfigLoading(false);
    }
  };

  
  // Funciones para configuracion de WhatsApp Bot
  const openWhatsAppConfigDialog = async (cliente) => {
    setSelectedClienteForWhatsApp(cliente);
    setWhatsappConfigLoading(true);
    setWhatsappMatchingGroups([]);
    setWhatsappSelectedGroup(null);
    setWhatsappGroupClients([]);
    setWhatsappStaffMembers([]);
    setWhatsappAllGroups([]);
    setWhatsappGroupSearch('');
    setWhatsappConfigPreflight(null);
    setShowWhatsAppConfigDialog(true);

    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

      // 1. Estado del bot + todos los grupos + config existente en paralelo
      const [statusRes, groupsRes, configRes] = await Promise.all([
        axios.get(`${API}/whatsapp-bot/status`),
        axios.get(`${API}/whatsapp-bot/groups`),
        axios.get(`${API}/whatsapp-bot/client/${cliente.id}/config`, { headers })
      ]);

      setWhatsappBotStatus(statusRes.data);

      if (statusRes.data.status !== 'connected') {
        toast.error('Bot de WhatsApp no conectado');
        setWhatsappConfigLoading(false);
        return;
      }

      // Guardar todos los grupos
      const allGroups = (groupsRes.data.groups || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setWhatsappAllGroups(allGroups);

      // Grupos que matchean por nombre
      const clienteLower = cliente.name.toLowerCase();
      const matching = allGroups.filter(g => (g.name || '').toLowerCase().includes(clienteLower));
      setWhatsappMatchingGroups(matching);

      // Config existente
      const cfg = configRes.data;
      if (cfg && cfg.grupo_id) {
        const selected = allGroups.find(g => g.id === cfg.grupo_id) || null;
        setWhatsappSelectedGroup(selected || { id: cfg.grupo_id, name: cfg.grupo_name || cfg.grupo_id, participants: 0 });
        const cfgState = {
          grupo_id: cfg.grupo_id,
          grupo_name: cfg.grupo_name || '',
          numeros_autorizados: cfg.numeros_autorizados || [],
          numero_whatsapp_cliente: cfg.numero_whatsapp_cliente || (cfg.numeros_autorizados?.[0] || '')
        };
        setWhatsappConfig(cfgState);
        await loadGroupParticipants(cfg.grupo_id);

        // 🩺 Auto-health-check: correr preflight con la config guardada para
        // detectar grupos donde el cliente no envía comprobantes (issue FORASTEROS).
        try {
          const preRes = await axios.post(
            `${API}/whatsapp-bot/client/${cliente.id}/config/preflight`,
            cfgState,
            { headers }
          );
          setWhatsappConfigPreflight(preRes.data);
        } catch (e) {
          // silencioso — el admin puede correr "Probar" manualmente
        }
      } else {
        setWhatsappConfig({ grupo_id: '', grupo_name: '', numeros_autorizados: [], numero_whatsapp_cliente: '' });
      }
    } catch (error) {
      console.error('Error cargando config WhatsApp:', error);
      setWhatsappConfig({ grupo_id: '', grupo_name: '', numeros_autorizados: [], numero_whatsapp_cliente: '' });
    } finally {
      setWhatsappConfigLoading(false);
    }
  };
  
  const loadGroupParticipants = async (groupId) => {
    try {
      const res = await axios.get(`${API}/whatsapp-bot/group/${encodeURIComponent(groupId)}/participants`);
      if (res.data) {
        setWhatsappGroupClients(res.data.clients || []);
        setWhatsappStaffMembers(res.data.staff || []);
        return res.data;
      }
    } catch (error) {
      console.error('Error cargando participantes:', error);
    }
    return null;
  };
  
  const selectWhatsAppGroup = async (group) => {
    setWhatsappSelectedGroup(group);
    setWhatsappConfigPreflight(null);
    const detail = await loadGroupParticipants(group.id);
    const availablePhones = new Set((detail?.clients || []).map(c => c.phone).filter(Boolean));
    const currentAuthorized = (whatsappConfig.numeros_autorizados || []).filter(phone => availablePhones.has(phone));
    setWhatsappConfig({
      ...whatsappConfig,
      grupo_id: group.id,
      grupo_name: group.name,
      numeros_autorizados: currentAuthorized,
      numero_whatsapp_cliente: currentAuthorized[0] || ''
    });
  };
  
  const toggleClientNumber = (phone) => {
    if (!phone) {
      toast.error('Este participante solo muestra LID; no tiene teléfono autorizable visible');
      return;
    }
    const current = whatsappConfig.numeros_autorizados || [];
    if (current.includes(phone)) {
      const next = current.filter(p => p !== phone);
      setWhatsappConfig({
        ...whatsappConfig,
        numeros_autorizados: next,
        numero_whatsapp_cliente: next[0] || ''
      });
    } else {
      const next = [...current, phone];
      setWhatsappConfig({
        ...whatsappConfig,
        numeros_autorizados: next,
        numero_whatsapp_cliente: whatsappConfig.numero_whatsapp_cliente || phone
      });
    }
    setWhatsappConfigPreflight(null);
  };
  
  const selectAllClientNumbers = () => {
    const allPhones = whatsappGroupClients.map(c => c.phone).filter(Boolean);
    setWhatsappConfig({
      ...whatsappConfig,
      numeros_autorizados: allPhones,
      numero_whatsapp_cliente: allPhones[0] || ''
    });
    setWhatsappConfigPreflight(null);
  };
  const buildWhatsAppConfigPayload = () => ({
    grupo_id: whatsappConfig.grupo_id,
    grupo_name: whatsappConfig.grupo_name,
    numeros_autorizados: whatsappConfig.numeros_autorizados || [],
    numero_whatsapp_cliente: whatsappConfig.numero_whatsapp_cliente || ((whatsappConfig.numeros_autorizados || [])[0] || '')
  });
  const testWhatsAppConfig = async ({ silent = false } = {}) => {
    if (!selectedClienteForWhatsApp?.id) return null;
    if (!whatsappConfig.grupo_id) {
      if (!silent) toast.error('Selecciona un grupo de WhatsApp');
      return null;
    }
    if ((whatsappConfig.numeros_autorizados || []).length === 0) {
      if (!silent) toast.error('Selecciona al menos un número autorizado');
      return null;
    }
    setWhatsappConfigTesting(true);
    try {
      const res = await axios.post(
        `${API}/whatsapp-bot/client/${selectedClienteForWhatsApp.id}/config/preflight`,
        buildWhatsAppConfigPayload(),
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setWhatsappConfigPreflight(res.data);
      if (!silent) {
        if (res.data.ok) toast.success('Prueba OK: grupo y números validados sin enviar mensajes');
        else toast.error('La prueba detectó problemas antes de guardar');
      }
      return res.data;
    } catch (error) {
      const detail = error.response?.data?.detail || 'No se pudo validar la configuración';
      setWhatsappConfigPreflight({ ok: false, checks: [{ key: 'error', ok: false, label: 'Error', detail }] });
      if (!silent) toast.error(detail);
      return null;
    } finally {
      setWhatsappConfigTesting(false);
    }
  };
  
  const saveWhatsAppConfig = async () => {
    if (!whatsappConfig.grupo_id) {
      toast.error('Selecciona un grupo de WhatsApp');
      return;
    }
    if (whatsappConfig.numeros_autorizados.length === 0) {
      toast.error('Selecciona al menos un numero de cliente');
      return;
    }

    setWhatsappConfigLoading(true);

    try {
      const preflight = await testWhatsAppConfig({ silent: true });
      if (!preflight?.ok) {
        toast.error('Corrige la prueba de configuración antes de guardar');
        return;
      }
      await axios.post(
        `${API}/whatsapp-bot/client/${selectedClienteForWhatsApp.id}/config`,
        buildWhatsAppConfigPayload(),
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      toast.success('Configuración única de WhatsApp guardada');
      setShowWhatsAppConfigDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setWhatsappConfigLoading(false);
    }
  };
  
  const deleteWhatsAppConfig = async () => {
    if (!confirm('¿Eliminar configuración de WhatsApp Bot?')) return;
    
    setWhatsappConfigLoading(true);
    
    try {
      await axios.delete(
        `${API}/whatsapp-bot/client/${selectedClienteForWhatsApp.id}/config`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      setWhatsappConfig({ grupo_id: '', grupo_name: '', numeros_autorizados: [], numero_whatsapp_cliente: '' });
      setWhatsappGroupClients([]);
      setWhatsappStaffMembers([]);
      setWhatsappSelectedGroup(null);
      setWhatsappConfigPreflight(null);
      toast.success('Configuración eliminada');
    } catch (error) {
      toast.error('Error al eliminar');
    } finally {
      setWhatsappConfigLoading(false);
    }
  };



  // Función para enviar recordatorio de pago por WhatsApp
  const enviarRecordatorioWhatsApp = async (clienteId, clienteName) => {
    try {
      setEnviandoRecordatorio(prev => ({ ...prev, [clienteId]: true }));
      
      const response = await axios.post(
        `${API}/clientes/${clienteId}/enviar-recordatorio`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      
      if (response.data.success) {
        const tipo = response.data.tipo;
        const monto = Math.abs(response.data.monto);
        const canales = (response.data.canales || []).join(" + ") || "sin canal";
        const fallidos = response.data.canales_fallidos || [];
        if (tipo === "recordatorio_pago") {
          toast.success(`✅ Recordatorio de pago enviado a ${clienteName} por ${canales} ($${monto.toLocaleString("es-AR")} ARS)`, { duration: 5000 });
        } else if (tipo === "solicitud_cuenta") {
          toast.success(`✅ Solicitud de cuenta enviada a ${clienteName} por ${canales} ($${monto.toLocaleString("es-AR")} ARS a favor)`, { duration: 5000 });
        } else {
          toast.info(response.data.message);
        }
        if (fallidos.length > 0) {
          toast.warning(`No se pudo enviar por ${fallidos.join(" + ")}`, { duration: 7000 });
        }
      } else {
        toast.error(response.data.message || "Error al enviar recordatorio");
      }
    } catch (error) {
      console.error("Error enviando recordatorio:", error);
      toast.error(error.response?.data?.detail || "Error al enviar el recordatorio");
    } finally {
      setEnviandoRecordatorio(prev => ({ ...prev, [clienteId]: false }));
    }
  };
  
  // Función para enviar estado de cuenta por WhatsApp
  const enviarEstadoCuentaWhatsApp = async (clienteId, clienteName) => {
    try {
      setEnviandoEstadoCuenta(prev => ({ ...prev, [clienteId]: true }));
      toast.info(`Generando y enviando estado de cuenta de ${clienteName}...`);
      
      const response = await axios.post(
        `${API}/clientes/${clienteId}/enviar-estado-cuenta-whatsapp`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      
      if (response.data.success) {
        toast.success(`✅ Estado de cuenta enviado a ${clienteName}`, { duration: 5000 });
      } else {
        toast.error(response.data.message || "Error al enviar estado de cuenta");
      }
    } catch (error) {
      console.error("Error enviando estado de cuenta:", error);
      toast.error(error.response?.data?.detail || "Error al enviar estado de cuenta por WhatsApp");
    } finally {
      setEnviandoEstadoCuenta(prev => ({ ...prev, [clienteId]: false }));
    }
  };

  // Refresca el mapa de auditoria (tarjetas pintadas) de la cartera activa
  const refreshAuditoria = async () => {
    if (!activeCartera) return;
    try {
      const res = await axios.get(`${API}/carteras/${activeCartera.id}/auditoria-summary`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setAuditoriaByCliente(res.data || {});
    } catch (err) { console.error("Error recargando auditoria:", err); }
  };

  // Corte en silencio: traza la linea en el dashboard sin enviar nada al grupo
  const cortarEnSilencio = async (clienteId, clienteName) => {
    try {
      setEnviandoEstadoCuenta(prev => ({ ...prev, [clienteId]: true }));
      const response = await axios.post(
        `${API}/clientes/${clienteId}/corte-silencioso`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      if (response.data.success) {
        toast.success(`🔇 ${clienteName}: corte en silencio (no se envió nada)`, { duration: 5000 });
        try {
          const cortesRes = await axios.get(
            `${API}/clientes/${clienteId}/cortes-estado-cuenta`,
            { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
          );
          setCortesEstadoCuenta(cortesRes.data || []);
        } catch (err) { console.error("Error recargando cortes:", err); }
        await refreshAuditoria();
      } else {
        toast.error(response.data.message || "Error registrando el corte");
      }
    } catch (error) {
      console.error("Error en corte silencioso:", error);
      toast.error(error.response?.data?.detail || "Error registrando el corte en silencio");
    } finally {
      setEnviandoEstadoCuenta(prev => ({ ...prev, [clienteId]: false }));
    }
  };

  // Enviar estado de cuenta como solo texto por WhatsApp (sin PDF)
  const enviarEstadoCuentaTexto = async (clienteId, clienteName) => {
    try {
      setEnviandoEstadoCuenta(prev => ({ ...prev, [clienteId]: true }));
      toast.info(`Enviando estado de cuenta (texto) de ${clienteName}...`);
      
      const response = await axios.post(
        `${API}/clientes/${clienteId}/enviar-estado-cuenta-texto`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      
      if (response.data.success) {
        toast.success(response.data.message || `Estado de cuenta (texto) enviado a ${clienteName}`, { duration: 5000 });
        // Recargar cortes para mostrar la nueva linea roja
        try {
          const cortesRes = await axios.get(
            `${API}/clientes/${clienteId}/cortes-estado-cuenta`,
            { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
          );
          setCortesEstadoCuenta(cortesRes.data || []);
        } catch (err) { console.error("Error recargando cortes:", err); }
        await refreshAuditoria();
      } else {
        toast.error(response.data.message || "Error al enviar estado de cuenta");
      }
    } catch (error) {
      console.error("Error enviando estado de cuenta texto:", error);
      toast.error(error.response?.data?.detail || "Error al enviar estado de cuenta por WhatsApp");
    } finally {
      setEnviandoEstadoCuenta(prev => ({ ...prev, [clienteId]: false }));
    }
  };

  // ===== PPPOKER CONFIG + ENVIO =====
  const handleEnviarPppoker = async (clienteId, clienteName) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const res = await axios.get(`${API}/clientes/${clienteId}/pppoker-config`, { headers });
      if (res.data.configured) {
        // Ya tiene config, mostrar preview
        setPppokerPreviewLoading(true);
        setShowPppokerPreview(true);
        setPppokerPreview(null);
        try {
          const previewRes = await axios.post(`${API}/clientes/${clienteId}/preview-estado-cuenta-pppoker`, {}, { headers });
          setPppokerPreview({ ...previewRes.data, clienteId, clienteName });
        } catch (error) {
          toast.error(error.response?.data?.detail || "Error consultando PPPoker");
          setShowPppokerPreview(false);
        } finally {
          setPppokerPreviewLoading(false);
        }
      } else {
        // No tiene config, abrir modal
        setPppokerConfigTarget({ id: clienteId, name: clienteName });
        setPppokerConfig({ club_id: '2534409', agentes: [{ uid: '', nombre: '', rakeback_pct: '15', spin_pct: '10' }] });
        setShowPppokerConfigModal(true);
      }
    } catch (error) {
      toast.error("Error verificando configuracion PPPoker");
    }
  };

  const confirmarEnvioPppoker = async () => {
    if (!pppokerPreview) return;
    const { clienteId, clienteName } = pppokerPreview;
    setShowPppokerPreview(false);
    setEnviandoEstadoCuenta(prev => ({ ...prev, [clienteId]: true }));
    toast.info(`Enviando estado de cuenta + PPPoker de ${clienteName}...`);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const sendRes = await axios.post(`${API}/clientes/${clienteId}/enviar-estado-cuenta-pppoker`, {}, { headers });
      if (sendRes.data.success) {
        const ppk = sendRes.data.pppoker;
        toast.success(`Enviado a ${clienteName}. Total estimado: ${ppk.total_estimado} PP`, { duration: 6000 });
        try {
          const cortesRes = await axios.get(`${API}/clientes/${clienteId}/cortes-estado-cuenta`, { headers });
          setCortesEstadoCuenta(cortesRes.data || []);
        } catch (err) { console.error("Error recargando cortes:", err); }
        await refreshAuditoria();
      } else {
        toast.error(sendRes.data.message || "Error al enviar");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar estado de cuenta PPPoker");
    } finally {
      setEnviandoEstadoCuenta(prev => ({ ...prev, [clienteId]: false }));
    }
  };

  const editPppokerConfig = async (clienteId, clienteName) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const res = await axios.get(`${API}/clientes/${clienteId}/pppoker-config`, { headers });
      if (res.data.configured) {
        const c = res.data.config;
        const agentes = c.agentes || [{ uid: String(c.agent_uid || ''), nombre: 'Principal', rakeback_pct: String(c.rakeback_pct || '15'), spin_pct: String(c.spin_pct || '10') }];
        setPppokerConfig({
          club_id: String(c.club_id || ''),
          agentes: agentes.map(a => ({ uid: String(a.uid || ''), nombre: a.nombre || '', rakeback_pct: String(a.rakeback_pct || ''), spin_pct: String(a.spin_pct || '') })),
        });
      }
      setPppokerConfigTarget({ id: clienteId, name: clienteName });
      setShowPppokerConfigModal(true);
    } catch (error) {
      toast.error("Error cargando configuracion PPPoker");
    }
  };

  const savePppokerConfig = async (andSend = false) => {
    if (!pppokerConfigTarget) return;
    setPppokerConfigSaving(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      await axios.post(`${API}/clientes/${pppokerConfigTarget.id}/pppoker-config`, {
        club_id: parseInt(pppokerConfig.club_id),
        agentes: pppokerConfig.agentes.map(a => ({
          uid: parseInt(a.uid),
          nombre: a.nombre || '',
          rakeback_pct: parseFloat(a.rakeback_pct || 0),
          spin_pct: parseFloat(a.spin_pct || 0),
        })),
      }, { headers });
      toast.success("Configuracion PPPoker guardada");
      setShowPppokerConfigModal(false);
      if (andSend) {
        handleEnviarPppoker(pppokerConfigTarget.id, pppokerConfigTarget.name);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error guardando configuracion");
    } finally {
      setPppokerConfigSaving(false);
    }
  };

  // Abrir modal de configuracion de cron de flyers
  const openFlyerCronDialog = async (clienteId, clienteName) => {
    setFlyerCronTarget({ id: clienteId, name: clienteName });
    setFlyerCronLoading(true);
    setFlyerCronConflict(null);
    setShowFlyerCronDialog(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const [cronRes, allRes] = await Promise.all([
        axios.get(`${API}/clientes/${clienteId}/flyer-cron`, { headers }),
        axios.get(`${API}/flyer-crons`, { headers })
      ]);
      if (cronRes.data.configured) {
        setFlyerCronConfig(cronRes.data.config);
        setFlyerCronHour(cronRes.data.config.hour);
        setFlyerCronMinute(cronRes.data.config.minute);
      } else {
        setFlyerCronConfig(null);
        setFlyerCronHour(9);
        setFlyerCronMinute(0);
      }
      setAllFlyerCrons(allRes.data.configs || []);
    } catch (error) {
      console.error("Error cargando config flyer cron:", error);
      toast.error("Error cargando configuracion de flyers");
    } finally {
      setFlyerCronLoading(false);
    }
  };

  // Guardar cron de flyers
  const saveFlyerCron = async () => {
    if (!flyerCronTarget) return;
    setFlyerCronSaving(true);
    setFlyerCronConflict(null);
    try {
      const response = await axios.post(
        `${API}/clientes/${flyerCronTarget.id}/flyer-cron`,
        { hour: flyerCronHour, minute: flyerCronMinute },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      if (response.data.success) {
        toast.success(response.data.message, { duration: 5000 });
        setFlyerCronConfig(response.data.config);
        setAllFlyerCrons(prev => {
          const filtered = prev.filter(c => c.cliente_id !== flyerCronTarget.id);
          return [...filtered, { ...response.data.config, cliente_id: flyerCronTarget.id }];
        });
        setFlyerCronConflict(null);
      } else if (response.data.conflict) {
        setFlyerCronConflict(response.data);
      } else {
        toast.error(response.data.message || "Error");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error guardando cron");
    } finally {
      setFlyerCronSaving(false);
    }
  };

  // Eliminar cron de flyers
  const deleteFlyerCron = async () => {
    if (!flyerCronTarget) return;
    try {
      const response = await axios.delete(
        `${API}/clientes/${flyerCronTarget.id}/flyer-cron`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      if (response.data.success) {
        toast.success(response.data.message);
        setFlyerCronConfig(null);
        setAllFlyerCrons(prev => prev.filter(c => c.cliente_id !== flyerCronTarget.id));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error eliminando cron");
    }
  };

  // Enviar flyers ahora (manual). diasOffset: 0=hoy (default), 1=mañana (adelantar)
  const enviarFlyersAhora = async (diasOffset = 0) => {
    if (!flyerCronTarget) return;
    const stateKey = diasOffset === 1 ? `${flyerCronTarget.id}_manana` : flyerCronTarget.id;
    setEnviandoFlyers(prev => ({ ...prev, [stateKey]: true }));
    try {
      const response = await axios.post(
        `${API}/clientes/${flyerCronTarget.id}/enviar-flyers`,
        {},
        {
          params: { dias_offset: diasOffset },
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        }
      );
      const cuandoLabel = diasOffset === 1 ? "de mañana" : "de hoy";
      if (response.data.success) {
        if (response.data.programado) {
          toast.success(`Flyers ${cuandoLabel} programados en ${response.data.delay_seconds}s (anti-ban)`, { duration: 6000 });
        } else {
          toast.success(`Enviando ${response.data.flyers_count} flyers ${cuandoLabel} a ${flyerCronTarget.name}`, { duration: 5000 });
        }
      } else if (response.data.ya_enviado) {
        toast.info(`Flyers ${cuandoLabel} ya enviados a ${flyerCronTarget.name}`);
      } else {
        toast.error(response.data.message || "Error");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error enviando flyers");
    } finally {
      setEnviandoFlyers(prev => ({ ...prev, [stateKey]: false }));
    }
  };

  // Función para previsualizar comprobante
  const handlePreviewReceipt = async (receipt) => {
    setPreviewLoading(true);
    setShowPreviewDialog(true);
    
    try {
      const response = await axios.get(`${API}/receipts/${receipt.id}/preview`);
      setPreviewReceipt({
        ...receipt,
        fileData: `data:${response.data.file_type};base64,${response.data.file_data}`
      });
    } catch (error) {
      toast.error("Error al cargar vista previa");
      setShowPreviewDialog(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Función para descargar comprobante
  const handleDownloadReceipt = async (receipt) => {
    try {
      const response = await axios.get(`${API}/receipts/${receipt.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', receipt.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Comprobante descargado");
    } catch (error) {
      toast.error("Error al descargar comprobante");
    }
  };

  // Función para copiar link del cliente
  // [DEPRECATED 2026-04-24] Portal web del cliente deshabilitado - funcion queda
  // stubbeada para no romper nada si algun boton aun la llama.
  const copyCustomerLink = async (_clienteId) => {
    toast.info('Portal web del cliente deshabilitado. Usa WhatsApp/Telegram.');
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Silencioso - el error de auth ya se maneja en App.js
      console.log('Error fetching user in Dashboard:', error.message);
    }
  };

  // Función para cargar solicitudes de eliminación pendientes
  // [DESACTIVADO] Endpoint /admin/deletion-requests fue eliminado del backend
  const loadDeletionRequests = async () => {
    // Endpoint eliminado - funcion desactivada
    return;
  };

  // Función para aprobar solicitud
  const approveDeletionRequest = async (requestId) => {
    setProcessingRequest(requestId);
    try {
      // [DESACTIVADO] Endpoint eliminado
      toast.info('Funcionalidad no disponible');
      return;
      
      // Recargar solicitudes y datos
      await loadDeletionRequests();
      await fetchClientes();
      if (selectedCliente) {
        await loadReceiptsForGroup(selectedCliente, activeCartera?.id);
      }
    } catch (error) {
      console.error('Error aprobando solicitud:', error);
      toast.error(error.response?.data?.detail || 'Error al aprobar solicitud');
    } finally {
      setProcessingRequest(null);
    }
  };

  // Función para rechazar solicitud
  const rejectDeletionRequest = async (requestId, reason) => {
    setProcessingRequest(requestId);
    try {
      // [DESACTIVADO] Endpoint eliminado
      toast.info('Funcionalidad no disponible');
      return;
      
      // Recargar solicitudes
      await loadDeletionRequests();
    } catch (error) {
      console.error('Error rechazando solicitud:', error);
      toast.error(error.response?.data?.detail || 'Error al rechazar solicitud');
    } finally {
      setProcessingRequest(null);
    }
  };

  // Función para actualizar reconciliación manualmente
  const updateManualReconciliation = async () => {
    if (!receiptToReconcile) return;
    
    setUpdatingReconciliation(true);
    try {
      const formData = new FormData();
      formData.append('status', manualReconciliationStatus);
      formData.append('comment', manualReconciliationComment || 'Verificación manual');
      const response = await axios.post(
        `${API}/receipts/${receiptToReconcile.id}/manual-reconciliation`,
        formData
      );
      toast.success('✅ Estado de conciliación actualizado manualmente', { duration: 4000 });
      
      // Cerrar diálogo y limpiar
      setShowManualReconciliationDialog(false);
      setReceiptToReconcile(null);
      setManualReconciliationComment('');
      
      invalidateCache(selectedCliente);
      setReceipts(prev => applyManualReconciliation(prev, response.data));
    } catch (error) {
      console.error('Error actualizando reconciliación:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar estado');
    } finally {
      setUpdatingReconciliation(false);
    }
  };

  // Función para auditar balance de un cliente específico
  const auditCustomerBalance = async (customerId, groupId) => {
    try {
      // [DESACTIVADO] Endpoint /admin/audit-customer eliminado
      toast.info('Funcionalidad no disponible'); return;
      const response = { data: { detail: 'disabled' } };
      const data = response.data;
      
      const difference = data.difference;
      const icon = difference > 0 ? '✅' : difference < 0 ? '⚠️' : '✓';
      
      toast.success(
        `${icon} Auditoría completada\n` +
        `${data.customer_name}\n` +
        `Anterior: $${data.old_balance.toLocaleString()}\n` +
        `Nuevo: $${data.new_balance.toLocaleString()}\n` +
        `Diferencia: $${Math.abs(difference).toLocaleString()}\n` +
        `Estrategia: ${data.reason}`,
        { duration: 8000 }
      );
      
      // Recargar datos
      await fetchClientes();
      if (selectedCliente === groupId) {
        await loadReceiptsForGroup(groupId, activeCartera?.id);
      }
    } catch (error) {
      console.error('Error auditando cliente:', error);
      toast.error(error.response?.data?.detail || 'Error al auditar cliente');
    }
  };

  // Función para recalcular saldos
  const recalculateBalances = async () => {
    if (!window.confirm('¿Recalcular todos los saldos de clientes y grupos desde cero? Esto corregirá cualquier inconsistencia en los saldos.')) {
      return;
    }

    setRecalculatingBalances(true);

    try {
      // [DESACTIVADO] Endpoint /admin/recalculate-balances eliminado
      toast.info('Funcionalidad no disponible'); return;
      const response = { data: { detail: 'disabled' } };
      
      toast.success(`✅ ${response.data.message}\n📊 Grupos: ${response.data.groups_updated}\n👥 Clientes: ${response.data.customers_updated}`, {
        duration: 6000
      });
      
      // Recargar datos
      await fetchClientes();
      if (selectedCliente) {
        await loadReceiptsForGroup(selectedCliente, activeCartera?.id);
      }
    } catch (error) {
      console.error('Error recalculando saldos:', error);
      toast.error(error.response?.data?.detail || 'Error al recalcular saldos');
    } finally {
      setRecalculatingBalances(false);
    }
  };

  // Función para reconciliación profunda
  const deepReconciliation = async () => {
    if (!window.confirm('¿Ejecutar reconciliación profunda de TODOS los comprobantes contra extractos bancarios? Esto buscará coincidencias usando múltiples criterios (fecha, monto, CUIL, titular, ID operación, etc.)')) {
      return;
    }

    setDeepReconciling(true);

    try {
      // [DESACTIVADO] Endpoint /admin/deep-reconciliation eliminado
      toast.info('Funcionalidad no disponible'); return;
      const response = { data: { detail: 'disabled' } };

      toast.success(response.data.message, { duration: 10000 });
      
      // Recargar comprobantes del cliente actual
      if (selectedCliente && activeCartera?.id) {
        await loadReceiptsForGroup(selectedCliente, activeCartera?.id);
      }
      
      // Recargar grupos
      await fetchClientes();
      
    } catch (error) {
      console.error('Error en reconciliación:', error);
      toast.error(error.response?.data?.detail || 'Error al reconciliar comprobantes');
    } finally {
      setDeepReconciling(false);
    }
  };

  // Función para re-procesar todos los comprobantes
  const reprocessAllReceipts = async () => {
    if (!window.confirm('¿Estás seguro de que deseas re-procesar TODOS los comprobantes? Esto volverá a ejecutar el OCR para extraer las fechas correctas. Puede tomar varios minutos.')) {
      return;
    }

    setReprocessing(true);

    try {
      // [DESACTIVADO] Endpoint /admin/reprocess-receipts eliminado
      toast.info('Funcionalidad no disponible'); return;
      const response = { data: { detail: 'disabled' } };

      toast.success(response.data.message, { duration: 10000 });
      
      // Recargar comprobantes del cliente actual
      if (selectedCliente && activeCartera?.id) {
        await loadReceiptsForGroup(selectedCliente, activeCartera?.id);
      }
      
      // Recargar grupos para actualizar balances
      await fetchClientes();
      
    } catch (error) {
      console.error('Error en re-procesamiento:', error);
      toast.error(error.response?.data?.detail || 'Error al re-procesar comprobantes');
    } finally {
      setReprocessing(false);
    }
  };

  // Función para exportar conciliación del cliente seleccionado
  const exportReconciliation = async () => {
    if (!selectedCliente) {
      toast.error('Por favor selecciona un cliente primero');
      return;
    }

    try {
      // [DESACTIVADO] Endpoint /admin/export-reconciliation eliminado
      toast.info('Funcionalidad no disponible');
      return;
      const token = localStorage.getItem('token');
      
      toast.info('Generando reporte de conciliación...');
      
      const response = await axios.get(
        // `${API}/admin/export-reconciliation/${selectedCliente}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Descargar archivo
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Obtener nombre del cliente
      const clienteName = accounts.find(a => a.id === selectedCliente)?.name || 'cliente';
      const filename = `conciliacion_${clienteName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Reporte de conciliación descargado exitosamente');
    } catch (error) {
      console.error('Error exportando conciliación:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Error al exportar conciliación';
      toast.error(errorMessage, { duration: 8000 });
    }
  };


  // Función para abrir dialog de pago
  const openPaymentDialog = async (groupId) => {
    try {
      // Obtener el cliente final del grupo
      if (!finalCustomers[groupId]) {
        const response = await axios.get(`${API}/final-customers?group_id=${groupId}`);
        if (response.data.length === 0) {
          toast.error('Este cliente no tiene una cuenta configurada');
          return;
        }
        const customer = response.data[0];
        setFinalCustomers(prev => ({ ...prev, [groupId]: customer }));
        setTargetCustomerForPayment(customer);
      } else {
        setTargetCustomerForPayment(finalCustomers[groupId]);
      }
      
      setPaymentAmount('');
      setPaymentDescription('');
      setShowPaymentDialog(true);
    } catch (error) {
      console.error('Error al cargar cliente:', error);
      toast.error('Error al cargar información del cliente');
    }
  };

  // Función para registrar pago a cliente
  const registerPayment = async () => {
    if (!targetCustomerForPayment) {
      toast.error('No hay cliente seleccionado');
      return;
    }

    const amount = parseFloat(paymentAmount);
    
    if (!amount || amount <= 0) {
      toast.error('El monto debe ser mayor a 0');
      return;
    }

    if (!paymentDescription.trim()) {
      toast.error('La descripción es requerida');
      return;
    }

    // Si el monto supera el saldo positivo, se pide confirmacion explicita
    // y se envia allow_negative. La AUTORIZACION real (solo admin/financiera)
    // la valida el backend en /payments/to-customer; si el rol no esta permitido
    // devuelve 400 "Saldo insuficiente" y se muestra en el catch.
    const dejaNegativo = targetCustomerForPayment.balance < amount;

    if (dejaNegativo && !paymentNegativeConfirm) {
      // Primer intento: mostrar advertencia y pedir confirmacion explicita.
      setPaymentNegativeConfirm(true);
      return;
    }

    setPaymentLoading(true);

    try {
      const response = await axios.post(`${API}/payments/to-customer`, {
        customer_id: targetCustomerForPayment.id,
        amount: amount,
        description: paymentDescription,
        allow_negative: dejaNegativo
      });

      toast.success(`Pago de $${amount.toLocaleString()} registrado a ${targetCustomerForPayment.name}`);
      
      // Actualizar el cliente en el estado local
      setFinalCustomers(prev => ({
        ...prev,
        [targetCustomerForPayment.group_id]: {
          ...targetCustomerForPayment,
          balance: response.data.new_balance
        }
      }));

      // Recargar grupos para actualizar balances
      await fetchClientes();

      // Cerrar dialog
      setShowPaymentDialog(false);
      setPaymentAmount('');
      setPaymentDescription('');
      setTargetCustomerForPayment(null);
      setPaymentNegativeConfirm(false);
    } catch (error) {
      console.error('Error al registrar pago:', error);
      toast.error(error.response?.data?.detail || 'Error al registrar pago');
    } finally {
      setPaymentLoading(false);
    }
  };


  // Función para abrir dialog de configuración de cliente
  const openConfigDialog = async (groupId) => {
    try {
      // Obtener el cliente final del grupo
      let customer = finalCustomers[groupId];
      if (!customer) {
        const response = await axios.get(`${API}/final-customers?group_id=${groupId}`);
        if (response.data.length === 0) {
          toast.error('Este cliente no tiene una cuenta configurada');
          return;
        }
        customer = response.data[0];
        setFinalCustomers(prev => ({ ...prev, [groupId]: customer }));
      }
      
      // Cargar valores actuales en el formulario (solo CBU, no titular)
      setConfigCustomerId(customer.id);
      setConfigDestinationAccount(customer.expected_destination_account || '');
      setShowConfigDialog(true);
    } catch (error) {
      console.error('Error al cargar configuración del cliente:', error);
      toast.error('Error al cargar configuración del cliente');
    }
  };

  // Función para guardar configuración del cliente
  const saveClientConfig = async () => {
    if (!configCustomerId) {
      toast.error('No hay cliente seleccionado');
      return;
    }

    setConfigLoading(true);

    try {
      const response = await axios.put(`${API}/final-customers/${configCustomerId}`, {
        expected_destination_account: configDestinationAccount.trim() || null
      });

      toast.success('Configuración guardada correctamente');
      
      // Actualizar en el estado local
      setFinalCustomers(prev => {
        const updated = { ...prev };
        for (const [groupId, customer] of Object.entries(updated)) {
          if (customer.id === configCustomerId) {
            updated[groupId] = {
              ...customer,
              expected_destination_account: response.data.expected_destination_account
            };
          }
        }
        return updated;
      });

      // Cerrar dialog
      setShowConfigDialog(false);
      setConfigCustomerId(null);
      setConfigDestinationAccount('');
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setConfigLoading(false);
    }
  };

  // Cambio: Ahora cargamos grupos pero los mostramos como "clientes"
  // Cada grupo representa una cuenta del cliente final
  const fetchClientes = useCallback(async () => {
    if (!activeCartera) {
      setClientes([]);
      setFinalCustomers({});
      return;
    }
    
    try {
      // Cargar clientes de la cartera
      const clientesResponse = await clientRead.get(`${API}/clientes?cartera_id=${activeCartera.id}`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      setClientes(clientesResponse.data);
      
      // Mapear clientes por ID para mantener compatibilidad
      const customersMap = {};
      clientesResponse.data.forEach(cliente => {
        customersMap[cliente.id] = cliente;
      });
      setFinalCustomers(customersMap);
      
      // Los tres resúmenes de badges van EN PARALELO (antes eran 3 esperas en serie)
      const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };

      const cargarGaps = axios.get(`${API}/carteras/${activeCartera.id}/gaps-summary`, authHeader)
        .then(gapsRes => {
          const counts = gapsRes.data?.counts || {};
          setGapsByClient(prev => {
            const next = { ...prev };
            Object.keys(counts).forEach(cid => {
              next[cid] = { ...(next[cid] || {}), count: counts[cid] };
            });
            // Reset counts de clientes que ya no aparecen en el summary
            Object.keys(next).forEach(cid => {
              if (!(cid in counts)) {
                next[cid] = { ...(next[cid] || {}), count: 0 };
              }
            });
            return next;
          });
        })
        .catch(e => console.warn('gaps-summary no disponible:', e?.response?.status));

      // Resumen de auditoria: pinta las tarjetas de los clientes ya cortados
      const cargarAuditoria = axios.get(`${API}/carteras/${activeCartera.id}/auditoria-summary`, authHeader)
        .then(audRes => setAuditoriaByCliente(audRes.data || {}))
        .catch(e => console.warn('auditoria-summary no disponible:', e?.response?.status));

      // FASE 4 (2026-02): pendientes de retiros de todos los clientes de la cartera.
      const cargarPendientes = axios.get(`${API}/retiros/pendientes`)
        .then(allPendsRes => {
          const pendList = Array.isArray(allPendsRes.data) ? allPendsRes.data : [];
          const grouped = {};
          for (const p of pendList) {
            if (!grouped[p.cliente_id]) grouped[p.cliente_id] = [];
            grouped[p.cliente_id].push(p);
          }
          setPendientesByCliente(grouped);
        })
        .catch(e => {
          // 403 si el rol no puede ver retiros; no es un error, solo no mostramos.
          if (e?.response?.status !== 403) {
            console.warn('Error cargando pendientes de retiros:', e?.response?.status);
          }
        });

      await Promise.all([cargarGaps, cargarAuditoria, cargarPendientes]);

    } catch (error) {
      if (isReadCancelled(error)) return;
      console.error('Error al cargar clientes:', error);
      toast.error('Error al cargar clientes');
    }
  }, [activeCartera?.id, clientRead.get]);

  // Refresco automatico de saldos (cada 15s): el WebSocket solo trae eventos del
  // cliente abierto, asi que las tarjetas de los demas clubes quedaban con el saldo
  // viejo hasta recargar. Endpoint liviano: solo id + balance.
  useEffect(() => {
    if (!activeCartera?.id) return;
    let cancelado = false;

    const refrescarSaldos = async () => {
      if (document.hidden) return;
      try {
        const res = await axios.get(`${API}/carteras/${activeCartera.id}/balances`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        });
        if (cancelado) return;
        const saldos = res.data || {};
        setClientes(prev => {
          let cambio = false;
          const next = prev.map(c => {
            const nuevo = saldos[c.id];
            if (nuevo !== undefined && nuevo !== c.balance) {
              cambio = true;
              return { ...c, balance: nuevo };
            }
            return c;
          });
          if (cambio) {
            // Si a un club auditado le cambio el saldo, dejo de estar al dia
            setAuditoriaByCliente(aud => {
              const upd = { ...aud };
              let tocado = false;
              prev.forEach(c => {
                const nuevo = saldos[c.id];
                if (nuevo !== undefined && nuevo !== c.balance && upd[c.id]?.auditado) {
                  upd[c.id] = { ...upd[c.id], auditado: false };
                  tocado = true;
                }
              });
              return tocado ? upd : aud;
            });
          }
          return cambio ? next : prev;
        });
        setFinalCustomers(prev => {
          let cambio = false;
          const next = { ...prev };
          Object.keys(next).forEach(id => {
            const nuevo = saldos[id];
            if (nuevo !== undefined && nuevo !== next[id].balance) {
              next[id] = { ...next[id], balance: nuevo };
              cambio = true;
            }
          });
          return cambio ? next : prev;
        });
      } catch (e) {
        console.warn('refresco de saldos falló:', e?.response?.status);
      }

      // Auditoría: el ❤ del bot contra el Drive de TUDEMUNE pinta la tarjeta sin recargar
      try {
        const audRes = await axios.get(`${API}/carteras/${activeCartera.id}/auditoria-summary`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        });
        if (cancelado) return;
        setAuditoriaByCliente(prev => {
          const next = audRes.data || {};
          return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
        });
      } catch (e) {
        console.warn('refresco de auditoría falló:', e?.response?.status);
      }
    };

    const intervalo = setInterval(refrescarSaldos, 15000);
    const alVolver = () => { if (!document.hidden) refrescarSaldos(); };
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      cancelado = true;
      clearInterval(intervalo);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [activeCartera?.id]);

  // Funciones de Financiera
  const fetchFinancieraSummary = useCallback(async () => {
    if (!activeCartera) return;
    
    try {
      setFinancieraLoading(true);
      const response = await axios.get(`${API}/financiera/summary`);
      setFinancieraSummary(response.data);
    } catch (error) {
      console.error('Error al cargar resumen Financiera:', error);
      if (error.response?.status === 404) {
        // No hay saldo inicial configurado
        setFinancieraSummary(null);
      } else {
        toast.error('Error al cargar datos de Financiera');
      }
    } finally {
      setFinancieraLoading(false);
    }
  }, [activeCartera?.id]);

  const fetchFinancieraMovements = useCallback(async () => {
    if (!activeCartera) return;
    
    try {
      const response = await axios.get(`${API}/financiera/movements?limit=100`);
      setFinancieraMovements(response.data.movements || []);
    } catch (error) {
      console.error('Error al cargar movimientos Financiera:', error);
      toast.error('Error al cargar movimientos');
    }
  }, [activeCartera?.id]);

  const saveInitialBalance = async () => {
    try {
      const balance = parseFloat(initialBalanceInput.replace(/[^0-9.-]/g, ''));
      if (isNaN(balance)) {
        toast.error('Monto inválido');
        return;
      }

      await axios.put(`${API}/financiera/initial-balance`, {
        initial_balance: balance
      });

      toast.success('Saldo inicial configurado');
      setShowInitialBalanceModal(false);
      setInitialBalanceInput('');
      await fetchFinancieraSummary();
    } catch (error) {
      console.error('Error al guardar saldo inicial:', error);
      toast.error('Error al guardar saldo inicial');
    }
  };

  const exportFinancieraToExcel = () => {
    if (!financieraMovements || financieraMovements.length === 0) {
      toast.error('No hay movimientos para exportar');
      return;
    }

    const data = financieraMovements.map(mov => ({
      'Fecha': mov.date,
      'Cliente': mov.cliente_name,
      'Tipo': mov.movement_type === 'income' ? 'Ingreso' : 'Pago',
      'Monto': mov.amount,
      'Comisión %': mov.commission_rate,
      'Comisión $': mov.commission_amount,
      'Neto': mov.net_amount
    }));

    // Agregar resumen al final
    if (financieraSummary) {
      data.push({});
      data.push({
        'Fecha': 'RESUMEN',
        'Cliente': '',
        'Tipo': '',
        'Monto': '',
        'Comisión %': '',
        'Comisión $': '',
        'Neto': ''
      });
      data.push({
        'Fecha': 'Saldo Inicial',
        'Neto': financieraSummary.initial_balance
      });
      data.push({
        'Fecha': 'Total Ingresos',
        'Neto': financieraSummary.total_income
      });
      data.push({
        'Fecha': 'Total Comisiones',
        'Neto': -financieraSummary.total_commissions
      });
      data.push({
        'Fecha': 'Total Pagos',
        'Neto': -financieraSummary.total_payments
      });
      data.push({
        'Fecha': 'Disponible Actual',
        'Neto': financieraSummary.available
      });
    }

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Financiera');
    
    const fecha = format(new Date(), 'yyyy-MM-dd');
    XLSX.writeFile(wb, `financiera_${fecha}.xlsx`);
    toast.success('Excel exportado');
  };

  // Cargar datos de Financiera al montar
  useEffect(() => {
    if (activeCartera && viewMode === 'list') {
      fetchFinancieraSummary();
    }
  }, [activeCartera, viewMode, fetchFinancieraSummary]);

  // Función para regenerar PIN del cliente
  const regeneratePIN = async (groupId) => {
    try {
      // Obtener el cliente final del grupo
      let customer = finalCustomers[groupId];
      if (!customer) {
        const response = await axios.get(`${API}/final-customers?group_id=${groupId}`);
        if (response.data.length === 0) {
          toast.error('Este cliente no tiene una cuenta configurada');
          return;
        }
        customer = response.data[0];
      }

      // Guardar el cliente y mostrar diálogo de confirmación
      setCustomerForPINRegen(customer);
      setShowPINConfirmDialog(true);
    } catch (error) {
      console.error('Error al obtener datos del cliente:', error);
      const errorMsg = error.response?.data?.detail || 'Error al obtener datos del cliente.';
      toast.error(errorMsg);
    }
  };

  // Función para confirmar y ejecutar regeneración de PIN
  const confirmRegeneratePIN = async () => {
    if (!customerForPINRegen) return;
    
    setPinRegenLoading(true);
    try {
      const response = await axios.post(`${API}/admin/final-customers/${customerForPINRegen.id}/regenerate-pin`);
      
      setRegeneratedPIN(response.data.new_pin);
      setShowPINConfirmDialog(false);
      setShowPINDialog(true);
      
      toast.success(response.data.message || 'PIN regenerado exitosamente. Sesión cerrada.');
      
      // Recargar datos para reflejar que la sesión se cerró
      await loadFinalCustomers();
    } catch (error) {
      console.error('Error al regenerar PIN:', error);
      const errorMsg = error.response?.data?.detail || 'Error al regenerar PIN. Intenta nuevamente.';
      toast.error(errorMsg);
    } finally {
      setPinRegenLoading(false);
      setCustomerForPINRegen(null);
    }
  };

  // Función para ver logs de acceso del cliente
  const viewAccessLogs = async (groupId) => {
    try {
      // Obtener el cliente final del grupo
      let customer = finalCustomers[groupId];
      if (!customer) {
        const response = await axios.get(`${API}/final-customers?group_id=${groupId}`);
        if (response.data.length === 0) {
          toast.error('Este cliente no tiene una cuenta configurada');
          return;
        }
        customer = response.data[0];
      }

      const response = await axios.get(`${API}/admin/final-customers/${customer.id}/access-logs`);
      setAccessLogs(response.data);
      setSelectedClientForLogs(customer);
      setShowAccessLogsDialog(true);
      
      // Mostrar mensaje si no hay logs
      if (response.data.length === 0) {
        toast.info('Este cliente aún no tiene logs de acceso registrados');
      }
    } catch (error) {
      console.error('Error al cargar logs:', error);
      if (error.response?.status === 404) {
        toast.error('Cliente no encontrado');
      } else {
        toast.error(error.response?.data?.detail || 'Error al cargar logs de acceso');
      }
    }
  };

  // Función para cerrar sesiones del cliente
  const closeClientSessions = async (groupId) => {
    try {
      // Obtener el cliente final del grupo
      let customer = finalCustomers[groupId];
      if (!customer) {
        const response = await axios.get(`${API}/final-customers?group_id=${groupId}`);
        if (response.data.length === 0) {
          toast.error('Este cliente no tiene una cuenta configurada');
          return;
        }
        customer = response.data[0];
      }

      const response = await axios.post(`${API}/admin/final-customers/${customer.id}/close-sessions`);
      
      toast.success(`Sesiones de ${customer.name} cerradas exitosamente`);
      
      // Recargar datos para actualizar el estado de sesión
      await loadFinalCustomers();
    } catch (error) {
      console.error('Error al cerrar sesiones:', error);
      const errorMsg = error.response?.data?.detail || 'Error al cerrar sesiones. Intenta nuevamente.';
      toast.error(errorMsg);
    }
  };


  // ============================================
  // FUNCIONES PARA SISTEMA DE CRÉDITOS
  // ============================================
  
  // Cargar solicitudes de crédito de un cliente
  const loadCreditRequests = async (clienteId) => {
    try {
      const response = await axios.get(`${API}/credit-requests`, {
        params: { cliente_id: clienteId, status: 'pending' }
      });
      return response.data || [];
    } catch (error) {
      console.error('Error cargando solicitudes de crédito:', error);
      return [];
    }
  };
  
  // Actualizar descubierto de un cliente
  const updateDescubierto = async (clienteId, descubierto) => {
    try {
      setUpdatingDescubierto(true);
      
      await axios.patch(`${API}/final-customers/${clienteId}`, {
        descubierto: parseFloat(descubierto)
      });
      
      toast.success('Descubierto actualizado correctamente');
      
      // Recargar datos
      await fetchClientes();
      
      setEditingDescubierto(null);
      setNewDescubierto('');
    } catch (error) {
      console.error('Error actualizando descubierto:', error);
      toast.error('Error al actualizar descubierto');
    } finally {
      setUpdatingDescubierto(false);
    }
  };

  // ========== TOGGLE RETIRO SELF-SERVICE (WhatsApp /pago) ==========
  // Habilita/deshabilita que el cliente solicite retiros via comando /pago en WhatsApp
  // Auto-aprobación hasta $60.000; arriba de eso requiere botón del operador en la card
  const [togglingRetiroSelfService, setTogglingRetiroSelfService] = useState(null); // cliente_id en curso
  const [editingRetiroLimit, setEditingRetiroLimit] = useState(null); // cliente_id en edicion
  const [newRetiroLimit, setNewRetiroLimit] = useState('');
  const [savingRetiroLimit, setSavingRetiroLimit] = useState(false);
  // FASE 3 (2026-02): límite diario + reset manual del contador
  const [editingRetiroDailyLimit, setEditingRetiroDailyLimit] = useState(null);
  const [newRetiroDailyLimit, setNewRetiroDailyLimit] = useState('');
  const [savingRetiroDailyLimit, setSavingRetiroDailyLimit] = useState(false);
  const [resettingRetiroDaily, setResettingRetiroDaily] = useState(null);
  // Retiros por Telegram (2026-06): cooldown editable + PIN definido por el operador
  const [editingRetiroCooldown, setEditingRetiroCooldown] = useState(null);
  const [newRetiroCooldown, setNewRetiroCooldown] = useState('');
  const [savingRetiroCooldown, setSavingRetiroCooldown] = useState(false);
  const [editingRetiroPin, setEditingRetiroPin] = useState(null);
  const [newRetiroPin, setNewRetiroPin] = useState('');
  const [savingRetiroPin, setSavingRetiroPin] = useState(false);
  const [unblockingRetiroPin, setUnblockingRetiroPin] = useState(null);

  const toggleRetiroSelfService = async (clienteId, currentValue) => {
    try {
      setTogglingRetiroSelfService(clienteId);
      const newValue = !currentValue;

      await axios.patch(`${API}/final-customers/${clienteId}`, {
        retiro_self_service_enabled: newValue
      });

      toast.success(
        newValue
          ? '🤖 Retiros automáticos ACTIVADOS (WhatsApp + Telegram)'
          : '🔒 Retiros automáticos DESACTIVADOS (WhatsApp + Telegram)'
      );

      await fetchClientes();
    } catch (error) {
      console.error('Error actualizando toggle de retiro self-service:', error);
      toast.error('Error al cambiar el estado de retiros automáticos');
    } finally {
      setTogglingRetiroSelfService(null);
    }
  };

  const saveRetiroAutoLimit = async (clienteId) => {
    const parsed = parseFloat(String(newRetiroLimit).replace(/\./g, '').replace(',', '.'));
    if (isNaN(parsed) || parsed < 0) {
      toast.error('Ingresá un monto válido (mayor o igual a 0)');
      return;
    }
    try {
      setSavingRetiroLimit(true);
      await axios.patch(`${API}/final-customers/${clienteId}`, {
        retiro_auto_limit: parsed
      });
      toast.success(`Límite de auto-aprobación actualizado a $${parsed.toLocaleString('es-AR')}`);
      await fetchClientes();
      setEditingRetiroLimit(null);
      setNewRetiroLimit('');
    } catch (error) {
      console.error('Error actualizando retiro_auto_limit:', error);
      toast.error('No se pudo actualizar el límite');
    } finally {
      setSavingRetiroLimit(false);
    }
  };

  // FASE 3: guardar tope diario
  const saveRetiroDailyLimit = async (clienteId) => {
    const parsed = parseFloat(String(newRetiroDailyLimit).replace(/\./g, '').replace(',', '.'));
    if (isNaN(parsed) || parsed < 0) {
      toast.error('Ingresá un monto válido (mayor o igual a 0)');
      return;
    }
    try {
      setSavingRetiroDailyLimit(true);
      await axios.patch(`${API}/final-customers/${clienteId}`, {
        retiro_daily_limit: parsed
      });
      toast.success(`Tope diario actualizado a $${parsed.toLocaleString('es-AR')}`);
      await fetchClientes();
      setEditingRetiroDailyLimit(null);
      setNewRetiroDailyLimit('');
    } catch (error) {
      console.error('Error actualizando retiro_daily_limit:', error);
      toast.error('No se pudo actualizar el tope diario');
    } finally {
      setSavingRetiroDailyLimit(false);
    }
  };

  // FASE 3: resetear contador diario (libera tope y cooldown)
  const resetRetiroDaily = async (clienteId, clienteName) => {
    if (!window.confirm(`¿Resetear el contador diario de retiros de ${clienteName || 'este cliente'}?\n\nEsto pone el gastado en $0 y libera el cooldown.`)) {
      return;
    }
    try {
      setResettingRetiroDaily(clienteId);
      await axios.post(`${API}/final-customers/${clienteId}/reset-daily`);
      toast.success('🔄 Contador diario reseteado');
      await fetchClientes();
    } catch (error) {
      console.error('Error reseteando retiro daily:', error);
      const msg = error?.response?.data?.detail || 'No se pudo resetear';
      toast.error(msg);
    } finally {
      setResettingRetiroDaily(null);
    }
  };

  // Cooldown entre pagos auto-aprobados (aplica a WhatsApp y Telegram)
  const saveRetiroCooldown = async (clienteId) => {
    const parsed = parseInt(String(newRetiroCooldown).replace(/\D/g, ''), 10);
    if (isNaN(parsed) || parsed < 0 || parsed > 1440) {
      toast.error('Ingresá los minutos de espera (0 a 1440)');
      return;
    }
    try {
      setSavingRetiroCooldown(true);
      await axios.patch(`${API}/final-customers/${clienteId}`, { retiro_cooldown_minutes: parsed });
      toast.success(`Cooldown actualizado a ${parsed} min`);
      await fetchClientes();
      setEditingRetiroCooldown(null);
      setNewRetiroCooldown('');
    } catch (error) {
      const msg = error?.response?.data?.detail || 'No se pudo actualizar el cooldown';
      toast.error(typeof msg === 'string' ? msg : 'No se pudo actualizar el cooldown');
    } finally {
      setSavingRetiroCooldown(false);
    }
  };

  // PIN de retiros por Telegram: lo define el operador, se guarda hasheado y nunca se muestra
  const saveRetiroPin = async (clienteId) => {
    const pin = String(newRetiroPin).replace(/\D/g, '');
    if (pin.length !== 4) {
      toast.error('El PIN debe tener exactamente 4 dígitos');
      return;
    }
    try {
      setSavingRetiroPin(true);
      await axios.patch(`${API}/final-customers/${clienteId}`, { retiro_pin: pin });
      toast.success('🔐 PIN de retiros guardado. Pasáselo al club por un canal privado.');
      await fetchClientes();
      setEditingRetiroPin(null);
      setNewRetiroPin('');
    } catch (error) {
      const msg = error?.response?.data?.detail || 'No se pudo guardar el PIN';
      toast.error(typeof msg === 'string' ? msg : 'No se pudo guardar el PIN');
    } finally {
      setSavingRetiroPin(false);
    }
  };

  const clearRetiroPin = async (clienteId, clienteName) => {
    if (!window.confirm(`¿Borrar el PIN de retiros de ${clienteName || 'este cliente'}?\n\nSin PIN, nadie puede pedir pagos por Telegram.`)) return;
    try {
      setSavingRetiroPin(true);
      await axios.patch(`${API}/final-customers/${clienteId}`, { retiro_pin: '' });
      toast.success('PIN borrado. Los pedidos por Telegram quedan bloqueados.');
      await fetchClientes();
      setEditingRetiroPin(null);
      setNewRetiroPin('');
    } catch (error) {
      toast.error('No se pudo borrar el PIN');
    } finally {
      setSavingRetiroPin(false);
    }
  };

  const unblockRetiroPin = async (clienteId) => {
    try {
      setUnblockingRetiroPin(clienteId);
      await axios.post(`${API}/final-customers/${clienteId}/unblock-pin`);
      toast.success('🔓 PIN desbloqueado');
      await fetchClientes();
    } catch (error) {
      toast.error('No se pudo desbloquear el PIN');
    } finally {
      setUnblockingRetiroPin(null);
    }
  };

  // ========== FASE 4 (2026-02): RETIROS PENDIENTES (>retiro_auto_limit) ==========
  // Solo admin/financiera puede aprobar/rechazar. Los pendientes vencen a las 6 hs.
  // currentUser viene del contexto; el rol se mira con currentUser?.role.
  const [pendientesByCliente, setPendientesByCliente] = useState({}); // {cliente_id: [...]}
  const [loadingPendientesFor, setLoadingPendientesFor] = useState(null);
  const [approvingPendiente, setApprovingPendiente] = useState(null);
  const [dismissingPendiente, setDismissingPendiente] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [pendienteToReject, setPendienteToReject] = useState(null);
  const [rejectMotivo, setRejectMotivo] = useState('');
  const [rejectingPendiente, setRejectingPendiente] = useState(false);

  const loadPendientesForCliente = async (clienteId) => {
    try {
      setLoadingPendientesFor(clienteId);
      const { data } = await axios.get(`${API}/retiros/pendientes`, {
        params: { cliente_id: clienteId },
      });
      setPendientesByCliente(prev => ({ ...prev, [clienteId]: Array.isArray(data) ? data : [] }));
    } catch (error) {
      console.error('Error cargando pendientes:', error);
    } finally {
      setLoadingPendientesFor(null);
    }
  };

  const approvePendiente = async (pendienteId, clienteId) => {
    if (!window.confirm('¿Aprobar este retiro? Se ejecutará el pago y debitará el saldo del cliente.')) return;
    const enviarAprobacion = async (allowNegative) => {
      const { data } = await axios.post(`${API}/retiros/pendientes/${pendienteId}/approve`, { allow_negative: allowNegative });
      return data;
    };
    try {
      setApprovingPendiente(pendienteId);
      let data;
      try {
        data = await enviarAprobacion(false);
      } catch (err) {
        const det = err?.response?.data?.detail;
        if (det?.code === 'saldo_insuficiente') {
          const fmt = (n) => `$${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;
          const confirmarAdelanto = window.confirm(
            `⚠️ ADELANTO DE PAGO\n\nSaldo disponible: ${fmt(det.saldo)}\nPedido: ${fmt(det.monto)}\n\n` +
            `El saldo del cliente va a quedar NEGATIVO hasta el cierre. ¿Aprobar igual?`
          );
          if (!confirmarAdelanto) return;
          data = await enviarAprobacion(true);
        } else {
          throw err;
        }
      }
      if (data?.success === false) {
        toast.error(data?.error || 'No se pudo aprobar');
      } else {
        toast.success('✅ Pendiente aprobado. Cliente notificado.');
        await Promise.all([loadPendientesForCliente(clienteId), fetchClientes()]);
      }
    } catch (error) {
      console.error('Error aprobando pendiente:', error);
      const det = error?.response?.data?.detail;
      toast.error((typeof det === 'string' ? det : det?.message) || 'No se pudo aprobar');
    } finally {
      setApprovingPendiente(null);
    }
  };

  const openRejectModal = (pendiente) => {
    setPendienteToReject(pendiente);
    setRejectMotivo('');
    setRejectModalOpen(true);
  };

  const submitReject = async () => {
    const motivo = rejectMotivo.trim();
    if (!motivo) {
      toast.error('Ingresá un motivo de rechazo');
      return;
    }
    if (!pendienteToReject) return;
    try {
      setRejectingPendiente(true);
      await axios.post(`${API}/retiros/pendientes/${pendienteToReject.id}/reject`, { motivo });
      toast.success('🚫 Pendiente rechazado. Cliente notificado.');
      setRejectModalOpen(false);
      setPendienteToReject(null);
      setRejectMotivo('');
      await loadPendientesForCliente(pendienteToReject.cliente_id);
    } catch (error) {
      console.error('Error rechazando pendiente:', error);
      toast.error(error?.response?.data?.detail || 'No se pudo rechazar');
    } finally {
      setRejectingPendiente(false);
    }
  };

  // FASE 4: descartar pendiente SIN notificar al cliente (acción interna del admin).
  const dismissPendiente = async (pendienteId, clienteId) => {
    if (!window.confirm('¿Cerrar esta solicitud?\n\nEl pendiente se marca como descartado y NO se notifica al cliente.')) return;
    try {
      setDismissingPendiente(pendienteId);
      await axios.post(`${API}/retiros/pendientes/${pendienteId}/dismiss`);
      toast.success('Solicitud cerrada (cliente no notificado)');
      await loadPendientesForCliente(clienteId);
    } catch (error) {
      console.error('Error descartando pendiente:', error);
      toast.error(error?.response?.data?.detail || 'No se pudo descartar');
    } finally {
      setDismissingPendiente(null);
    }
  };
  
  // Abrir diálogo de solicitudes de crédito
  const openCreditRequestsDialog = async (cliente) => {
    setSelectedClienteForCredit(cliente);
    const requests = await loadCreditRequests(cliente.id);
    setCreditRequests(requests);
    setShowCreditRequestsDialog(true);
  };
  
  // Aprobar/rechazar solicitud de crédito
  const updateCreditRequestStatus = async (requestId, status, notes = '') => {
    try {
      await axios.patch(`${API}/credit-requests/${requestId}`, {
        status: status,
        admin_notes: notes
      });
      
      toast.success(`Solicitud ${status === 'approved' ? 'aprobada' : 'rechazada'}`);
      
      // Recargar solicitudes
      if (selectedClienteForCredit) {
        const requests = await loadCreditRequests(selectedClienteForCredit.id);
        setCreditRequests(requests);
      }
    } catch (error) {
      console.error('Error actualizando solicitud:', error);
      toast.error('Error al actualizar solicitud');
    }
  };
  // ============================================
  // FUNCIONES PARA SISTEMA DE MESAS
  // ============================================
  
  // Función para cargar recibos - CON CACHÉ OPTIMIZADO
  const loadReceiptsForGroup = useCallback(async (groupId, portfolioId, append = false) => {
    if (!portfolioId || !groupId) {
      console.log('⚠️ No portfolioId or groupId - clearing receipts');
      setReceipts([]);
      setReceiptsOffset(0);
      setCortesEstadoCuenta([]);
      setHasMoreReceipts(false);
      setBalanceSnapshot(null);
      return;
    }

    if (!append) {
      receiptRead.get(`${API}/clientes/${groupId}/balance-snapshot`, { recoveryOptional: true })
        .then(res => setBalanceSnapshot(res.data))
        .catch(err => {
          if (isReadCancelled(err)) return;
          if (err?.response?.status !== 404 && err?.response?.status !== 403) {
            console.warn('No se pudo cargar balance-snapshot:', err?.response?.status);
          }
          setBalanceSnapshot(null);
        });
    }
    
    console.log('🔍 Loading receipts for groupId:', groupId, 'portfolioId:', portfolioId);
    
    if (append) {
      setLoadingMoreReceipts(true);
      
      try {
        let currentOffset = 0;
        await new Promise(resolve => {
          setReceiptsOffset(prev => {
            currentOffset = prev;
            resolve();
            return prev;
          });
        });
        
        const params = { 
          cartera_id: portfolioId,
          cliente_id: groupId,
          limit: RECEIPTS_PER_PAGE,
          offset: currentOffset
        };
        
        const response = await receiptRead.get(`${API}/receipts/metadata`, { params });
        const data = response.data;
        
        setReceipts(prev => [...prev, ...data]);
        setReceiptsOffset(prev => prev + data.length);
        setHasMoreReceipts(data.length === RECEIPTS_PER_PAGE);
      } catch (error) {
        if (isReadCancelled(error)) return;
        console.error('❌ Error loading more receipts:', error);
        toast.error('Error al cargar más comprobantes');
      } finally {
        setLoadingMoreReceipts(false);
      }
      return;
    }
    
    setLoadingReceipts(true);
    
    try {
      const cached = getFromCache(groupId);
      
      let serverTimestamp = null;
      try {
        const timestampResponse = await receiptRead.get(`${API}/clientes/${groupId}/receipts-timestamp`, { recoveryOptional: true });
        serverTimestamp = timestampResponse.data.last_modified;
        console.log(`🕐 Server timestamp para ${groupId}: ${serverTimestamp}`);
      } catch (tsError) {
        if (isReadCancelled(tsError)) return;
        console.warn('⚠️ No se pudo obtener timestamp del servidor:', tsError);
      }
      
      if (cached && cached.timestamp && serverTimestamp) {
        if (cached.timestamp === serverTimestamp) {
          console.log(`✅ Cache VÁLIDO para ${groupId} - Usando datos cacheados`);
          setReceipts(cached.receipts);
          setReceiptsOffset(cached.receipts.length);
          setHasMoreReceipts(cached.receipts.length === RECEIPTS_PER_PAGE);
          setLoadingReceipts(false);
          toast.success('Datos cargados desde caché (instantáneo)', { duration: 2000 });
          return;
        } else {
          console.log(`⚠️ Cache DESACTUALIZADO para ${groupId} - Timestamp diferente`);
        }
      }
      
      console.log(`📡 Fetching desde servidor para ${groupId}`);
      const params = { 
        cartera_id: portfolioId,
        cliente_id: groupId,
        limit: RECEIPTS_PER_PAGE,
        offset: 0
      };
      
      const response = await receiptRead.get(`${API}/receipts/metadata`, { params });
      const data = response.data;
      
      const headerTimestamp = response.headers['x-last-modified'];
      const timestampToCache = headerTimestamp || serverTimestamp;
      
      console.log(`✅ API Response: ${data.length} receipts | Header timestamp: ${headerTimestamp}`);
      
      setReceipts(data);
      setReceiptsOffset(data.length);
      setHasMoreReceipts(data.length === RECEIPTS_PER_PAGE);
      
      if (timestampToCache) {
        saveToCache(groupId, data, timestampToCache);
      }
      
    } catch (error) {
      if (isReadCancelled(error)) return;
      console.error('❌ Error loading receipts:', error);
      toast.error('Error al actualizar comprobantes; se conservan los datos visibles');
    } finally {
      setLoadingReceipts(false);
    }
  }, [RECEIPTS_PER_PAGE, receiptRead.get]); // Solo RECEIPTS_PER_PAGE como dependencia

  // ========== RECUPERACIÓN DE COMPROBANTES PERDIDOS (GAPS) ==========
  const loadGapsForCliente = useCallback(async (clienteId) => {
    if (!clienteId) return;
    try {
      const res = await axios.get(`${API}/clientes/${clienteId}/gaps`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setGapsByClient(prev => ({ ...prev, [clienteId]: { ...(prev[clienteId] || {}), ...res.data } }));
    } catch (e) {
      // Silencioso: si falla, no mostrar toast para no molestar al cliente sin gaps
      console.warn('No se pudieron cargar gaps:', e?.response?.status);
    }
  }, []);

  // Refresca SOLO los counts de gaps por cliente (no toca lista de clientes)
  const refreshGapsSummary = useCallback(async (carteraId) => {
    const cid = carteraId;
    if (!cid) return;
    try {
      const gapsRes = await axios.get(`${API}/carteras/${cid}/gaps-summary`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      const counts = gapsRes.data?.counts || {};
      setGapsByClient(prev => {
        const next = { ...prev };
        Object.keys(counts).forEach(k => {
          next[k] = { ...(next[k] || {}), count: counts[k] };
        });
        // Los que no vinieron en counts -> 0
        Object.keys(next).forEach(k => {
          if (!(k in counts)) {
            next[k] = { ...(next[k] || {}), count: 0 };
          }
        });
        return next;
      });
    } catch (e) {
      console.warn('refreshGapsSummary fail:', e?.response?.status);
    }
  }, []);

  const handleRecoverGap = useCallback(async (gap, clienteId) => {
    const cartera_id = gapsByClient[clienteId]?.cartera_id_default;
    if (!cartera_id) {
      toast.error('No se pudo determinar la cartera del cliente');
      return;
    }
    setRecoveringGapId(gap.wa_message_id);
    try {
      const res = await axios.post(`${API}/receipts/recover-gap`, {
        wa_message_id: gap.wa_message_id,
        cliente_id: clienteId,
        cartera_id
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      if (res.data?.ok) {
        toast.success(res.data.message || 'Comprobante recuperado');
        invalidateCache(clienteId);
        await loadReceiptsForGroup(clienteId, activeCartera?.id);
        await loadGapsForCliente(clienteId);
        await refreshGapsSummary(activeCartera?.id);
      } else {
        const code = res.data?.code || 'UNKNOWN';
        toast.error(`No se pudo recuperar (${code}): ${res.data?.error || 'error'}`);
      }
    } catch (e) {
      toast.error('Error al recuperar: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRecoveringGapId(null);
    }
  }, [gapsByClient, activeCartera?.id, loadReceiptsForGroup, loadGapsForCliente, refreshGapsSummary]);

  const handleCreatePlaceholder = useCallback(async (gap, clienteId) => {
    const cartera_id = gapsByClient[clienteId]?.cartera_id_default;
    if (!cartera_id) {
      toast.error('No se pudo determinar la cartera del cliente');
      return;
    }
    if (!window.confirm(`¿Crear placeholder vacío para este comprobante del ${new Date(gap.timestamp).toLocaleString('es-AR')}? Deberás completarlo manualmente después.`)) {
      return;
    }
    setRecoveringGapId(gap.wa_message_id);
    try {
      const res = await axios.post(`${API}/receipts/create-placeholder`, {
        wa_message_id: gap.wa_message_id,
        cliente_id: clienteId,
        cartera_id,
        original_timestamp: gap.timestamp,
        from_name: gap.from_name
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      if (res.data?.ok) {
        toast.success('Placeholder creado. Editalo para completar datos.');
        invalidateCache(clienteId);
        await loadReceiptsForGroup(clienteId, activeCartera?.id);
        await loadGapsForCliente(clienteId);
        await refreshGapsSummary(activeCartera?.id);
      } else {
        toast.error(res.data?.error || 'No se pudo crear placeholder');
      }
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRecoveringGapId(null);
    }
  }, [gapsByClient, activeCartera?.id, loadReceiptsForGroup, loadGapsForCliente, refreshGapsSummary]);

  const handleDismissGap = useCallback(async (gap, clienteId) => {
    if (!window.confirm(`¿Descartar este comprobante perdido? No se creará ningún movimiento y desaparecerá del listado.\n\nFecha: ${new Date(gap.timestamp).toLocaleString('es-AR')}\nRemitente: ${gap.from_name || 'sin nombre'}`)) {
      return;
    }
    setRecoveringGapId(gap.wa_message_id);
    try {
      const res = await axios.post(`${API}/receipts/dismiss-gap`, {
        wa_message_id: gap.wa_message_id
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      if (res.data?.ok) {
        toast.success('Comprobante descartado');
        await loadGapsForCliente(clienteId);
        await refreshGapsSummary(activeCartera?.id);
      } else {
        toast.error(res.data?.error || 'No se pudo descartar');
      }
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRecoveringGapId(null);
    }
  }, [activeCartera?.id, loadGapsForCliente, refreshGapsSummary]);

  // Función para cargar notificaciones - DECLARADA DESPUÉS de fetchClientes y loadReceiptsForGroup
  const loadNotifications = useCallback(async (showToast = false) => {
    try {
      const response = await axios.get(`${API}/notifications?unread_only=true`);
      const newNotifications = response.data;
      
      setUnreadCount(newNotifications.length);
      
      // Si hay notificaciones y debemos mostrar toast
      if (showToast && newNotifications.length > 0) {
        // Filtrar solo las notificaciones que NO hemos visto antes
        const unseenNotifications = newNotifications.filter(
          notif => !seenNotificationIds.has(notif.id)
        );
        
        if (unseenNotifications.length > 0) {
          // Mostrar toast solo para la más reciente no vista
          const latestUnseen = unseenNotifications[0];
          
          toast.info(`🔔 ${latestUnseen.message}`, {
            duration: 5000,
          });
          
          // Auto-refresh: Recargar grupos y comprobantes
          fetchClientes();
          if (selectedCliente) {
            loadReceiptsForGroup(selectedCliente, activeCartera?.id);
          }
          
          // Marcar todas las notificaciones actuales como vistas
          const allIds = new Set([...seenNotificationIds, ...newNotifications.map(n => n.id)]);
          setSeenNotificationIds(allIds);
        }
      }
      
      setNotifications(newNotifications);
    } catch (error) {
      // Silenciar error si el endpoint no existe o no hay permisos (403 o 404)
      if (error.response?.status !== 403 && error.response?.status !== 404) {
        console.error('Error loading notifications:', error);
      }
    }
  }, [seenNotificationIds, selectedCliente, activeCartera?.id, fetchClientes, loadReceiptsForGroup]);

  // Función para cargar destinatarios únicos del cliente (AGREGADO 2025-11-04)
  const loadDestinatarios = useCallback(async (clienteId) => {
    if (!clienteId) {
      setAvailableDestinatarios([]);
      return;
    }
    
    setLoadingDestinatarios(true);
    try {
      const response = await axios.get(`${API}/clientes/${clienteId}/destinatarios`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setAvailableDestinatarios(response.data.destinatarios || []);
      console.log(`📋 Cargados ${response.data.destinatarios.length} destinatarios únicos`);
    } catch (error) {
      console.error('Error cargando destinatarios:', error);
      if (error.response?.status !== 404) {
        toast.error('Error al cargar destinatarios');
      }
      setAvailableDestinatarios([]);
    } finally {
      setLoadingDestinatarios(false);
    }
  }, []);

  // Polling para notificaciones y solicitudes de eliminación (cada 10 segundos)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    // Carga inicial
    loadNotifications(false);
    loadDeletionRequests();
    
    // Configurar polling
    const interval = setInterval(() => {
      loadNotifications(true);
      loadDeletionRequests();
    }, 10000); // 10 segundos
    
    return () => clearInterval(interval);
  }, [loadNotifications]);

  // Cargar datos iniciales solo una vez
  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchUser();
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Manejar cambio de cartera activa - LIMPIAR TODO
  useEffect(() => {
    if (!activeCartera) {
      setClientes([]);
      setReceipts([]);
      setSelectedCliente(null);
      receiptsCache.current = {}; // Limpiar TODA la caché al cambiar cartera
      return;
    }
    
    console.log('📂 CAMBIO DE CARTERA:', activeCartera.name, '| ID:', activeCartera.id);
    setSwitchingCartera(true);
    
    // AISLAMIENTO NIVEL 1: Limpiar TODO cuando cambia cartera
    setClientes([]);
    setReceipts([]);
    setSelectedCliente(null);
    receiptsCache.current = {}; // ← CRÍTICO: Limpiar caché completa
    
    // Debounce para evitar múltiples llamadas rápidas
    const timeoutId = setTimeout(() => {
      const loadPortfolioData = async () => {
        try {
          await fetchClientes();
        } catch (error) {
          console.error('Error loading portfolio data:', error);
          toast.error('Error al cargar datos de la cartera');
        } finally {
          setSwitchingCartera(false);
        }
      };
      
      loadPortfolioData();
    }, 100);
    
    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      setSwitchingCartera(false);
    };
  }, [activeCartera?.id, fetchClientes]);

  // Manejar cambio de cuenta seleccionada - AISLAMIENTO NIVEL 2
  useEffect(() => {
    if (selectedCliente && activeCartera) {
      console.log('🗂️ CAMBIO DE CUENTA:', selectedCliente, '| Cartera:', activeCartera.id);
      
      // AISLAMIENTO NIVEL 2: Solo cargar si el cliente ID realmente cambió
      const currentClienteId = selectedCliente;
      
      // Si el cliente no cambió, no recargar (evita borrar comprobantes de WebSocket)
      if (lastLoadedClienteId.current === currentClienteId) {
        console.log('⏭️ Cliente no cambió, saltando recarga de receipts');
        return;
      }
      
      // Cliente cambió, limpiar y recargar
      lastLoadedClienteId.current = currentClienteId;
      setReceipts([]);
      
      // Llamar con los valores EXACTOS actuales
      const currentPortfolioId = activeCartera.id;
      
      console.log('🔍 Loading receipts for:', {
        groupId: currentClienteId,
        portfolioId: currentPortfolioId
      });
      
      loadReceiptsForGroup(currentClienteId, currentPortfolioId);

      // Cargar gaps (comprobantes perdidos) para este cliente
      loadGapsForCliente(currentClienteId);

      // Cargar cortes de estado de cuenta
      axios.get(
        `${API}/clientes/${currentClienteId}/cortes-estado-cuenta`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      ).then(res => setCortesEstadoCuenta(res.data || []))
       .catch(() => setCortesEstadoCuenta([]));
      
      // Cargar destinatarios únicos del cliente (AGREGADO 2025-11-04)
      loadDestinatarios(currentClienteId);
    } else {
      setReceipts([]);
      lastLoadedClienteId.current = null;
      setAvailableDestinatarios([]);
      setSelectedDestinatario('all');
    }
  }, [selectedCliente, activeCartera?.id, loadReceiptsForGroup, loadDestinatarios, loadGapsForCliente]);

  const createCliente = async () => {
    if (!newClienteName.trim()) {
      toast.error('El nombre del cliente es requerido');
      return;
    }

    if (!activeCartera) {
      toast.error('Debe seleccionar una cartera primero');
      return;
    }

    try {
      // Crear cliente asociado a la cartera
      const clienteResponse = await axios.post(`${API}/clientes`, {
        name: newClienteName,
        cartera_id: activeCartera.id
      });
      
      // Mostrar PIN al admin
      const clientePIN = clienteResponse.data.access_pin;
      if (clientePIN) {
        toast.success(
          <div className="space-y-2">
            <p className="font-bold">Cliente "{newClienteName}" creado exitosamente</p>
            <div className="bg-amber-50 border border-amber-200 p-2 rounded">
              <p className="text-sm font-semibold text-amber-900">PIN de acceso del cliente:</p>
              <p className="text-2xl font-mono font-bold text-amber-700 tracking-widest">{clientePIN}</p>
              <p className="text-xs text-amber-600 mt-1">⚠️ Comparte este PIN con el cliente. No se mostrará de nuevo.</p>
            </div>
          </div>,
          { duration: 15000 }
        );
      } else {
        toast.success(`Cliente "${newClienteName}" creado exitosamente`);
      }
      
      setShowClienteDialog(false);
      setNewClienteName('');
      await fetchClientes();
    } catch (error) {
      toast.error('Error al crear cliente');
      console.error(error);
    }
  };

  const createSubaccount = async () => {
    if (!newSubaccountName.trim()) {
      toast.error('El nombre de la subcuenta es requerido');
      return;
    }

    if (!activeCartera) {
      toast.error('Debe seleccionar una cartera primero');
      return;
    }

    try {
      const parentGroup = clientes.find(g => g.id === selectedParentForSubaccount);
      await axios.post(`${API}/groups`, {
        name: newSubaccountName,
        color: parentGroup?.color || '#3b82f6',
        portfolio_id: activeCartera.id,
        parent_id: selectedParentForSubaccount
      });
      toast.success('Subcuenta creada exitosamente');
      setShowSubaccountDialog(false);
      setNewSubaccountName('');
      setSelectedParentForSubaccount(null);
      await fetchClientes();
    } catch (error) {
      toast.error('Error al crear subcuenta');
    }
  };

  const deleteGroup = async (groupId) => {
    if (!window.confirm('¿Estás seguro? Se eliminarán todos los comprobantes de este grupo.')) return;

    try {
      await axios.delete(`${API}/groups/${groupId}`);
      toast.success('Grupo eliminado');
      setSelectedCliente(null);
      await fetchClientes();
    } catch (error) {
      toast.error('Error al eliminar grupo');
    }
  };

  // Función para exportar comprobantes filtrados a Excel (AGREGADO 2025-11-04)
  const exportToExcel = () => {
    try {
      // Aplicar filtros
      let filteredReceipts = [...receipts];
      
      // 1. Filtro de fecha (si está activo)
      if (dateFilterEnabled && startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        filteredReceipts = filteredReceipts.filter(r => {
          const receiptDate = new Date(r.date);
          return receiptDate >= start && receiptDate <= end;
        });
      }
      
      // 2. Filtro de destinatario (si hay selección)
      if (selectedDestinatario && selectedDestinatario !== 'all') {
        filteredReceipts = filteredReceipts.filter(r => 
          r.destination_holder === selectedDestinatario
        );
      }
      
      if (filteredReceipts.length === 0) {
        toast.error('No hay comprobantes para exportar con los filtros seleccionados');
        return;
      }
      
      // Preparar datos
      const data = filteredReceipts.map(r => ({
        'Fecha': r.date ? format(new Date(r.date), 'dd/MM/yyyy') : '-',
        'Descripción': r.description || '-',
        'Origen': r.origin_holder || '-',
        'CUIL': r.origin_cuil || '-',
        'Destino': r.destination_holder || '-',
        'Monto': r.amount || 0,
        'Archivo': r.file_name || 'comprobante',
        'Renglón': r.reconciliation_status === 'matched' ? 'Conciliado' : 
                   r.reconciliation_status === 'partial' ? 'Parcial' : 
                   r.reconciliation_status === 'not_found' ? 'No encontrado' :
                   r.reconciliation_status === 'push_matched' ? 'Push Auto' :
                   r.reconciliation_status === 'playwright_matched' ? 'Playwright' :
                   r.reconciliation_status === 'santander_worker_realtime' ? 'Santander Auto' : '-'
      }));
      
      // Calcular suma total
      const totalMonto = filteredReceipts.reduce((sum, r) => sum + (r.amount || 0), 0);
      
      // Agregar fila de total
      data.push({
        'ID': '',
        'Fecha': '',
        'Descripción': '',
        'Origen': '',
        'CUIL': '',
        'Destino': 'TOTAL',
        'Monto': totalMonto,
        'Archivo': '',
        'Renglón': ''
      });
      
      // Crear workbook
      const ws = XLSX.utils.json_to_sheet(data);
      
      // Configurar anchos de columna
      ws['!cols'] = [
        {wch: 15}, // ID
        {wch: 12}, // Fecha
        {wch: 20}, // Descripción
        {wch: 25}, // Origen
        {wch: 15}, // CUIL
        {wch: 25}, // Destino
        {wch: 15}, // Monto
        {wch: 20}, // Archivo
        {wch: 15}  // Renglón
      ];
      
      // Formato de montos como número
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let row = range.s.r + 1; row <= range.e.r; row++) {
        const cellAddress = XLSX.utils.encode_cell({r: row, c: 6}); // Columna G (Monto)
        if (ws[cellAddress] && typeof ws[cellAddress].v === 'number') {
          ws[cellAddress].t = 'n';
          ws[cellAddress].z = '#,##0.00';
        }
      }
      
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Comprobantes');
      
      // Nombre del archivo
      const destinatario = selectedDestinatario || 'Todos';
      const fecha = format(new Date(), 'yyyy-MM-dd');
      const filename = `${destinatario}_${fecha}.xlsx`;
      
      XLSX.writeFile(wb, filename);
      
      toast.success(`✅ Excel exportado: ${filename}`, {
        description: `${filteredReceipts.length} comprobantes | Total: ${formatAmount(totalMonto)}`
      });
    } catch (error) {
      console.error('Error exportando Excel:', error);
      toast.error('Error al exportar Excel');
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      const validFiles = files.filter(file => validTypes.includes(file.type));
      
      // Combinar con archivos existentes
      const newFilesList = [...selectedFiles, ...validFiles];
      
      // Validar límite de 20 archivos
      if (newFilesList.length > 20) {
        toast.error(`Máximo 20 comprobantes por vez. Tienes ${selectedFiles.length} seleccionados, intentas agregar ${validFiles.length} más.`);
        e.target.value = ''; // Limpiar el input
        return;
      }
      
      if (validFiles.length !== files.length) {
        toast.error('Algunos archivos fueron excluidos. Usa JPG, PNG o PDF');
      }
      
      setSelectedFiles(newFilesList);
      e.target.value = ''; // Limpiar input para permitir nueva selección
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      const validFiles = files.filter(file => validTypes.includes(file.type));
      
      // Combinar con archivos existentes
      const newFilesList = [...selectedFiles, ...validFiles];
      
      // Validar límite de 20 archivos
      if (newFilesList.length > 20) {
        toast.error(`Máximo 20 comprobantes por vez. Tienes ${selectedFiles.length} seleccionados, intentas agregar ${validFiles.length} más.`);
        return;
      }
      
      if (validFiles.length !== files.length) {
        toast.error('Algunos archivos fueron excluidos. Usa JPG, PNG o PDF');
      }
      
      setSelectedFiles(newFilesList);
    }
  };

  const uploadReceipt = async () => {
    if (selectedFiles.length === 0 || !selectedCliente) {
      toast.error('Selecciona archivos y un cliente');
      return;
    }

    if (selectedFiles.length > 20) {
      toast.error('Máximo 20 comprobantes por vez');
      return;
    }

    setUploadLoading(true);
    const totalFiles = selectedFiles.length;
    const formData = new FormData();
    
    selectedFiles.forEach(file => {
      formData.append('files', file);
    });
    formData.append('cliente_id', selectedCliente);

    try {
      const response = await axios.post(`${API}/receipts/upload-multiple`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const results = response.data.results;
      const successCount = results.filter(r => r.status === 'success').length;
      const skippedCount = results.filter(r => r.status === 'skipped').length;
      const rejectedCount = results.filter(r => r.status === 'rejected').length;
      const rejectedItems = results.filter(r => r.status === 'rejected');
      
      if (successCount > 0) {
        // Mensaje mejorado con formato "X de Y éxito"
        toast.success(`✅ ${successCount} de ${totalFiles} comprobante(s) - ÉXITO`);
      }
      if (skippedCount > 0) {
        // Mostrar detalle de cada duplicado con el cliente donde ya está cargado
        const skippedItems = results.filter(r => r.status === 'skipped');
        skippedItems.forEach(item => {
          toast.warning(
            <div>
              <strong>⚠️ Duplicado:</strong>
              <br />
              {item.filename}
              <br />
              <small>{item.reason}</small>
            </div>,
            { duration: 6000 }
          );
        });
      }
      if (rejectedCount > 0) {
        // Mostrar mensaje detallado para rechazos
        rejectedItems.forEach(item => {
          toast.error(
            <div>
              <strong>❌ Comprobante rechazado:</strong>
              <br />
              {item.filename}
              <br />
              <small>{item.reason}</small>
            </div>,
            { duration: 8000 }
          );
        });
      }
      
      // NO CERRAR EL DIALOG - dejarlo abierto para seguir subiendo
      // setShowUploadDialog(false);  // COMENTADO
      setSelectedFiles([]); // Limpiar archivos seleccionados
      if (fileInputRef.current) {
        fileInputRef.current.value = ''; // Resetear el input manualmente
      }
      
      // Invalidar caché del cliente actual
      if (selectedCliente) {
        invalidateCache(selectedCliente);
      }
      
      await loadReceiptsForGroup(selectedCliente, activeCartera.id);
      await fetchClientes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir comprobantes');
    } finally {
      setUploadLoading(false);
    }
  };

  const exportSubaccountToExcel = async (subaccountId) => {
    try {
      const response = await axios.get(`${API}/receipts/export-account/${subaccountId}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `subcuenta_export_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Subcuenta exportada');
    } catch (error) {
      toast.error('Error al exportar subcuenta');
    }
  };

  const downloadReceiptBackup = async (receiptId, filename) => {
    try {
      const response = await axios.get(`${API}/receipts/${receiptId}/download`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Archivo descargado');
    } catch (error) {
      toast.error('Error al descargar archivo');
    }
  };

  const previewReceiptBackup = async (receipt) => {
    setPreviewLoading(true);
    setShowPreviewDialog(true);
    
    try {
      const response = await axios.get(`${API}/receipts/${receipt.id}/download`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(response.data);
      setPreviewReceipt({
        ...receipt,
        previewUrl: url
      });
    } catch (error) {
      toast.error('Error al cargar vista previa');
      setShowPreviewDialog(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const deleteReceipt = async (receiptId) => {
    if (!window.confirm('¿Eliminar este comprobante?')) return;

    try {
      await axios.delete(`${API}/receipts/${receiptId}`);
      toast.success('Comprobante eliminado');
      
      // Invalidar caché del cliente actual
      if (selectedCliente) {
        invalidateCache(selectedCliente);
      }
      
      await loadReceiptsForGroup(selectedCliente, activeCartera.id);
      await fetchClientes();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Funciones para mover comprobantes arriba/abajo
  const moveReceiptUp = async (receiptId) => {
    try {
      await axios.post(`${API}/receipts/${receiptId}/move-up`);
      toast.success("Comprobante movido hacia arriba");
      if (selectedCliente) {
        invalidateCache(selectedCliente);
      }
      await loadReceiptsForGroup(selectedCliente, activeCartera.id);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info("El comprobante ya está en la primera posición");
      } else {
        toast.error("Error al mover comprobante");
      }
    }
  };

  const moveReceiptDown = async (receiptId) => {
    try {
      await axios.post(`${API}/receipts/${receiptId}/move-down`);
      toast.success("Comprobante movido hacia abajo");
      if (selectedCliente) {
        invalidateCache(selectedCliente);
      }
      await loadReceiptsForGroup(selectedCliente, activeCartera.id);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info("El comprobante ya está en la última posición");
      } else {
        toast.error("Error al mover comprobante");
      }
    }
  };

  const resetGroupBalance = async (groupId) => {
    if (!window.confirm('¿Resetear el balance de esta cuenta a $0? Esto NO eliminará los comprobantes, solo reiniciará el contador.')) return;

    try {
      // Get current cliente
      const cliente = clientes.find(c => c.id === groupId);
      if (!cliente) return;

      // Update balance to 0
      await axios.put(`${API}/groups/${groupId}`, null, {
        params: { balance: 0 }
      });
      
      toast.success('Balance reseteado a $0');
      await fetchClientes();
    } catch (error) {
      toast.error('Error al resetear balance');
    }
  };

  const openEditDialog = (group) => {
    setEditingCliente(group);
    setEditClienteName(group.name);
    setShowEditDialog(true);
  };

  const updateGroupName = async () => {
    if (!editClienteName.trim()) {
      toast.error('El nombre no puede estar vacío');
      return;
    }

    try {
      await axios.put(`${API}/groups/${editingCliente.id}`, null, {
        params: { name: editClienteName.trim() }
      });
      
      toast.success('Nombre actualizado');
      setShowEditDialog(false);
      setEditingCliente(null);
      setEditClienteName('');
      await fetchClientes();
    } catch (error) {
      toast.error('Error al actualizar nombre');
    }
  };

  const openManualEntryDialog = (groupId) => {
    setTargetClienteForManual(groupId);
    setManualEntryType('manual_credit');
    setManualAmount('');
    setManualDescription('');
    setManualDate(format(new Date(), 'yyyy-MM-dd'));
    setManualDestination('');
    setManualFile(null);
    setShowManualEntryDialog(true);
  };

  const handleManualFileChange = async (file) => {
    if (!file) return;
    
    setManualFile(file);
    setProcessingOCR(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/receipts/ocr-preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success && response.data.data) {
        const ocrData = response.data.data;
        
        // Pre-llenar campos con datos del OCR
        if (ocrData.amount && ocrData.amount > 0) {
          setManualAmount(ocrData.amount.toString());
        }
        
        if (ocrData.destination_holder) {
          setManualDestination(ocrData.destination_holder);
        }
        
        if (ocrData.date) {
          setManualDate(ocrData.date);
        }
        
        // Actualizar descripción con info extraída
        const description = `Pago a ${ocrData.destination_holder || 'destinatario'} - ${ocrData.bank || 'N/A'}`;
        setManualDescription(description);
        
        toast.success('✓ Comprobante procesado - Campos completados automáticamente');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar el comprobante con OCR');
    } finally {
      setProcessingOCR(false);
    }
  };

  // Reintentar OCR en un comprobante fallido
  const retryOcr = async (receiptId) => {
    setRetryingOcr(receiptId);
    try {
      const response = await axios.post(`${API}/receipts/${receiptId}/retry-ocr`);
      toast.success(response.data.message);
      
      // Recargar datos
      if (selectedCliente) {
        invalidateCache(selectedCliente);
        await loadReceiptsForGroup(selectedCliente, activeCartera?.id);
        await fetchClientes();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reintentar OCR');
    } finally {
      setRetryingOcr(null);
    }
  };

  // Abrir modal para editar movimiento manual
  const openEditManualEntry = (receipt) => {
    setEditingManualReceipt(receipt);
    setEditManualType(receipt.entry_type);
    setEditManualAmount(Math.abs(receipt.amount).toString());
    setEditManualDescription(receipt.description || '');
    setEditManualDate(receipt.date || '');
    setEditManualDestination(receipt.destination_holder || '');
    setShowEditManualDialog(true);
  };

  // Abrir modal de reubicacion cronologica de un receipt recuperado via reply 🎯
  // Muestra la lista de receipts vecinos (+/- 2 dias) en orden cronologico, con
  // botones "Insertar aqui" entre cada par para ubicar el recuperado en ese hueco.
  const editRecoveredTime = (receipt) => {
    // Filtrar receipts del mismo cliente en ventana de +/- 2 dias alrededor del receipt
    const baseDateStr = receipt.date || new Date().toISOString().slice(0, 10);
    const baseDate = new Date(baseDateStr + 'T00:00:00');
    const rangeStart = new Date(baseDate); rangeStart.setDate(rangeStart.getDate() - 2);
    const rangeEnd = new Date(baseDate); rangeEnd.setDate(rangeEnd.getDate() + 2);
    const rangeStartStr = rangeStart.toISOString().slice(0, 10);
    const rangeEndStr = rangeEnd.toISOString().slice(0, 10);

    // IMPORTANTE: preferimos sort_order (epoch UTC que usa el Dashboard para
    // ordenar la lista). Si un vecino no lo tiene, caemos a date+time (hora
    // AR real) que tambien da un epoch consistente. Jamas usamos created_at
    // directamente porque llega naive-UTC y el browser AR lo interpreta como
    // local (3h de desfase).
    const getSortDate = (r) => {
      if (typeof r.sort_order === 'number' && !isNaN(r.sort_order)) {
        return r.sort_order * 1000;
      }
      const d = r.date || '';
      const t = r.time || '00:00';
      const iso = `${d}T${(t.length === 5 ? t + ':00' : t)}`;
      const ms = new Date(iso).getTime();
      return isNaN(ms) ? new Date(r.created_at || d).getTime() : ms;
    };
    const neighbors = (receipts || [])
      .filter(r => r.cliente_id === receipt.cliente_id && r.id !== receipt.id)
      .filter(r => {
        const d = r.date || '';
        return d >= rangeStartStr && d <= rangeEndStr;
      })
      .sort((a, b) => getSortDate(b) - getSortDate(a)); // DESC (mas nuevo arriba, como Dashboard)

    setRecoveryReloc({ receipt, neighbors });
  };

  // Ubicar el receipt recuperado ENTRE dos vecinos (o al principio/final)
  // Calcula el punto medio en tiempo entre beforeReceipt (mas nuevo, arriba) y
  // afterReceipt (mas viejo, abajo), lo convierte a hora AR y llama al endpoint
  // update-time para que actualice created_at y sort_order.
  const placeRecoveredBetween = async (beforeReceipt, afterReceipt) => {
    const reloc = recoveryReloc;
    if (!reloc) return;
    const receipt = reloc.receipt;

    // Preferimos sort_order (epoch UTC del Dashboard) para que el midpoint
    // caiga EXACTAMENTE entre los vecinos visualmente. Fallback: date+time AR
    // convertido a epoch. Evitamos created_at por su ambigüedad de tz.
    const getTs = (r) => {
      if (typeof r.sort_order === 'number' && !isNaN(r.sort_order)) {
        return r.sort_order * 1000;
      }
      const d = r.date || '';
      const t = r.time || '00:00';
      const iso = `${d}T${(t.length === 5 ? t + ':00' : t)}`;
      const ms = new Date(iso).getTime();
      return isNaN(ms) ? new Date(r.created_at || d).getTime() : ms;
    };
    let targetMs;
    if (beforeReceipt && afterReceipt) {
      // Entre dos vecinos: punto medio
      targetMs = (getTs(beforeReceipt) + getTs(afterReceipt)) / 2;
    } else if (beforeReceipt) {
      // Al final de la lista (mas viejo que todos): 60s antes del ultimo
      targetMs = getTs(beforeReceipt) - 60000;
    } else if (afterReceipt) {
      // Al principio (mas nuevo que todos): 60s despues del primero
      targetMs = getTs(afterReceipt) + 60000;
    } else {
      targetMs = new Date().getTime();
    }

    // targetMs es un epoch UTC real (sort_order*1000 o date+time AR convertido
    // por el browser). Al formatearlo con getHours/getMinutes/getSeconds en
    // zona AR obtenemos la hora AR exacta a reubicar. Agregamos segundos para
    // precision fina del sort_order (asi el midpoint cae exactamente entre
    // vecinos cuando sus sort_order estan separados por pocos segundos).
    const arDate = new Date(targetMs);
    const y = arDate.getFullYear();
    const mo = String(arDate.getMonth() + 1).padStart(2, '0');
    const d = String(arDate.getDate()).padStart(2, '0');
    const hh = String(arDate.getHours()).padStart(2, '0');
    const mm = String(arDate.getMinutes()).padStart(2, '0');
    const ss = String(arDate.getSeconds()).padStart(2, '0');
    const dateStr = `${y}-${mo}-${d}`;
    const timeStr = `${hh}:${mm}:${ss}`;

    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/receipts/${receipt.id}/update-time`,
        { date: dateStr, time: timeStr },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Reubicado a ${dateStr} ${hh}:${mm}`);
      setRecoveryReloc(null);
      if (selectedCliente) {
        await loadReceiptsForGroup(selectedCliente, activeCartera?.id);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`Error: ${msg}`);
    }
  };

  // Guardar edicion de movimiento manual
  const saveEditManualEntry = async () => {
    if (!editManualAmount || parseFloat(editManualAmount) <= 0) {
      toast.error('Ingresa un monto valido');
      return;
    }
    
    setEditManualLoading(true);
    const formData = new FormData();
    formData.append('entry_type', editManualType);
    formData.append('amount', parseFloat(editManualAmount));
    formData.append('description', editManualDescription);
    formData.append('date', editManualDate);
    if (editManualType === 'manual_payment') {
      formData.append('destination_holder', editManualDestination);
    }
    
    try {
      await axios.put(
        `${API}/receipts/${editingManualReceipt.id}/manual`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success('Movimiento actualizado exitosamente');
      setShowEditManualDialog(false);
      setEditingManualReceipt(null);
      
      // Recargar datos
      const clienteId = editingManualReceipt.cliente_id || editingManualReceipt.group_id;
      if (clienteId) {
        invalidateCache(clienteId);
      }
      await fetchClientes();
      if (selectedCliente) {
        await loadReceiptsForGroup(selectedCliente, activeCartera?.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar movimiento');
    } finally {
      setEditManualLoading(false);
    }
  };

  const createManualEntry = async () => {
    if (!manualAmount || parseFloat(manualAmount) <= 0) {
      toast.error('Ingresa un monto válido');
      return;
    }

    if (!manualDescription.trim()) {
      toast.error('La descripción es requerida');
      return;
    }

    if (manualEntryType === 'manual_payment' && !manualDestination.trim() && !manualFile) {
      toast.error('Para pagos, ingresa un destinatario o sube un comprobante con OCR');
      return;
    }

    setManualLoading(true);
    const formData = new FormData();
    formData.append('cliente_id', targetClienteForManual);  // Nueva estructura: cliente_id directo
    formData.append('entry_type', manualEntryType);
    formData.append('amount', parseFloat(manualAmount));
    formData.append('description', manualDescription);
    formData.append('date', manualDate);
    
    if (manualEntryType === 'manual_payment') {
      formData.append('destination_holder', manualDestination);
      if (manualFile) {
        formData.append('file', manualFile);
      }
    }

    try {
      const response = await axios.post(`${API}/receipts/manual`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const typeNames = {
        'manual_credit': 'Crédito',
        'manual_debit': 'Débito',
        'manual_payment': 'Pago'
      };
      
      let message = `${typeNames[manualEntryType]} registrado exitosamente`;
      
      // Si se procesó con OCR, agregar info
      if (response.data.ocr_processed && response.data.extracted_destination) {
        message += `. Destinatario extraído: ${response.data.extracted_destination}`;
      }
      
      toast.success(message);
      setShowManualEntryDialog(false);
      
      // Invalidar caché del cliente
      if (targetClienteForManual) {
        invalidateCache(targetClienteForManual);
      }
      
      await fetchClientes();
      if (selectedCliente === targetClienteForManual) {
        await loadReceiptsForGroup(targetClienteForManual, activeCartera.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear movimiento');
    } finally {
      setManualLoading(false);
    }
  };

  const currentCliente = clientes.find(c => c.id === selectedCliente);
  const totalAmount = receipts.reduce((sum, r) => sum + r.amount, 0);

  // SIMPLIFICADO: Solo cuentas planas, sin subcuentas
  const accounts = clientes; // Todos los clientes
  const generalBalance = clientes.reduce((sum, cliente) => sum + cliente.balance, 0);
  
  // Función para ordenar cuentas en vista simple
  const getSortedAccounts = (accountsList, order) => {
    const sorted = [...accountsList];
    
    switch(order) {
      case 'most_debtor':
        // Más deudor primero (más negativo primero)
        return sorted.sort((a, b) => a.balance - b.balance);
      
      case 'most_positive':
        // Más positivo primero (más alto primero)
        return sorted.sort((a, b) => b.balance - a.balance);
      
      case 'alpha_asc':
        // Alfabético A-Z
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      
      case 'alpha_desc':
        // Alfabético Z-A
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      
      default:
        // Orden por defecto (como están en la BD)
        return sorted;
    }
  };
  
  // Cuentas ordenadas para vista simple
  const sortedAccounts = getSortedAccounts(accounts, sortOrder);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-semibold text-gray-600">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="max-w-[1700px] mx-auto px-6 py-8 relative"><ReadRecoveryNotice reads={[clientRead, receiptRead]} />
        {/* FASE BAL-1 (2026-02): container ampliado de max-w-7xl (1280px) a 1700px
            para evitar scroll lateral en monitores grandes y dejar espacio a la
            nueva columna "Saldo" del running balance. */}
        {switchingCartera && (
          <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-gray-700 dark:text-gray-300 font-medium">Cambiando a {activeCartera?.name}...</p>
            </div>
          </div>
        )}
        
        {/* Empty Portfolio State */}
        {!activeCartera ? (
          <Card className="mb-8 bg-gradient-to-br from-indigo-100 to-purple-100 border-indigo-200">
            <CardContent className="py-12 text-center">
              <Wallet className="w-16 h-16 text-indigo-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No hay cartera seleccionada</h3>
              <p className="text-gray-600 mb-6">
                Crea o selecciona una cartera en el menú lateral para comenzar a gestionar tus movimientos financieros.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
        {/* Saldo General Total */}
        <Card className={`mb-8 border-0 shadow-xl ${
          generalBalance < 0 
            ? 'bg-gradient-to-br from-red-500 to-red-600' 
            : 'bg-gradient-to-br from-indigo-500 to-purple-600'
        }`}>
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/90 text-sm font-medium mb-1">Saldo General Total - {activeCartera.name}</p>
                <p className="text-white text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  ${generalBalance.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-white/80 text-xs mt-2">
                  {accounts.length} {accounts.length === 1 ? 'cliente' : 'clientes'}
                </p>
              </div>
              <div className="bg-white/20 p-4 rounded-xl backdrop-blur-sm">
                <Wallet className="w-12 h-12 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Groups Section */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Mis Clientes</h2>
              
              {/* Toggle de Vista */}
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => {
                    setViewMode('grid');
                    localStorage.setItem('dashboard_view_mode', 'grid');
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'grid' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Vista de tarjetas"
                >
                  <LayoutGrid className="w-4 h-4" />
                  <span>Tarjetas</span>
                </button>
                <button
                  onClick={() => {
                    setViewMode('list');
                    localStorage.setItem('dashboard_view_mode', 'list');
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'list' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Vista simple"
                >
                  <List className="w-4 h-4" />
                  <span>Simple</span>
                </button>
              </div>
              
              {unreadCount > 0 && (
                <button
                  onClick={async () => {
                    try {
                      await axios.put(`${API}/notifications/mark-all-read`);
                      setUnreadCount(0);
                      setNotifications([]);
                      toast.success('Notificaciones marcadas como leídas');
                    } catch (error) {
                      console.error('Error marking notifications as read:', error);
                      toast.error('Error al marcar notificaciones como leídas');
                    }
                  }}
                  className="relative hover:scale-105 transition-transform cursor-pointer"
                  title="Clic para marcar todas como leídas"
                >
                  <div className="flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1 rounded-full animate-pulse hover:bg-blue-200">
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
                    </span>
                    <span className="text-sm font-semibold">{unreadCount} nueva{unreadCount !== 1 ? 's' : ''}</span>
                    <X className="w-3 h-3 ml-1 opacity-70" />
                  </div>
                </button>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              {/* Botón Exportar Conciliación */}
              {selectedCliente && (
                <Button
                  onClick={exportReconciliation}
                  className="flex items-center gap-2 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
                  title="Exportar reporte de conciliación del cliente seleccionado"
                >
                  <Download className="w-4 h-4" />
                  Exportar Conciliación
                </Button>
              )}
              
              {/* Botón Solicitudes de Eliminación */}
              <Button 
                onClick={() => setShowDeletionRequestsDialog(true)}
                className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 relative"
                title="Ver solicitudes de eliminación pendientes"
              >
                <Bell className="w-4 h-4" />
                Solicitudes
                {deletionRequests.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center animate-pulse">
                    {deletionRequests.length}
                  </span>
                )}
              </Button>
              
              <Button 
                onClick={recalculateBalances}
                disabled={recalculatingBalances}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
                title="Recalcular saldos de todos los clientes y grupos"
              >
                {recalculatingBalances ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Recalculando...
                  </>
                ) : (
                  <>
                    <Calculator className="w-4 h-4" />
                    Recalcular Saldos
                  </>
                )}
              </Button>
              <Button 
                onClick={deepReconciliation}
                disabled={deepReconciling}
                className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                title="Reconciliación profunda usando múltiples criterios"
              >
                {deepReconciling ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Reconciliando...
                  </>
                ) : (
                  <>
                    <FileSpreadsheet className="w-4 h-4" />
                    Reconciliar
                  </>
                )}
              </Button>
              <Button 
                onClick={reprocessAllReceipts}
                disabled={reprocessing}
                className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                title="Re-procesar todos los comprobantes para actualizar fechas"
              >
                {reprocessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Re-procesando...
                  </>
                ) : (
                  <>
                    <RotateCcw className="w-4 h-4" />
                    Re-procesar OCR
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Dialog de Subir Comprobantes */}
          <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Subir Comprobantes</DialogTitle>
                <DialogDescription>Arrastra o selecciona múltiples archivos (JPG, PNG, PDF)</DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <div
                  data-testid="drop-zone"
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                    dragActive ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-indigo-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <Upload className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-sm font-medium text-gray-700 mb-1">Arrastra tus archivos aquí</p>
                  <p className="text-xs text-gray-500 mb-4">o haz clic para seleccionar múltiples archivos</p>
                  
                  {selectedFiles.length > 0 && (
                    <div className="space-y-1 mb-4 max-h-40 overflow-y-auto">
                      <p className="text-xs font-medium text-gray-600 mb-2">{selectedFiles.length} archivo(s) seleccionado(s):</p>
                      {selectedFiles.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                          <span className="text-xs text-gray-600">{file.name}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedFiles(selectedFiles.filter((_, i) => i !== idx));
                            }}
                            className="h-6 w-6 p-0 hover:bg-red-50 hover:text-red-600"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <Input
                    ref={fileInputRef}
                    type="file"
                    data-testid="file-input"
                    accept="image/jpeg,image/png,image/jpg,application/pdf"
                    onChange={handleFileChange}
                    className="cursor-pointer"
                    multiple
                  />
                </div>
                <Button
                  data-testid="submit-upload-btn"
                  onClick={uploadReceipt}
                  disabled={selectedFiles.length === 0 || uploadLoading || !selectedCliente}
                  className="w-full"
                >
                  {uploadLoading ? `Procesando ${selectedFiles.length} archivo(s) con OCR...` : `Subir y Procesar ${selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}`}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Dialog de Registrar Pago a Cliente */}
          <Dialog open={showPaymentDialog} onOpenChange={(o) => { setShowPaymentDialog(o); if (!o) setPaymentNegativeConfirm(false); }}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Registrar Pago a Cliente</DialogTitle>
                <DialogDescription>
                  Registra un pago al cliente seleccionado
                </DialogDescription>
              </DialogHeader>
              {targetCustomerForPayment && (
                <div className="mt-2 p-3 bg-indigo-50 rounded-lg mx-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{targetCustomerForPayment.name}</p>
                      <p className="text-sm text-gray-600">Saldo actual: <span className="font-bold text-green-600">${targetCustomerForPayment.balance.toLocaleString()}</span></p>
                    </div>
                    <DollarSign className="w-8 h-8 text-indigo-500" />
                  </div>
                </div>
              )}
              <div className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="paymentAmount">Monto a Pagar</Label>
                  <Input
                    id="paymentAmount"
                    data-testid="payment-amount-input"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Ej: 1000.00"
                    value={paymentAmount}
                    onChange={(e) => { setPaymentAmount(e.target.value); setPaymentNegativeConfirm(false); }}
                    disabled={paymentLoading}
                  />
                </div>
                <div>
                  <Label htmlFor="paymentDescription">Descripción del Pago</Label>
                  <Input
                    id="paymentDescription"
                    data-testid="payment-description-input"
                    placeholder="Ej: Pago quincenal"
                    value={paymentDescription}
                    onChange={(e) => setPaymentDescription(e.target.value)}
                    disabled={paymentLoading}
                  />
                </div>
                {paymentNegativeConfirm && (
                  <div
                    data-testid="payment-negative-warning"
                    className="flex items-start gap-2 p-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm"
                  >
                    <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>
                      Este pago dejará el saldo del cliente en <b>NEGATIVO</b>:{' '}
                      <b>${(targetCustomerForPayment ? targetCustomerForPayment.balance - (parseFloat(paymentAmount) || 0) : 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</b>. ¿Aprobar de todas formas?
                    </span>
                  </div>
                )}
                <Button 
                  data-testid="submit-payment-btn" 
                  onClick={registerPayment} 
                  disabled={paymentLoading || !paymentAmount || !paymentDescription}
                  className="w-full"
                  style={{ background: paymentNegativeConfirm ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                >
                  {paymentLoading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Procesando pago...
                    </span>
                  ) : paymentNegativeConfirm ? (
                    `Aprobar pago en negativo`
                  ) : (
                    `Registrar Pago`
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Dialog de Configuración de Cliente */}
          <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Configurar Cuenta Recaudadora</DialogTitle>
                <DialogDescription>
                  Define el CBU de tu cuenta recaudadora para validar que los uploads del cliente sean a la cuenta correcta.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="configDestAccount">CBU de Cuenta Recaudadora</Label>
                  <Input
                    id="configDestAccount"
                    data-testid="config-dest-account-input"
                    placeholder="Ej: 0170095720000037721378"
                    value={configDestinationAccount}
                    onChange={(e) => setConfigDestinationAccount(e.target.value)}
                    disabled={configLoading}
                  />
                  <p className="text-xs text-gray-500 mt-1">Los clientes solo podrán subir comprobantes que vayan a este CBU</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    ℹ️ <strong>Cómo funciona:</strong> El titular del comprobante puede ser cualquiera (diferentes personas que pagan), pero la cuenta destino DEBE coincidir con el CBU configurado aquí (tu cuenta recaudadora).
                  </p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-800">
                    ⚠️ <strong>Validación:</strong> Si configuras el CBU, los uploads con cuenta destino incorrecta serán bloqueados automáticamente.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    data-testid="submit-config-btn" 
                    onClick={saveClientConfig} 
                    disabled={configLoading}
                    className="flex-1"
                    style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}
                  >
                    {configLoading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Guardando...
                      </span>
                    ) : (
                      'Guardar Configuración'
                    )}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setConfigDestinationAccount('');
                    }}
                    disabled={configLoading}
                    title="Limpiar campo para desactivar validación"
                  >
                    Limpiar
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>


          {/* Dialog de Confirmación de Regeneración de PIN */}
          <Dialog open={showPINConfirmDialog} onOpenChange={setShowPINConfirmDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                  Confirmar Regeneración de PIN
                </DialogTitle>
                <DialogDescription>
                  Verifica la información del cliente antes de regenerar el PIN
                </DialogDescription>
              </DialogHeader>
              
              {customerForPINRegen && (
                <div className="space-y-4">
                  {/* Información del Cliente */}
                  <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                    <h4 className="font-semibold text-sm text-slate-900 dark:text-slate-100 mb-3">
                      Información del Cliente
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600 dark:text-slate-400">Nombre:</span>
                        <span className="font-medium text-slate-900 dark:text-slate-100">{customerForPINRegen.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600 dark:text-slate-400">Cuenta:</span>
                        <span className="font-medium text-slate-900 dark:text-slate-100">{customerForPINRegen.group_name}</span>
                      </div>
                    </div>
                  </div>

                  {/* Estado de Sesión */}
                  {customerForPINRegen.has_active_session ? (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm text-amber-900 dark:text-amber-100 mb-2">
                            ⚠️ Sesión Activa Detectada
                          </h4>
                          <div className="space-y-1 text-xs text-amber-800 dark:text-amber-200">
                            {customerForPINRegen.last_access_ip && (
                              <p>• Última IP: <strong>{customerForPINRegen.last_access_ip}</strong></p>
                            )}
                            {customerForPINRegen.last_access_date && (
                              <p>• Último acceso: <strong>{new Date(customerForPINRegen.last_access_date).toLocaleString('es-ES')}</strong></p>
                            )}
                            <p className="mt-2 font-semibold">
                              Al regenerar el PIN, se cerrará la sesión activa del cliente.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-green-600 dark:text-green-500" />
                        <p className="text-sm text-green-800 dark:text-green-200 font-medium">
                          ✓ Sin sesiones activas
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Advertencia de Seguridad */}
                  <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3">
                    <p className="text-xs text-slate-700 dark:text-slate-300">
                      <strong>Nota:</strong> El PIN anterior quedará invalidado inmediatamente. El cliente deberá usar el nuevo PIN para acceder a su portal.
                    </p>
                  </div>

                  {/* Botones de Acción */}
                  <div className="flex gap-3">
                    <Button
                      onClick={() => {
                        setShowPINConfirmDialog(false);
                        setCustomerForPINRegen(null);
                      }}
                      variant="outline"
                      className="flex-1"
                      disabled={pinRegenLoading}
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={confirmRegeneratePIN}
                      className="flex-1 bg-amber-600 hover:bg-amber-700"
                      disabled={pinRegenLoading}
                    >
                      {pinRegenLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Regenerando...
                        </>
                      ) : (
                        <>
                          <Key className="w-4 h-4 mr-2" />
                          Confirmar y Regenerar PIN
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Dialog de PIN Regenerado */}
          <Dialog open={showPINDialog} onOpenChange={setShowPINDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5 text-yellow-600" />
                  PIN Regenerado
                </DialogTitle>
                <DialogDescription>
                  Comparte este nuevo PIN con el cliente. No se mostrará de nuevo.
                </DialogDescription>
              </DialogHeader>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                <p className="text-sm font-semibold text-yellow-900 mb-2">Nuevo PIN:</p>
                <p className="text-4xl font-mono font-bold text-yellow-700 tracking-widest">{regeneratedPIN}</p>
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(regeneratedPIN);
                    toast.success('PIN copiado al portapapeles');
                  }}
                  variant="outline"
                  className="mt-4"
                  size="sm"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar PIN
                </Button>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-800">
                  ⚠️ <strong>Importante:</strong> El PIN anterior ya no será válido. El cliente deberá usar este nuevo PIN para acceder.
                </p>
              </div>
            </DialogContent>
          </Dialog>

          {/* Dialog de Logs de Acceso */}
          <Dialog open={showAccessLogsDialog} onOpenChange={setShowAccessLogsDialog}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-cyan-600" />
                  Logs de Acceso - {selectedClientForLogs?.name}
                </DialogTitle>
                <DialogDescription>
                  Historial completo de accesos y eventos de seguridad
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {accessLogs.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">No hay logs de acceso registrados</p>
                ) : (
                  <div className="space-y-2">
                    {accessLogs.map((log, index) => (
                      <div key={index} className="bg-gray-50 border rounded-lg p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge 
                                className={
                                  log.action === 'pin_verified' ? 'bg-green-100 text-green-800' :
                                  log.action === 'pin_failed' ? 'bg-red-100 text-red-800' :
                                  log.action === 'pin_blocked' ? 'bg-amber-100 text-amber-800' :
                                  'bg-gray-100 text-gray-800'
                                }
                              >
                                {log.action === 'pin_verified' ? '✓ Acceso exitoso' :
                                 log.action === 'pin_failed' ? '✗ PIN incorrecto' :
                                 log.action === 'pin_blocked' ? '⚠ Bloqueado' :
                                 log.action === 'session_closed_by_admin' ? '🔒 Sesión cerrada por admin' :
                                 log.action}
                              </Badge>
                              {log.new_ip_detected && (
                                <Badge className="bg-orange-100 text-orange-800">
                                  🌍 Nueva IP
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-700">{log.details || 'Sin detalles'}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <span>🌐 IP: {log.ip_address}</span>
                              <span>🕐 {new Date(log.timestamp).toLocaleString('es-AR')}</span>
                            </div>
                            {log.user_agent && (
                              <p className="text-xs text-gray-400 mt-1 truncate">
                                📱 {log.user_agent}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>


          {/* Render Cuentas - Vista Condicional */}
          {viewMode === 'list' ? (
            /* Vista Simple - Layout 50/50 con Estadísticas */
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Columna Izquierda: Tabla */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col max-h-[800px]">
                <table className="w-full">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                      <th className="text-left py-3 px-4 font-bold text-sm uppercase tracking-wide w-7/12">Clubes</th>
                      <th className="text-right py-3 px-4 font-bold text-sm uppercase tracking-wide w-5/12">
                        <div className="flex items-center justify-end gap-2">
                          <span>Total</span>
                          <select
                            value={sortOrder}
                            onChange={(e) => {
                              setSortOrder(e.target.value);
                              localStorage.setItem('dashboard_sort_order', e.target.value);
                            }}
                            className="bg-white/20 hover:bg-white/30 text-white text-xs px-2 py-1 rounded border border-white/30 cursor-pointer focus:outline-none focus:ring-2 focus:ring-white/50"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="default" className="text-gray-900">Por defecto</option>
                            <option value="most_debtor" className="text-gray-900">📉 Más deudor</option>
                            <option value="most_positive" className="text-gray-900">📈 Más positivo</option>
                            <option value="alpha_asc" className="text-gray-900">🔤 A → Z</option>
                            <option value="alpha_desc" className="text-gray-900">🔤 Z → A</option>
                          </select>
                        </div>
                      </th>
                    </tr>
                  </thead>
                </table>
                <div className="overflow-y-auto flex-1">
                  <table className="w-full">
                    <tbody>
                      {sortedAccounts.map((account, index) => {
                        const balance = account.balance || 0;
                        const isPositive = balance > 0;
                        const isZero = balance === 0;
                        const bgColor = isZero ? 'bg-gray-50' : isPositive ? 'bg-green-50' : 'bg-red-50';
                        const hoverColor = isZero ? 'hover:bg-gray-100' : isPositive ? 'hover:bg-green-100' : 'hover:bg-red-100';
                        
                        return (
                          <tr 
                            key={account.id}
                            onClick={() => setSelectedCliente(account.id)}
                            className={`${bgColor} ${hoverColor} cursor-pointer transition-colors border-b border-gray-200 ${
                              selectedCliente === account.id ? 'ring-2 ring-inset ring-blue-500' : ''
                            }`}
                          >
                            <td className="py-3 px-4 w-7/12">
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-1 h-8 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: account.color }}
                                />
                                <span className="font-medium text-gray-900 truncate">{account.name}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-right w-5/12">
                              <span className={`font-bold text-base ${
                                isZero ? 'text-gray-600' : isPositive ? 'text-green-700' : 'text-red-700'
                              }`}>
                                {formatAmount(balance)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Columna Derecha: Estadísticas */}
              <div className="space-y-4">
                {(() => {
                  // Calcular estadísticas
                  const positivos = sortedAccounts.filter(acc => acc.balance > 0);
                  const deudores = sortedAccounts.filter(acc => acc.balance < 0);
                  const enCero = sortedAccounts.filter(acc => acc.balance === 0);
                  
                  const totalPositivos = positivos.reduce((sum, acc) => sum + acc.balance, 0);
                  const totalDeudores = deudores.reduce((sum, acc) => sum + acc.balance, 0);
                  const balanceNeto = totalPositivos + totalDeudores;
                  
                  const percentPositivos = sortedAccounts.length > 0 
                    ? (positivos.length / sortedAccounts.length) * 100 
                    : 0;
                  const percentDeudores = sortedAccounts.length > 0 
                    ? (deudores.length / sortedAccounts.length) * 100 
                    : 0;
                  
                  return (
                    <>
                      {/* Card Financiera */}
                      <Card 
                        className="border-2 border-blue-200 bg-blue-50 cursor-pointer hover:shadow-lg transition-shadow"
                        onClick={() => {
                          if (!financieraSummary && !financieraLoading) {
                            setShowInitialBalanceModal(true);
                          } else {
                            fetchFinancieraMovements();
                            setShowFinancieraModal(true);
                          }
                        }}
                      >
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                            <Building2 className="w-5 h-5" />
                            Cuenta Financiera
                            <div className="ml-auto">
                              <ExternalLink className="w-4 h-4 text-blue-600" />
                            </div>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {financieraLoading ? (
                            <div className="flex items-center gap-2 text-gray-600">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <span className="text-sm">Cargando...</span>
                            </div>
                          ) : financieraSummary ? (
                            <>
                              <div className="text-sm text-gray-600 mb-1">💰 Disponible Actual:</div>
                              <div className={`text-2xl font-bold ${
                                financieraSummary.available >= 0 ? 'text-green-700' : 'text-red-700'
                              }`}>
                                {formatAmount(financieraSummary.available)}
                              </div>
                              <div className="mt-3 space-y-1 text-xs text-gray-600">
                                <div className="flex justify-between">
                                  <span>📥 Ingresos:</span>
                                  <span className="font-semibold">{formatAmount(financieraSummary.total_income)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>📉 Comisiones:</span>
                                  <span className="font-semibold text-red-600">-{formatAmount(financieraSummary.total_commissions)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>📤 Pagos:</span>
                                  <span className="font-semibold text-red-600">-{formatAmount(financieraSummary.total_payments)}</span>
                                </div>
                              </div>
                              <div className="mt-2 text-xs text-blue-600 font-medium">
                                📊 Click para ver detalle completo →
                              </div>
                            </>
                          ) : (
                            <div className="text-center py-2">
                              <div className="text-sm text-gray-600 mb-2">
                                Configura el saldo inicial
                              </div>
                              <div className="text-xs text-blue-600 font-medium">
                                Click para configurar →
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Balance General */}
                      <Card className={`border-2 ${balanceNeto >= 0 ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                            <Wallet className="w-5 h-5" />
                            Balance General Total
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className={`text-3xl font-bold ${balanceNeto >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {formatAmount(balanceNeto)}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{sortedAccounts.length} clientes totales</p>
                        </CardContent>
                      </Card>

                      {/* Clientes Positivos */}
                      <Card className="border-2 border-green-200 bg-green-50">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100">
                              📈
                            </div>
                            Clientes Positivos
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-3xl font-bold text-green-700">
                            {formatAmount(totalPositivos)}
                          </div>
                          <p className="text-sm text-gray-600 mt-2">
                            {positivos.length} cliente{positivos.length !== 1 ? 's' : ''} ({percentPositivos.toFixed(1)}%)
                          </p>
                        </CardContent>
                      </Card>

                      {/* Clientes Deudores */}
                      <Card className="border-2 border-red-200 bg-red-50">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100">
                              📉
                            </div>
                            Clientes Deudores
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-3xl font-bold text-red-700">
                            {formatAmount(totalDeudores)}
                          </div>
                          <p className="text-sm text-gray-600 mt-2">
                            {deudores.length} cliente{deudores.length !== 1 ? 's' : ''} ({percentDeudores.toFixed(1)}%)
                          </p>
                        </CardContent>
                      </Card>

                      {/* Clientes en Cero */}
                      {enCero.length > 0 && (
                        <Card className="border-2 border-gray-200 bg-gray-50">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100">
                                ⚖️
                              </div>
                              Sin Movimientos
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="text-3xl font-bold text-gray-600">
                              {enCero.length}
                            </div>
                            <p className="text-sm text-gray-600 mt-2">
                              Cliente{enCero.length !== 1 ? 's' : ''} en $0
                            </p>
                          </CardContent>
                        </Card>
                      )}

                      {/* Gráfico Visual */}
                      <Card className="border-2 border-gray-200">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                            <Activity className="w-5 h-5" />
                            Distribución
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {/* Barra de progreso */}
                            <div className="w-full bg-gray-200 rounded-full h-8 flex overflow-hidden">
                              {percentPositivos > 0 && (
                                <div 
                                  className="bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center text-white text-xs font-semibold"
                                  style={{ width: `${percentPositivos}%` }}
                                >
                                  {percentPositivos > 15 && `${percentPositivos.toFixed(0)}%`}
                                </div>
                              )}
                              {percentDeudores > 0 && (
                                <div 
                                  className="bg-gradient-to-r from-red-500 to-red-600 flex items-center justify-center text-white text-xs font-semibold"
                                  style={{ width: `${percentDeudores}%` }}
                                >
                                  {percentDeudores > 15 && `${percentDeudores.toFixed(0)}%`}
                                </div>
                              )}
                            </div>
                            
                            {/* Leyenda */}
                            <div className="flex justify-between text-xs text-gray-600">
                              <div className="flex items-center gap-1">
                                <div className="w-3 h-3 bg-green-500 rounded"></div>
                                <span>Positivos</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <div className="w-3 h-3 bg-red-500 rounded"></div>
                                <span>Deudores</span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </>
                  );
                })()}
              </div>
            </div>
          ) : (
            /* Vista de Tarjetas - Original */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {accounts.map((account) => {
              const auditoria = auditoriaByCliente[account.id];
              const yaAuditado = !!auditoria?.auditado;
              return (
              <Card
                key={account.id}
                data-testid={`account-card-${account.id}`}
                className={`hover:shadow-lg transition-all duration-200 cursor-pointer ${
                  selectedCliente === account.id ? 'ring-2 ring-offset-2 shadow-xl bg-gradient-to-r from-indigo-50 to-purple-50' : (yaAuditado ? 'bg-emerald-50/70 ring-1 ring-emerald-200' : '')
                }`}
                style={{ 
                  borderLeft: `4px solid ${account.color}`,
                  ...(selectedCliente === account.id ? { ringColor: account.color } : {})
                }}
                onClick={() => setSelectedCliente(account.id)}
              >
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-lg font-bold">
                      {account.name}
                    </CardTitle>
                    <div className="flex flex-wrap gap-1">
                      {yaAuditado && (
                        <span
                          data-testid={`audited-badge-${account.id}`}
                          className="h-8 px-2 flex items-center gap-1 rounded-md bg-emerald-100 text-emerald-700 border border-emerald-300 text-xs font-semibold"
                          title={`Auditado el ${new Date((auditoria.ultimo_corte || '') + (/Z|[+-]\d{2}:?\d{2}$/.test(auditoria.ultimo_corte || '') ? '' : 'Z')).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'America/Argentina/Buenos_Aires', hour12: false })}${auditoria.origen === 'drive_tudemune' ? ' — ❤ coincide con el Drive de TUDEMUNE' : (auditoria.silencioso ? ' (en silencio)' : '')}`}
                        >
                          {auditoria.origen === 'drive_tudemune'
                            ? <Heart className="w-3.5 h-3.5 fill-emerald-600" />
                            : (auditoria.silencioso ? <EyeOff className="w-3.5 h-3.5" /> : <CheckCircle className="w-3.5 h-3.5" />)}
                          Auditado
                        </span>
                      )}
                      {/* Badge de Comprobantes Perdidos (Gaps) */}
                      {(gapsByClient[account.id]?.count || 0) > 0 && (
                        <Button
                          data-testid={`gaps-button-${account.id}`}
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 flex items-center gap-1 bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-300 rounded-md animate-pulse"
                          onClick={(e) => {
                            e.stopPropagation();
                            setGapsModalCliente(account);
                            loadGapsForCliente(account.id);
                          }}
                          title={`${gapsByClient[account.id].count} comprobante(s) perdido(s) - click para revisar`}
                        >
                          <AlertTriangle className="w-4 h-4" />
                          <span className="text-xs font-bold">{gapsByClient[account.id].count}</span>
                        </Button>
                      )}
                      {/* Dropdown para Estado de Cuenta PDF */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            data-testid={`view-statement-${account.id}`}
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 flex items-center justify-center hover:bg-purple-50 hover:text-purple-600 rounded-md"
                            onClick={(e) => e.stopPropagation()}
                            title="Estado de cuenta PDF"
                          >
                            {enviandoEstadoCuenta[account.id] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <FileDown className="w-4 h-4" />
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenuItem
                            onClick={async () => {
                              const customer = finalCustomers[account.id];
                              if (customer && customer.access_token) {
                                try {
                                  toast.info('Generando reporte PDF...');
                                  const reportUrl = `${API}/statement/${customer.access_token}?t=${Date.now()}`;
                                  const response = await fetch(reportUrl, {
                                    method: 'GET',
                                    headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
                                  });
                                  if (!response.ok) throw new Error('Error al generar el reporte');
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = `estado_cuenta_${customer.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
                                  document.body.appendChild(link);
                                  link.click();
                                  setTimeout(() => {
                                    document.body.removeChild(link);
                                    window.URL.revokeObjectURL(url);
                                  }, 100);
                                  toast.success('Reporte PDF descargado');
                                } catch (error) {
                                  toast.error('Error al descargar el reporte');
                                }
                              } else {
                                toast.error('No se pudo generar el reporte');
                              }
                            }}
                            className="cursor-pointer"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Descargar PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => enviarEstadoCuentaWhatsApp(account.id, account.name)}
                            disabled={enviandoEstadoCuenta[account.id]}
                            className="cursor-pointer"
                          >
                            <MessageCircle className="w-4 h-4 mr-2 text-green-600" />
                            Enviar por WhatsApp
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => enviarEstadoCuentaTexto(account.id, account.name)}
                            disabled={enviandoEstadoCuenta[account.id]}
                            className="cursor-pointer"
                          >
                            <FileText className="w-4 h-4 mr-2 text-blue-600" />
                            Enviar solo texto
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => cortarEnSilencio(account.id, account.name)}
                            disabled={enviandoEstadoCuenta[account.id] || !canWrite}
                            className="cursor-pointer"
                            data-testid={`silent-cut-${account.id}`}
                          >
                            <EyeOff className="w-4 h-4 mr-2 text-emerald-600" />
                            Cortar en silencio
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleEnviarPppoker(account.id, account.name)}
                            disabled={enviandoEstadoCuenta[account.id]}
                            className="cursor-pointer"
                          >
                            <BarChart3 className="w-4 h-4 mr-2 text-violet-600" />
                            Texto + datos PPPoker
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => editPppokerConfig(account.id, account.name)}
                            className="cursor-pointer"
                          >
                            <Settings className="w-4 h-4 mr-2 text-gray-500" />
                            Config PPPoker
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      {canWrite && (
                        <>
                          {/* Icono de Agregar Movimiento Manual */}
                          <Button
                            data-testid={`add-manual-entry-${account.id}`}
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 flex items-center justify-center hover:bg-green-50 hover:text-green-600 rounded-md"
                            onClick={(e) => {
                              e.stopPropagation();
                              openManualEntryDialog(account.id);
                            }}
                            title="Agregar movimiento manual"
                          >
                            <PlusCircle className="w-4 h-4" />
                          </Button>
                          {/* Exportar a Excel */}
                          <Button
                            data-testid={`export-account-${account.id}`}
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 flex items-center justify-center hover:bg-emerald-50 hover:text-emerald-600 rounded-md"
                            onClick={(e) => {
                              e.stopPropagation();
                              exportAccountToExcel(account.id, account.name);
                            }}
                            title="Exportar a Excel"
                          >
                            <FileSpreadsheet className="w-4 h-4" />
                          </Button>
                          {/* Resetear Balance */}
                          <Button
                            data-testid={`reset-account-${account.id}`}
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 flex items-center justify-center hover:bg-blue-50 hover:text-blue-600 rounded-md"
                            onClick={(e) => {
                              e.stopPropagation();
                              resetGroupBalance(account.id);
                            }}
                            title="Resetear balance a $0"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                          {/* Configurar Telegram */}
                          <Button
                            data-testid={`telegram-config-${account.id}`}
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 flex items-center justify-center hover:bg-cyan-50 hover:text-cyan-600 rounded-md"
                            onClick={(e) => {
                              e.stopPropagation();
                              const cliente = finalCustomers[account.id];
                              if (cliente) setChannelClient({ ...cliente, id: account.id });
                            }}
                            title="Canales del cliente: Telegram y WhatsApp"
                          >
                            <svg 
                              xmlns="http://www.w3.org/2000/svg" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              className="w-4 h-4"
                            >
                              <path d="M21.5 2L2 10.5L11 14L14.5 23L23 3.5L21.5 2Z"/>
                              <path d="M11 14L23 2"/>
                            </svg>
                          </Button>
                          
                          <ClientCommandsControl client={{ id: account.id, name: account.name }} readOnly={isReadOnly} />
                          <Button
                            data-testid={`whatsapp-config-${account.id}`}
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 flex items-center justify-center hover:bg-green-50 hover:text-green-600 rounded-md"
                            onClick={(e) => {
                              e.stopPropagation();
                              const cliente = finalCustomers[account.id];
                              if (cliente) setChannelClient({ ...cliente, id: account.id });
                            }}
                            title="Canales del cliente: WhatsApp y Telegram"
                          >
                            <svg 
                              xmlns="http://www.w3.org/2000/svg" 
                              viewBox="0 0 24 24" 
                              fill="currentColor"
                              className="w-4 h-4"
                            >
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                            </svg>
                          </Button>

                          {/* Botón Enviar Recordatorio WhatsApp */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className={"h-8 w-8 p-0 flex items-center justify-center rounded-md " + (account.balance < 0 ? "hover:bg-red-50 hover:text-red-600" : "hover:bg-green-50 hover:text-green-600")}
                            onClick={(e) => {
                              e.stopPropagation();
                              enviarRecordatorioWhatsApp(account.id, account.name);
                            }}
                            disabled={enviandoRecordatorio[account.id] || account.balance === 0}
                            title={account.balance < 0 ? "Enviar recordatorio de pago (WhatsApp + Telegram)" : account.balance > 0 ? "Solicitar datos bancarios (WhatsApp + Telegram)" : "Sin saldo pendiente"}
                          >
                            {enviandoRecordatorio[account.id] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Bell className={"w-4 h-4 " + (account.balance < 0 ? "text-red-500" : "text-green-500")} />
                            )}
                          </Button>

                          {/* Boton Enviar Flyers de la Jornada */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 flex items-center justify-center rounded-md hover:bg-orange-50 hover:text-orange-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              openFlyerCronDialog(account.id, account.name);
                            }}
                            disabled={enviandoFlyers[account.id]}
                            title="Enviar flyers de la jornada al grupo"
                          >
                            {enviandoFlyers[account.id] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Megaphone className="w-4 h-4 text-orange-500" />
                            )}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold" style={{ 
                      color: account.balance < 0 ? '#dc2626' : account.color 
                    }}>
                      {formatAmount(account.balance)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Balance Grupo</p>
                  
                  {/* Saldo del Cliente Final (doble verificación) */}
                  {finalCustomers[account.id] && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <div className="flex items-baseline gap-2">
                        <User className="w-3 h-3 text-gray-400" />
                        <span className="text-lg font-semibold text-indigo-600">
                          {formatAmount(finalCustomers[account.id].balance)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">Balance Cliente (portal)</p>
                      {Math.abs(account.balance - finalCustomers[account.id].balance) > 0.01 && (
                        <div className="mt-2 flex items-center gap-2">
                          <p className="text-xs text-red-600 font-semibold animate-pulse flex-1">
                            ⚠️ Desincronizado
                          </p>
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              auditCustomerBalance(finalCustomers[account.id].id, account.id);
                            }}
                            className="h-6 px-2 text-xs bg-amber-500 hover:bg-amber-600 flex items-center gap-1"
                            title="Auditar y corregir automáticamente"
                          >
                            <CheckCircle className="w-3 h-3" />
                            Auditar
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Sistema de Créditos */}
                  {finalCustomers[account.id] && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-purple-600" />
                          <span className="text-sm font-semibold text-gray-700">Sistema de Créditos</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 relative"
                          onClick={async (e) => {
                            e.stopPropagation();
                            await openCreditRequestsDialog(finalCustomers[account.id]);
                          }}
                          title="Ver solicitudes de crédito"
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            viewBox="0 0 24 24" 
                            fill="currentColor" 
                            className="w-5 h-5 text-blue-500"
                          >
                            <path d="M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 2c-4.418 0-8 3.582-8 8s3.582 8 8 8 8-3.582 8-8-3.582-8-8-8zm-1 3h2v1c1.1 0 2 .9 2 2 0 .738-.404 1.376-1 1.723V14h-2v-2.277c-.596-.347-1-.985-1-1.723 0-1.1.9-2 2-2V7h-2zm1 4c-.552 0-1 .448-1 1s.448 1 1 1 1-.448 1-1-.448-1-1-1zm-3 5h6v2H9v-2z" />
                          </svg>
                          {/* Badge de notificaciones pendientes */}
                          {creditRequests.filter(r => r.cliente_id === finalCustomers[account.id].id && r.status === 'pending').length > 0 && (
                            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                              {creditRequests.filter(r => r.cliente_id === finalCustomers[account.id].id && r.status === 'pending').length}
                            </span>
                          )}
                        </Button>
                      </div>
                      
                      {/* Descubierto */}
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Descubierto:</span>
                          {editingDescubierto === finalCustomers[account.id].id ? (
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                value={newDescubierto}
                                onChange={(e) => setNewDescubierto(e.target.value)}
                                className="h-6 w-24 text-xs"
                                placeholder="0"
                                onClick={(e) => e.stopPropagation()}
                              />
                              <Button
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateDescubierto(finalCustomers[account.id].id, newDescubierto);
                                }}
                                className="h-6 px-2 text-xs"
                                disabled={updatingDescubierto}
                              >
                                {updatingDescubierto ? <Loader2 className="w-3 h-3 animate-spin" /> : '✓'}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingDescubierto(null);
                                  setNewDescubierto('');
                                }}
                                className="h-6 px-2 text-xs"
                              >
                                ✕
                              </Button>
                            </div>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingDescubierto(finalCustomers[account.id].id);
                                setNewDescubierto(finalCustomers[account.id].descubierto || 0);
                              }}
                              className="text-sm font-medium text-purple-600 hover:text-purple-800 flex items-center gap-1"
                            >
                              ${(finalCustomers[account.id].descubierto || 0).toLocaleString()}
                              <Edit className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                        
                        {/* Crédito Disponible */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Crédito disponible:</span>
                          <span className="text-sm font-semibold text-green-600">
                            ${Math.max(0, (finalCustomers[account.id].descubierto || 0) + finalCustomers[account.id].balance).toLocaleString()}
                          </span>
                        </div>

                        {/* Habilita que el cliente solicite retiros automáticos por WhatsApp con /pago */}
                        {/* Auto-aprobación hasta $60.000; arriba de eso queda pendiente para botón del operador */}
                        <div
                          className="flex items-center justify-between pt-2 mt-1 border-t border-gray-100"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`retiro-self-service-row-${finalCustomers[account.id].id}`}
                        >
                          <div className="flex flex-col">
                            <span className="text-xs text-gray-600 font-medium flex items-center gap-1">
                              <Send className="w-3 h-3 text-emerald-600" />
                              Retiro automático (WhatsApp + Telegram)
                            </span>
                            <span className="text-[10px] text-gray-400">
                              Cliente pide con /pago · en Telegram exige PIN
                            </span>
                          </div>
                          {togglingRetiroSelfService === finalCustomers[account.id].id ? (
                            <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                          ) : (
                            <Switch
                              checked={!!finalCustomers[account.id].retiro_self_service_enabled}
                              onCheckedChange={() =>
                                toggleRetiroSelfService(
                                  finalCustomers[account.id].id,
                                  !!finalCustomers[account.id].retiro_self_service_enabled
                                )
                              }
                              disabled={isReadOnly}
                              data-testid={`retiro-self-service-toggle-${finalCustomers[account.id].id}`}
                              aria-label="Activar retiros automáticos por WhatsApp"
                            />
                          )}
                        </div>

                        {/* === Límite de auto-aprobación (visible solo si el toggle está ON) === */}
                        {finalCustomers[account.id].retiro_self_service_enabled && (
                          <div
                            className="flex items-center justify-between pt-1"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`retiro-auto-limit-row-${finalCustomers[account.id].id}`}
                          >
                            <span className="text-[11px] text-gray-500 flex items-center gap-1">
                              <span className="w-3 h-3 inline-block" />
                              Tope auto-aprobación:
                            </span>
                            {editingRetiroLimit === finalCustomers[account.id].id ? (
                              <div className="flex items-center gap-1">
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={newRetiroLimit}
                                  onChange={(e) => setNewRetiroLimit(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveRetiroAutoLimit(finalCustomers[account.id].id);
                                    if (e.key === 'Escape') { setEditingRetiroLimit(null); setNewRetiroLimit(''); }
                                  }}
                                  className="w-24 px-2 py-0.5 text-xs border border-emerald-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                  placeholder="60000"
                                  autoFocus
                                  data-testid={`retiro-auto-limit-input-${finalCustomers[account.id].id}`}
                                />
                                {savingRetiroLimit ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600" />
                                ) : (
                                  <>
                                    <button
                                      onClick={() => saveRetiroAutoLimit(finalCustomers[account.id].id)}
                                      className="text-emerald-600 hover:text-emerald-700"
                                      title="Guardar"
                                      data-testid={`retiro-auto-limit-save-${finalCustomers[account.id].id}`}
                                    >
                                      <Check className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => { setEditingRetiroLimit(null); setNewRetiroLimit(''); }}
                                      className="text-gray-400 hover:text-gray-600"
                                      title="Cancelar"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  </>
                                )}
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  setEditingRetiroLimit(finalCustomers[account.id].id);
                                  setNewRetiroLimit(String(finalCustomers[account.id].retiro_auto_limit || 60000));
                                }}
                                disabled={isReadOnly}
                                className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Click para editar el tope"
                                data-testid={`retiro-auto-limit-display-${finalCustomers[account.id].id}`}
                              >
                                ${(finalCustomers[account.id].retiro_auto_limit ?? 60000).toLocaleString('es-AR')}
                              </button>
                            )}
                          </div>
                        )}

                        {/* === FASE 3 (2026-02): Tope diario + barra de progreso + reset === */}
                        {finalCustomers[account.id].retiro_self_service_enabled && (() => {
                          const c = finalCustomers[account.id];
                          const dailyLimit = c.retiro_daily_limit ?? 400000;
                          const dailyUsed = c.retiro_daily_used ?? 0;
                          const pct = dailyLimit > 0 ? Math.min(100, (dailyUsed / dailyLimit) * 100) : 0;
                          const barColor = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-orange-500' : 'bg-emerald-500';
                          const lastAutoAt = c.retiro_last_auto_at;
                          return (
                            <div
                              className="pt-1 space-y-1"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`retiro-daily-row-${c.id}`}
                            >
                              {/* Fila: label + editor del tope */}
                              <div className="flex items-center justify-between">
                                <span className="text-[11px] text-gray-500 flex items-center gap-1">
                                  <span className="w-3 h-3 inline-block" />
                                  Tope diario:
                                </span>
                                {editingRetiroDailyLimit === c.id ? (
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      value={newRetiroDailyLimit}
                                      onChange={(e) => setNewRetiroDailyLimit(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') saveRetiroDailyLimit(c.id);
                                        if (e.key === 'Escape') { setEditingRetiroDailyLimit(null); setNewRetiroDailyLimit(''); }
                                      }}
                                      className="w-28 px-2 py-0.5 text-xs border border-emerald-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                      placeholder="400000"
                                      autoFocus
                                      data-testid={`retiro-daily-limit-input-${c.id}`}
                                    />
                                    {savingRetiroDailyLimit ? (
                                      <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600" />
                                    ) : (
                                      <>
                                        <button
                                          onClick={() => saveRetiroDailyLimit(c.id)}
                                          className="text-emerald-600 hover:text-emerald-700"
                                          title="Guardar"
                                          data-testid={`retiro-daily-limit-save-${c.id}`}
                                        >
                                          <Check className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                          onClick={() => { setEditingRetiroDailyLimit(null); setNewRetiroDailyLimit(''); }}
                                          className="text-gray-400 hover:text-gray-600"
                                          title="Cancelar"
                                        >
                                          <X className="w-3.5 h-3.5" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => {
                                      setEditingRetiroDailyLimit(c.id);
                                      setNewRetiroDailyLimit(String(dailyLimit));
                                    }}
                                    disabled={isReadOnly}
                                    className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Click para editar el tope diario"
                                    data-testid={`retiro-daily-limit-display-${c.id}`}
                                  >
                                    ${dailyLimit.toLocaleString('es-AR')}
                                  </button>
                                )}
                              </div>

                              {/* Barra de progreso + reset */}
                              <div className="flex items-center gap-2">
                                <div
                                  className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden"
                                  title={`Gastado hoy: $${dailyUsed.toLocaleString('es-AR')} de $${dailyLimit.toLocaleString('es-AR')}`}
                                  data-testid={`retiro-daily-progress-${c.id}`}
                                >
                                  <div
                                    className={`h-full ${barColor} transition-all`}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                                <span className={`text-[10px] font-medium tabular-nums ${pct >= 100 ? 'text-red-600' : 'text-gray-500'}`}>
                                  ${dailyUsed.toLocaleString('es-AR')}
                                </span>
                                {dailyUsed > 0 && (
                                  resettingRetiroDaily === c.id ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600" />
                                  ) : (
                                    <button
                                      onClick={() => resetRetiroDaily(c.id, c.name)}
                                      disabled={isReadOnly}
                                      className="text-[10px] text-emerald-700 hover:text-emerald-900 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                                      title="Resetear contador diario (libera cooldown también)"
                                      data-testid={`retiro-daily-reset-${c.id}`}
                                    >
                                      ↻ reset
                                    </button>
                                  )
                                )}
                              </div>

                              {/* Aviso de último auto-pago (cooldown info) */}
                              {lastAutoAt && (
                                <div className="text-[10px] text-gray-400" data-testid={`retiro-last-auto-${c.id}`}>
                                  Último auto: {(() => {
                                    try {
                                      const d = new Date(lastAutoAt);
                                      return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
                                    } catch { return lastAutoAt; }
                                  })()}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* === Telegram (2026-06): cooldown + PIN que define el operador === */}
                        {finalCustomers[account.id].retiro_self_service_enabled && (() => {
                          const c = finalCustomers[account.id];
                          const cooldown = c.retiro_cooldown_minutes ?? 10;
                          const pinPuesto = !!c.retiro_pin_configurado;
                          const bloqueadoHasta = c.retiro_pin_bloqueado_hasta;
                          const bloqueado = bloqueadoHasta ? new Date(bloqueadoHasta) > new Date() : false;
                          return (
                            <div
                              className="pt-2 mt-1 border-t border-sky-200 bg-sky-50/40 -mx-1 px-1 pb-1 rounded space-y-1"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`retiro-telegram-config-${c.id}`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-[11px] font-medium text-sky-800 flex items-center gap-1">
                                  <MessageCircle className="w-3 h-3" />
                                  Telegram · seguridad
                                </span>
                                {pinPuesto ? (
                                  <span className="text-[10px] font-medium text-emerald-700" data-testid={`retiro-pin-status-${c.id}`}>
                                    🔐 PIN configurado
                                  </span>
                                ) : (
                                  <span className="text-[10px] font-medium text-amber-700" data-testid={`retiro-pin-status-${c.id}`}>
                                    ⚠️ sin PIN · Telegram bloqueado
                                  </span>
                                )}
                              </div>

                              {/* Cooldown editable (aplica a los dos canales) */}
                              <div className="flex items-center justify-between">
                                <span className="text-[11px] text-gray-500">Espera entre pagos:</span>
                                {editingRetiroCooldown === c.id ? (
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      value={newRetiroCooldown}
                                      onChange={(e) => setNewRetiroCooldown(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') saveRetiroCooldown(c.id);
                                        if (e.key === 'Escape') { setEditingRetiroCooldown(null); setNewRetiroCooldown(''); }
                                      }}
                                      className="w-16 px-2 py-0.5 text-xs border border-sky-300 rounded focus:outline-none focus:ring-1 focus:ring-sky-500"
                                      placeholder="10"
                                      autoFocus
                                      data-testid={`retiro-cooldown-input-${c.id}`}
                                    />
                                    <span className="text-[10px] text-gray-400">min</span>
                                    {savingRetiroCooldown ? (
                                      <Loader2 className="w-3.5 h-3.5 animate-spin text-sky-600" />
                                    ) : (
                                      <>
                                        <button
                                          onClick={() => saveRetiroCooldown(c.id)}
                                          className="text-sky-600 hover:text-sky-800"
                                          title="Guardar"
                                          data-testid={`retiro-cooldown-save-${c.id}`}
                                        >
                                          <Check className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                          onClick={() => { setEditingRetiroCooldown(null); setNewRetiroCooldown(''); }}
                                          className="text-gray-400 hover:text-gray-600"
                                          title="Cancelar"
                                        >
                                          <X className="w-3.5 h-3.5" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => { setEditingRetiroCooldown(c.id); setNewRetiroCooldown(String(cooldown)); }}
                                    disabled={isReadOnly}
                                    className="text-xs font-semibold text-sky-700 hover:text-sky-900 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Minutos que tiene que esperar entre pagos automáticos"
                                    data-testid={`retiro-cooldown-display-${c.id}`}
                                  >
                                    {cooldown} min
                                  </button>
                                )}
                              </div>

                              {/* PIN de 4 dígitos */}
                              <div className="flex items-center justify-between">
                                <span className="text-[11px] text-gray-500">PIN de 4 dígitos:</span>
                                {editingRetiroPin === c.id ? (
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="password"
                                      inputMode="numeric"
                                      maxLength={4}
                                      value={newRetiroPin}
                                      onChange={(e) => setNewRetiroPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') saveRetiroPin(c.id);
                                        if (e.key === 'Escape') { setEditingRetiroPin(null); setNewRetiroPin(''); }
                                      }}
                                      className="w-20 px-2 py-0.5 text-xs tracking-[0.4em] text-center border border-sky-300 rounded focus:outline-none focus:ring-1 focus:ring-sky-500"
                                      placeholder="••••"
                                      autoComplete="new-password"
                                      autoFocus
                                      data-testid={`retiro-pin-input-${c.id}`}
                                    />
                                    {savingRetiroPin ? (
                                      <Loader2 className="w-3.5 h-3.5 animate-spin text-sky-600" />
                                    ) : (
                                      <>
                                        <button
                                          onClick={() => saveRetiroPin(c.id)}
                                          className="text-sky-600 hover:text-sky-800"
                                          title="Guardar PIN"
                                          data-testid={`retiro-pin-save-${c.id}`}
                                        >
                                          <Check className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                          onClick={() => { setEditingRetiroPin(null); setNewRetiroPin(''); }}
                                          className="text-gray-400 hover:text-gray-600"
                                          title="Cancelar"
                                        >
                                          <X className="w-3.5 h-3.5" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => { setEditingRetiroPin(c.id); setNewRetiroPin(''); }}
                                      disabled={isReadOnly}
                                      className="text-xs font-semibold text-sky-700 hover:text-sky-900 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                                      title="El PIN no se puede ver, solo reemplazar"
                                      data-testid={`retiro-pin-edit-${c.id}`}
                                    >
                                      {pinPuesto ? 'Cambiar PIN' : 'Definir PIN'}
                                    </button>
                                    {pinPuesto && (
                                      <button
                                        onClick={() => clearRetiroPin(c.id, c.name)}
                                        disabled={isReadOnly}
                                        className="text-[10px] text-red-600 hover:text-red-800 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Borrar el PIN (bloquea Telegram)"
                                        data-testid={`retiro-pin-clear-${c.id}`}
                                      >
                                        borrar
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>

                              {bloqueado && (
                                <div
                                  className="flex items-center justify-between bg-red-50 border border-red-200 rounded px-2 py-1"
                                  data-testid={`retiro-pin-blocked-${c.id}`}
                                >
                                  <span className="text-[10px] text-red-700">
                                    🚫 PIN bloqueado por intentos fallidos
                                  </span>
                                  {unblockingRetiroPin === c.id ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-red-600" />
                                  ) : (
                                    <button
                                      onClick={() => unblockRetiroPin(c.id)}
                                      disabled={isReadOnly}
                                      className="text-[10px] font-semibold text-red-700 hover:text-red-900 hover:underline disabled:opacity-50"
                                      data-testid={`retiro-pin-unblock-${c.id}`}
                                    >
                                      🔓 desbloquear
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* === FASE 4 (2026-02): Retiros pendientes (>auto_limit, requieren aprobación) === */}
                        {/* Solo admin/financiera puede aprobar/rechazar. Se vencen automáticamente a las 6 hs. */}
                        {(userRole === 'admin' || userRole === 'financiera') && (pendientesByCliente[finalCustomers[account.id].id] || []).length > 0 && (
                          <div
                            className="pt-2 mt-1 border-t border-amber-200 bg-amber-50/40 -mx-1 px-1 pb-1 rounded"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`retiros-pendientes-section-${finalCustomers[account.id].id}`}
                          >
                            <div className="text-[11px] font-semibold text-amber-700 flex items-center gap-1 mb-1">
                              🔔 {(pendientesByCliente[finalCustomers[account.id].id] || []).length} {((pendientesByCliente[finalCustomers[account.id].id] || []).length === 1) ? 'retiro pendiente' : 'retiros pendientes'}
                              <span className="text-[9px] text-amber-500 font-normal ml-1">(vencen en 6hs)</span>
                            </div>
                            <div className="space-y-1.5">
                              {(pendientesByCliente[finalCustomers[account.id].id] || []).map(pend => {
                                const fechaStr = (() => {
                                  try { return new Date(pend.created_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
                                  catch { return pend.created_at; }
                                })();
                                return (
                                  <div
                                    key={pend.id}
                                    className="bg-white border border-amber-200 rounded p-1.5 text-[11px] relative"
                                    data-testid={`pendiente-${pend.id}`}
                                  >
                                    {/* FASE 4: X para cerrar sin notificar al cliente */}
                                    {dismissingPendiente === pend.id ? (
                                      <Loader2 className="absolute top-1 right-1 w-3 h-3 animate-spin text-gray-400" />
                                    ) : (
                                      <button
                                        onClick={() => dismissPendiente(pend.id, pend.cliente_id)}
                                        disabled={isReadOnly}
                                        className="absolute top-0.5 right-0.5 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Cerrar solicitud (no notifica al cliente)"
                                        data-testid={`pendiente-dismiss-${pend.id}`}
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    )}
                                    <div className="flex items-center justify-between mb-0.5 pr-4">
                                      <span className="font-bold text-amber-900">
                                        ${Number(pend.monto).toLocaleString('es-AR')}
                                        {pend.es_adelanto && (
                                          <span
                                            className="ml-1 px-1 py-px rounded bg-orange-100 text-orange-700 border border-orange-300 text-[9px] font-semibold align-middle"
                                            title="El pedido supera el saldo: si lo aprobás, el saldo queda negativo hasta el cierre"
                                            data-testid={`pendiente-adelanto-${pend.id}`}
                                          >
                                            ADELANTO
                                          </span>
                                        )}
                                      </span>
                                      <span className="text-gray-400 text-[9px]">{fechaStr}</span>
                                    </div>
                                    <div className="text-gray-600 truncate">
                                      → <span className="font-medium">{pend.titular_destino || '—'}</span>
                                    </div>
                                    <div className="text-gray-400 text-[10px] truncate">
                                      {pend.destino}
                                    </div>
                                    <div className="flex gap-1 mt-1">
                                      {approvingPendiente === pend.id ? (
                                        <span className="text-[10px] text-emerald-600 flex items-center gap-1">
                                          <Loader2 className="w-3 h-3 animate-spin" />Aprobando…
                                        </span>
                                      ) : (
                                        <>
                                          <button
                                            onClick={() => approvePendiente(pend.id, pend.cliente_id)}
                                            disabled={isReadOnly}
                                            className="flex-1 px-2 py-0.5 text-[10px] font-medium bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                                            data-testid={`pendiente-approve-${pend.id}`}
                                          >
                                            ✓ Aprobar
                                          </button>
                                          <button
                                            onClick={() => openRejectModal(pend)}
                                            disabled={isReadOnly}
                                            className="flex-1 px-2 py-0.5 text-[10px] font-medium bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                                            data-testid={`pendiente-reject-${pend.id}`}
                                          >
                                            ✗ Rechazar
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Sistema de WhatsApp - Removido para compatibilidad con preview */}
                  
                  {/* Estado de Conciliación - MODIFICADO 2025-11-03: Usar conteos del backend */}
                  {(() => {
                    // Usar conteos del backend directamente (no depender de receipts en memoria)
                    const matched = account.reconciliation_matched || 0;
                    const partial = account.reconciliation_partial || 0;
                    const notFound = account.reconciliation_not_found || 0;
                    const playwright = account.reconciliation_playwright || 0;
                    const workerRealtime = account.reconciliation_worker_realtime || 0;
                    
                    const totalOcr = matched + partial + notFound + playwright + workerRealtime;
                    
                    // Solo mostrar si hay comprobantes OCR
                    if (totalOcr === 0) return null;
                    
                    return (
                      <div className="flex gap-2 mt-3 flex-wrap">
                        {matched > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                            🟢 {matched}
                          </span>
                        )}
                        {partial > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">
                            🟡 {partial}
                          </span>
                        )}
                        {notFound > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                            🔴 {notFound}
                          </span>
                        )}
                        {playwright > 0 && (
                          <span data-testid={`reconciliation-playwright-count-${account.id}`} title="Conciliados por Playwright / extracto" className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                            🔵 {playwright}
                          </span>
                        )}
                        {workerRealtime > 0 && (
                          <span data-testid={`reconciliation-worker-count-${account.id}`} title="Conciliados por Worker V9 en tiempo real" className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-violet-100 text-violet-700 rounded-full border border-violet-200">
                            🟣 {workerRealtime}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  
                  {/* Botón de Subir Comprobante - Posición Inferior Derecha */}
                  {canWrite && (
                    <div className="mt-4 flex justify-end">
                      <Button
                        data-testid={`upload-receipt-${account.id}`}
                        variant="default"
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2 px-3 py-2 h-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCliente(account.id);
                          setShowUploadDialog(true);
                        }}
                        title="Subir comprobante"
                      >
                        <Upload className="w-4 h-4" />
                        <span className="text-sm">Subir Comprobante</span>
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
              );
            })}
            </div>
          )}

          {/* Dialog para REUBICAR cronologicamente un receipt recuperado via 🎯 */}
      <Dialog open={!!recoveryReloc} onOpenChange={(open) => !open && setRecoveryReloc(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-600" />
              Reubicar comprobante recuperado
            </DialogTitle>
            <DialogDescription>
              Elegi entre que movimientos ubicar el comprobante recuperado ({recoveryReloc?.receipt?.origin_holder || ''} · ${recoveryReloc?.receipt?.amount?.toLocaleString('es-AR')}). Se actualizara la fecha/hora automaticamente al punto medio.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto border rounded-md">
            {(!recoveryReloc?.neighbors || recoveryReloc.neighbors.length === 0) ? (
              <div className="p-6 text-center text-sm text-gray-500">
                No hay otros movimientos cerca de la fecha del recuperado (+/- 2 dias)
              </div>
            ) : (
              <div className="divide-y">
                {/* Boton: al principio (mas nuevo que todos) */}
                <button
                  type="button"
                  onClick={() => placeRecoveredBetween(null, recoveryReloc.neighbors[0])}
                  className="w-full px-4 py-2 text-sm text-left text-orange-700 bg-orange-50 hover:bg-orange-100 transition-colors"
                  data-testid="relocate-top"
                >
                  ⬆️ Ubicar AL PRINCIPIO (mas reciente que todos)
                </button>
                {recoveryReloc.neighbors.map((n, idx) => {
                  const next = recoveryReloc.neighbors[idx + 1];
                  // Mostramos date+time tal como estan en BD (hora AR real). No
                  // usamos created_at porque llega naive-UTC y el browser lo
                  // interpreta como local -> quedaria 3h adelantado.
                  const dateStr = n.date || '';
                  const timeStr = (n.time || '').slice(0, 5);
                  return (
                    <React.Fragment key={n.id}>
                      {/* Fila: vecino existente */}
                      <div className={`px-4 py-2 text-sm flex items-center justify-between ${n.recovered ? 'bg-orange-50' : ''}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-gray-500">{dateStr} {timeStr}</span>
                            {n.recovered && <span className="text-xs text-orange-600 font-semibold">RECUPERADO</span>}
                          </div>
                          <div className="truncate">
                            <span className="text-gray-700">{n.origin_holder || n.description || '—'}</span>
                            {n.amount != null && <span className="ml-2 font-semibold text-gray-900">${Math.abs(n.amount).toLocaleString('es-AR')}</span>}
                          </div>
                        </div>
                      </div>
                      {/* Boton entre este y el siguiente */}
                      {next && (
                        <button
                          type="button"
                          onClick={() => placeRecoveredBetween(n, next)}
                          className="w-full px-4 py-1.5 text-xs text-orange-700 bg-orange-50 hover:bg-orange-100 transition-colors border-l-4 border-orange-400 text-left"
                          data-testid={`relocate-between-${idx}`}
                        >
                          ↕️ Ubicar AQUI (entre {Math.abs(n.amount || 0).toLocaleString('es-AR')} y {Math.abs(next.amount || 0).toLocaleString('es-AR')})
                        </button>
                      )}
                    </React.Fragment>
                  );
                })}
                {/* Boton: al final (mas viejo que todos) */}
                <button
                  type="button"
                  onClick={() => placeRecoveredBetween(recoveryReloc.neighbors[recoveryReloc.neighbors.length - 1], null)}
                  className="w-full px-4 py-2 text-sm text-left text-orange-700 bg-orange-50 hover:bg-orange-100 transition-colors"
                  data-testid="relocate-bottom"
                >
                  ⬇️ Ubicar AL FINAL (mas viejo que todos)
                </button>
              </div>
            )}
          </div>
          <div className="text-xs text-gray-500 pt-2">
            Tip: la fecha/hora resultante es el punto medio entre los dos vecinos. Si necesitas mas precision, despues podes repetir el proceso.
          </div>
        </DialogContent>
      </Dialog>

          {/* Dialog para editar movimiento manual */}
      <Dialog open={showEditManualDialog} onOpenChange={setShowEditManualDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-amber-600" />
              Editar Movimiento Manual
            </DialogTitle>
            <DialogDescription>
              Modifica los datos del movimiento
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="editEntryType">Tipo de Movimiento</Label>
              <Select value={editManualType} onValueChange={setEditManualType}>
                <SelectTrigger id="editEntryType" data-testid="edit-manual-entry-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual_credit">Credito (Deuda)</SelectItem>
                  <SelectItem value="manual_debit">Debito (Descuento)</SelectItem>
                  <SelectItem value="manual_payment">Pago</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="editAmount">Monto</Label>
              <Input
                id="editAmount"
                type="number"
                step="0.01"
                min="0"
                value={editManualAmount}
                onChange={(e) => setEditManualAmount(e.target.value)}
                placeholder="Ej: 50000"
                data-testid="edit-manual-amount"
              />
            </div>
            <div>
              <Label htmlFor="editDescription">Descripcion</Label>
              <Input
                id="editDescription"
                value={editManualDescription}
                onChange={(e) => setEditManualDescription(e.target.value)}
                placeholder="Descripcion del movimiento"
                data-testid="edit-manual-description"
              />
            </div>
            <div>
              <Label htmlFor="editDate">Fecha</Label>
              <Input
                id="editDate"
                type="date"
                value={editManualDate}
                onChange={(e) => setEditManualDate(e.target.value)}
                data-testid="edit-manual-date"
              />
            </div>
            {editManualType === 'manual_payment' && (
              <div>
                <Label htmlFor="editDestination">Destinatario</Label>
                <Input
                  id="editDestination"
                  value={editManualDestination}
                  onChange={(e) => setEditManualDestination(e.target.value)}
                  placeholder="Nombre del destinatario"
                  data-testid="edit-manual-destination"
                />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowEditManualDialog(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={saveEditManualEntry}
              disabled={editManualLoading || !editManualAmount}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="save-edit-manual-entry"
            >
              {editManualLoading ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog para editar nombre */}
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Editar Nombre</DialogTitle>
                <DialogDescription>
                  Cambia el nombre de {editingCliente?.name}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="editClienteName">Nuevo Nombre</Label>
                  <Input
                    id="editClienteName"
                    data-testid="edit-group-name-input"
                    placeholder="Ingresa el nuevo nombre"
                    value={editClienteName}
                    onChange={(e) => setEditClienteName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        updateGroupName();
                      }
                    }}
                  />
                </div>
                <Button data-testid="submit-edit-name-btn" onClick={updateGroupName} className="w-full">
                  Actualizar Nombre
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Dialog para configurar saldo inicial de Financiera */}
          <Dialog open={showInitialBalanceModal} onOpenChange={setShowInitialBalanceModal}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>💳 Configurar Cuenta Financiera</DialogTitle>
                <DialogDescription>
                  Ingresa el saldo inicial que tienes disponible en Financiera
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="initialBalance">Saldo Inicial Disponible</Label>
                  <Input
                    id="initialBalance"
                    placeholder="$ 0,00"
                    value={initialBalanceInput}
                    onChange={(e) => setInitialBalanceInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        saveInitialBalance();
                      }
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    ℹ️ Este es el saldo que tienes actualmente en Financiera. El sistema calculará automáticamente a partir de aquí.
                  </p>
                </div>
                <Button onClick={saveInitialBalance} className="w-full">
                  Guardar
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Dialog detallado de Financiera */}
          <Dialog open={showFinancieraModal} onOpenChange={setShowFinancieraModal}>
            <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>💳 Cuenta Financiera - Detalle Completo</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportFinancieraToExcel}
                    className="flex items-center gap-2"
                  >
                    <FileDown className="w-4 h-4" />
                    Exportar Excel
                  </Button>
                </DialogTitle>
              </DialogHeader>
              
              {financieraSummary && (
                <div className="space-y-6 mt-4">
                  {/* Resumen General */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-xs text-gray-600">Saldo Inicial</div>
                        <div className="text-lg font-bold text-blue-700">
                          {formatAmount(financieraSummary.initial_balance)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-xs text-gray-600">Ingresos Brutos</div>
                        <div className="text-lg font-bold text-green-700">
                          {formatAmount(financieraSummary.total_income)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-xs text-gray-600">Comisiones</div>
                        <div className="text-lg font-bold text-red-700">
                          -{formatAmount(financieraSummary.total_commissions)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-xs text-gray-600">Pagos Realizados</div>
                        <div className="text-lg font-bold text-red-700">
                          -{formatAmount(financieraSummary.total_payments)}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Disponible Actual */}
                  <Card className={`border-2 ${
                    financieraSummary.available >= 0 ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                  }`}>
                    <CardContent className="pt-4">
                      <div className="text-center">
                        <div className="text-sm text-gray-600 mb-2">💰 Disponible Actual</div>
                        <div className={`text-4xl font-bold ${
                          financieraSummary.available >= 0 ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {formatAmount(financieraSummary.available)}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Tabla de Movimientos */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3">📋 Movimientos</h3>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left py-2 px-3 text-xs font-semibold">Fecha</th>
                            <th className="text-left py-2 px-3 text-xs font-semibold">Cliente</th>
                            <th className="text-center py-2 px-3 text-xs font-semibold">Tipo</th>
                            <th className="text-right py-2 px-3 text-xs font-semibold">Monto</th>
                            <th className="text-right py-2 px-3 text-xs font-semibold">Comisión</th>
                            <th className="text-right py-2 px-3 text-xs font-semibold">Neto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {financieraMovements.map((mov, idx) => (
                            <tr key={idx} className="border-t hover:bg-gray-50">
                              <td className="py-2 px-3 text-sm">{mov.date}</td>
                              <td className="py-2 px-3 text-sm">{mov.cliente_name}</td>
                              <td className="py-2 px-3 text-center">
                                <Badge variant={mov.movement_type === 'income' ? 'default' : 'destructive'}>
                                  {mov.movement_type === 'income' ? '📥 IN' : '📤 OUT'}
                                </Badge>
                              </td>
                              <td className="py-2 px-3 text-sm text-right font-semibold">
                                {formatAmount(mov.amount)}
                              </td>
                              <td className="py-2 px-3 text-sm text-right text-red-600">
                                {mov.commission_amount > 0 ? `-${formatAmount(mov.commission_amount)}` : '-'}
                              </td>
                              <td className={`py-2 px-3 text-sm text-right font-bold ${
                                mov.net_amount >= 0 ? 'text-green-700' : 'text-red-700'
                              }`}>
                                {formatAmount(mov.net_amount)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Mostrando {financieraMovements.length} de {financieraSummary.movements_count} movimientos
                    </p>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Dialog para agregar movimiento manual */}
          <Dialog open={showManualEntryDialog} onOpenChange={setShowManualEntryDialog}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Agregar Movimiento Manual</DialogTitle>
                <DialogDescription>
                  Registra un crédito, débito o pago manualmente
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Tipo de Movimiento */}
                <div>
                  <Label htmlFor="entryType">Tipo de Movimiento</Label>
                  <Select value={manualEntryType} onValueChange={setManualEntryType}>
                    <SelectTrigger id="entryType" data-testid="manual-entry-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual_credit">💰 Crédito (Suma al saldo)</SelectItem>
                      <SelectItem value="manual_debit">💸 Débito (Resta del saldo)</SelectItem>
                      <SelectItem value="manual_payment">🧾 Pago (Resta del saldo)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Monto */}
                <div>
                  <Label htmlFor="manualAmount">Monto ($)</Label>
                  <Input
                    id="manualAmount"
                    data-testid="manual-amount"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={manualAmount}
                    onChange={(e) => setManualAmount(e.target.value)}
                  />
                </div>

                {/* Descripción */}
                <div>
                  <Label htmlFor="manualDescription">Descripción / Concepto</Label>
                  <Input
                    id="manualDescription"
                    data-testid="manual-description"
                    placeholder="Ej: Depósito inicial, Pago a proveedor..."
                    value={manualDescription}
                    onChange={(e) => setManualDescription(e.target.value)}
                  />
                </div>

                {/* Fecha */}
                <div>
                  <Label htmlFor="manualDate">Fecha</Label>
                  <Input
                    id="manualDate"
                    data-testid="manual-date"
                    type="date"
                    value={manualDate}
                    onChange={(e) => setManualDate(e.target.value)}
                  />
                </div>

                {/* Campos condicionales para Pago */}
                {manualEntryType === 'manual_payment' && (
                  <>
                    <div>
                      <Label htmlFor="manualDestination">Destinatario (Opcional si subes comprobante)</Label>
                      <Input
                        id="manualDestination"
                        data-testid="manual-destination"
                        placeholder="Nombre del destinatario"
                        value={manualDestination}
                        onChange={(e) => setManualDestination(e.target.value)}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Si subes un comprobante, el destinatario se extraerá automáticamente con OCR
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="manualFile">Comprobante con OCR 📄</Label>
                      <Input
                        id="manualFile"
                        data-testid="manual-file"
                        type="file"
                        accept="image/jpeg,image/png,image/jpg,application/pdf"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            handleManualFileChange(file);
                          }
                        }}
                        className="cursor-pointer"
                        disabled={processingOCR}
                      />
                      {processingOCR && (
                        <p className="text-xs text-blue-600 mt-1 font-medium animate-pulse">
                          🔄 Procesando comprobante con OCR...
                        </p>
                      )}
                      {manualFile && !processingOCR && (
                        <p className="text-xs text-green-600 mt-1 font-medium">
                          ✓ {manualFile.name} - Datos extraídos y aplicados
                        </p>
                      )}
                    </div>
                  </>
                )}

                <Button
                  data-testid="submit-manual-entry"
                  onClick={createManualEntry}
                  disabled={manualLoading || processingOCR}
                  className="w-full"
                >
                  {manualLoading ? 'Guardando...' : processingOCR ? 'Procesando OCR...' : 'Registrar Movimiento'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Dialog para previsualizar comprobante */}
          <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
            <DialogContent className="max-w-4xl max-h-[90vh]">
              <DialogHeader>
                <DialogTitle>Vista Previa del Comprobante</DialogTitle>
                <DialogDescription>
                  {previewReceipt?.filename} • {previewReceipt?.date}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4">
                {previewLoading ? (
                  <div className="flex items-center justify-center h-96">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Cargando comprobante...</p>
                    </div>
                  </div>
                ) : previewReceipt?.previewUrl ? (
                  <div className="space-y-4">
                    {/* Preview Image/PDF */}
                    <div className="border rounded-lg overflow-hidden bg-gray-50">
                      {previewReceipt.file_type?.includes('pdf') ? (
                        <iframe
                          src={previewReceipt.previewUrl}
                          className="w-full h-[600px]"
                          title="PDF Preview"
                        />
                      ) : (
                        <img
                          src={previewReceipt.previewUrl}
                          alt={previewReceipt.filename}
                          className="w-full h-auto max-h-[600px] object-contain"
                        />
                      )}
                    </div>
                    
                    {/* Receipt Info */}
                    <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-xs text-gray-500">Fecha</p>
                        <p className="font-semibold">{previewReceipt.date} {previewReceipt.time || ''}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Monto</p>
                        <p className="font-semibold text-green-600">${previewReceipt.amount?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Destino</p>
                        <p className="font-semibold">{previewReceipt.destination_holder || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Banco</p>
                        <p className="font-semibold">{previewReceipt.bank || '-'}</p>
                      </div>
                      {previewReceipt.transaction_id && (
                        <div className="col-span-2">
                          <p className="text-xs text-gray-500">ID Transacción</p>
                          <p className="font-semibold font-mono text-sm">{previewReceipt.transaction_id}</p>
                        </div>
                      )}
                    </div>

                    {/* Download Button */}
                    <Button
                      onClick={() => downloadReceiptBackup(previewReceipt.id, previewReceipt.filename)}
                      className="w-full"
                      variant="outline"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Descargar Comprobante Original
                    </Button>
                  </div>
                ) : null}
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        {selectedCliente && currentCliente && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <Wallet className="w-4 h-4" /> Balance Total
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold" style={{ color: currentCliente.color }}>
                  {formatAmount(currentCliente.balance)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Total Comprobantes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-gray-900">{currentCliente.receipts_count || 0}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Receipts Table */}
        {selectedCliente ? (
          <Card>
            <CardHeader>
              <div className="flex flex-wrap justify-between items-start gap-4 mb-2">
                <div>
                  <CardTitle className="text-lg font-bold">Comprobantes de {currentCliente?.name}</CardTitle>
                  <CardDescription>Historial de transacciones procesadas con OCR</CardDescription>
                </div>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-4 flex-wrap">
                    <ReceiptInbox key={selectedCliente} clientId={selectedCliente} onPreview={handlePreviewReceipt} /><RecoverFailed key={`recovery-${selectedCliente}`} clientId={selectedCliente} onUpdated={() => { invalidateCache(selectedCliente); loadReceiptsForGroup(selectedCliente, activeCartera?.id); fetchClientes(); }} />
                    {/* Switch de Filtro de Fechas */}
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={dateFilterEnabled}
                        onChange={(e) => {
                          setDateFilterEnabled(e.target.checked);
                          if (!e.target.checked) {
                            setStartDate('');
                            setEndDate('');
                          }
                        }}
                        className="w-9 h-5 appearance-none bg-gray-300 rounded-full relative cursor-pointer transition-colors checked:bg-indigo-600 before:content-[''] before:absolute before:w-4 before:h-4 before:rounded-full before:bg-white before:top-0.5 before:left-0.5 before:transition-transform checked:before:translate-x-4"
                      />
                      <Calendar className="w-4 h-4" />
                      Filtrar por fecha
                    </label>
                    
                    {/* Filtro de Destinatarios (AGREGADO 2025-11-04) */}
                    <div className="flex items-center gap-2">
                      <Select
                        value={selectedDestinatario}
                        onValueChange={setSelectedDestinatario}
                        disabled={loadingDestinatarios || availableDestinatarios.length === 0}
                      >
                        <SelectTrigger className="w-[220px]">
                          <SelectValue placeholder="🔍 Filtrar destinatario" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos los destinatarios</SelectItem>
                          {availableDestinatarios.map(dest => (
                            <SelectItem key={dest} value={dest}>
                              {dest}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {selectedDestinatario && selectedDestinatario !== 'all' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSelectedDestinatario('all')}
                          className="h-8 w-8 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {/* Botón Exportar Excel (siempre visible cuando hay comprobantes) */}
                      {receipts.length > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={exportToExcel}
                          className="flex items-center gap-2 border-green-600 text-green-600 hover:bg-green-50"
                        >
                          <FileSpreadsheet className="w-4 h-4" />
                          Exportar Excel
                        </Button>
                      )}
                    </div>
                    
                    {/* Botón Semana Vencida */}
                    {dateFilterEnabled && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          // Calcular SEMANA VENCIDA (la semana anterior completa)
                          const now = new Date();
                          const dayOfWeek = now.getDay(); // 0=Domingo, 1=Lunes, ..., 6=Sábado
                          const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Días hasta el lunes de esta semana
                          
                          // Lunes de esta semana
                          const thisMonday = new Date(now);
                          thisMonday.setDate(now.getDate() + diffToMonday);
                          
                          // Lunes de la semana ANTERIOR (semana vencida)
                          const lastMonday = new Date(thisMonday);
                          lastMonday.setDate(thisMonday.getDate() - 7);
                          lastMonday.setHours(0, 0, 0, 0);
                          
                          // Domingo de la semana vencida (23:59)
                          const lastSunday = new Date(lastMonday);
                          lastSunday.setDate(lastMonday.getDate() + 6);
                          lastSunday.setHours(23, 59, 59, 999);
                          
                          // Formatear a YYYY-MM-DD para los inputs
                          const formatDate = (date) => {
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const day = String(date.getDate()).padStart(2, '0');
                            return `${year}-${month}-${day}`;
                          };
                          
                          setStartDate(formatDate(lastMonday));
                          setEndDate(formatDate(lastSunday));
                          
                          toast.success('Semana vencida seleccionada: ' + formatDate(lastMonday) + ' al ' + formatDate(lastSunday));
                        }}
                        className="flex items-center gap-2"
                      >
                        <Calendar className="w-4 h-4" />
                        Semana Vencida
                      </Button>
                    )}
                    
                  </div>
                  
                  {/* Inputs de Fecha */}
                  {dateFilterEnabled && (
                    <div className="flex gap-2 items-center flex-wrap">
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-40"
                        placeholder="Desde"
                      />
                      <span className="text-gray-500">-</span>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-40"
                        placeholder="Hasta"
                      />
                      
                      {/* Botones de acción cuando hay filtro activo */}
                      {(startDate || endDate) && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={exportToExcel}
                            className="flex items-center gap-2 border-green-600 text-green-600 hover:bg-green-50"
                          >
                            <Download className="w-4 h-4" />
                            Excel
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const reportUrl = `/reporte-semanal?group=${selectedCliente}&start=${startDate}&end=${endDate}&autoprint=true`;
                              window.open(reportUrl, '_blank');
                            }}
                            className="flex items-center gap-2 border-red-600 text-red-600 hover:bg-red-50"
                          >
                            <FileText className="w-4 h-4" />
                            Generar PDF
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingReceipts && receipts.length === 0 ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                  <p className="text-gray-600 font-medium">Cargando comprobantes...</p>
                </div>
              ) : receipts.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 font-medium">No hay comprobantes aún</p>
                  <p className="text-sm text-gray-400 mt-1">Sube tu primer comprobante para comenzar</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="receipts-table">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-center py-3 px-2 text-sm font-semibold text-gray-700">●</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Tipo</th>
                        <th data-testid="receipts-datetime-header" className="min-w-[156px] whitespace-nowrap text-left py-3 px-4 text-sm font-semibold text-gray-700">Fecha y hora</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Descripción</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Origen</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">CUIL</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Destino</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Monto</th>
                        {/* FASE BAL (2026-02): columna Saldo acumulado (running balance) */}
                        <th className="text-right py-3 px-4 text-sm font-semibold text-emerald-700 bg-emerald-50/40">Saldo</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Archivo</th>
                        <th className="text-center py-3 px-3 text-sm font-semibold text-gray-700">Renglón</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Filtrar comprobantes por fecha si el filtro está activado
                        let filteredReceipts = receipts;
                        
                        if (dateFilterEnabled && (startDate || endDate)) {
                          filteredReceipts = receipts.filter(receipt => {
                            const receiptDate = new Date(receipt.date);
                            let matchStart = true;
                            let matchEnd = true;
                            
                            if (startDate) {
                              const start = new Date(startDate);
                              matchStart = receiptDate >= start;
                            }
                            
                            if (endDate) {
                              const end = new Date(endDate);
                              end.setHours(23, 59, 59, 999); // Incluir todo el día final
                              matchEnd = receiptDate <= end;
                            }
                            
                            return matchStart && matchEnd;
                          });
                        }
                        
                        // Filtrar por destinatario si hay selección (AGREGADO 2025-11-04)
                        if (selectedDestinatario && selectedDestinatario !== 'all') {
                          filteredReceipts = filteredReceipts.filter(receipt => 
                            receipt.destination_holder === selectedDestinatario
                          );
                        }
                        
                        // Construir lista intercalada con lineas de corte
                        const items = [];
                        // _toDate: las fechas naive (cierres de ciclo, cortes de estado de cuenta)
                        // son UTC. Hay que forzar la Z o el browser las lee como locales y se corren
                        // 3h respecto de los movimientos y del export a Excel.
                        const _toDate = (v) => {
                          if (!v) return new Date(0);
                          if (typeof v === 'string' && !/([Zz]|[+-]\d{2}:?\d{2})$/.test(v)) {
                            return new Date(v.replace(' ', 'T') + 'Z');
                          }
                          return new Date(v);
                        };
                        // 🐛 FIX 2026-08-02: antes esto quitaba el offset y hacia new Date(naive),
                        // asi que la barra del estado de cuenta se dibujaba 3h corrida de donde
                        // realmente se envio. Ahora usa la misma escala UTC que los movimientos.
                        const cortesFechas = cortesEstadoCuenta.map(c => _toDate(c.fecha));
                        // Merge receipts + gaps DESC, ordenados por sort_order (unico campo que
                        // mueven las flechitas ↑↓ y que usa el Excel); fallback a created_at.
                        const clienteGaps = gapsByClient[selectedCliente]?.gaps || [];
                        const receiptIdsSet = new Set(filteredReceipts.map(r => r.id));
                        const combinedList = [
                          ...filteredReceipts.map(r => ({
                            kind: 'receipt',
                            data: r,
                            sortDate: new Date(receiptSortValue(r) * 1000)
                          })),
                          ...clienteGaps.map(g => ({
                            kind: 'gap',
                            data: g,
                            sortDate: new Date(g.timestamp)
                          }))
                        ].sort((a, b) => a.kind === 'receipt' && b.kind === 'receipt' ? compareReceiptsNewest(a.data, b.data) : b.sortDate - a.sortDate);
                        
                        combinedList.forEach((entry, idx) => {
                          if (entry.kind === 'gap') {
                            items.push({ type: 'gap', data: entry.data });
                          } else {
                            items.push({ type: 'receipt', data: entry.data });
                          }
                          
                          const receiptDate = entry.sortDate;
                          
                          // Luego verificar si hay un corte DESPUES de este item (entre este y el siguiente)
                          cortesFechas.forEach((corteFecha, ci) => {
                            const nextEntry = combinedList[idx + 1];
                            const nextDate = nextEntry ? nextEntry.sortDate : null;
                            
                            // El corte va despues de este receipt si: receipt >= corte > nextReceipt
                            if (cutFitsBetween(receiptDate, nextDate, corteFecha, hasMoreReceipts)) {
                              const corteKey = `corte-${ci}`;
                              if (!items.find(it => it.type === 'corte' && it.key === corteKey)) {
                                items.push({
                                  type: 'corte',
                                  key: corteKey,
                                  fecha: cortesEstadoCuenta[ci].fecha,
                                  enviado_por: cortesEstadoCuenta[ci].enviado_por,
                                  silencioso: !!cortesEstadoCuenta[ci].silencioso,
                              origen: cortesEstadoCuenta[ci].origen
                                });
                              }
                            }
                          });
                        });

                        // FASE BAL (2026-02): running balance desde el balance ACTUAL hacia atrás.
                        //
                        // Convención (estilo extracto bancario):
                        //   - Saldo en cada fila = saldo del cliente JUSTO DESPUÉS de ese movimiento.
                        //   - El movimiento más reciente (arriba) tiene saldo = balance actual del cliente
                        //     (currentCliente.balance, EL MISMO valor que se muestra en la card).
                        //   - Cada movimiento más viejo descuenta su propio delta del saldo del siguiente.
                        //
                        // De esta forma el saldo del último receipt SIEMPRE coincide con el balance
                        // de la card del cliente (que es lo que el cliente externo ve), aunque haya
                        // descuadre entre el balance legacy y el balance recalculado por baseline.
                        //
                        // El divisor del baseline queda como MARCA VISUAL del momento desde el cual
                        // el sistema garantiza inmutabilidad (no afecta el cálculo del saldo).
                        if (currentCliente?.balance != null) {
                          // Helper: determina si el receipt es egreso (resta) o ingreso (suma).
                          // ⚠️ MUY IMPORTANTE: replica la MISMA lógica de signo que la columna "Monto" del render.
                          // Si la lógica no coincide, el saldo no cuadra con la suma visible.
                          const computeSignedDelta = (r) => {
                            const amt = Number(r.amount || 0);
                            if (amt < 0) return amt; // ya viene negativo (whatsapp_payout, etc)
                            const et = r.entry_type;
                            const isOperatorUpload = r.telegram_user_role && r.telegram_user_role !== 'cliente' && et !== 'telegram_pago_financiera';
                            const isDebit = (et === 'manual_debit' || et === 'manual_payment' || et === 'whatsapp_payout' || isOperatorUpload);
                            return isDebit ? -Math.abs(amt) : Math.abs(amt);
                          };

                          // combinedList viene ordenado DESC (más nuevo arriba); recorremos en ese orden.
                          // ANCLA: el saldo del receipt más nuevo == balance de la card del cliente.
                          let saldoCorriente = Number(currentCliente.balance);
                          for (const e of combinedList) {
                            if (e.kind !== 'receipt') continue;
                            const r = e.data;
                            // Saldo después de ESTE mov = el saldo corriente actual.
                            r.__saldoAcum = saldoCorriente;
                            // Para el siguiente (más viejo), restamos el delta firmado de ESTE mov:
                            // si era un crédito (+), el saldo previo era menor.
                            // si era un débito (-), el saldo previo era mayor.
                            saldoCorriente -= computeSignedDelta(r);
                          }

                          // Insertar fila divisor del baseline (solo marca visual)
                          const baselineDate = balanceSnapshot?.baseline_at ? new Date(balanceSnapshot.baseline_at) : null;
                          if (baselineDate) {
                            const idxFirstPre = items.findIndex(it => {
                              if (it.type !== 'receipt') return false;
                              const rd = new Date(it.data?.created_at || 0);
                              return rd <= baselineDate;
                            });
                            if (idxFirstPre > 0) {
                              items.splice(idxFirstPre, 0, {
                                type: 'baseline_divider',
                                key: 'baseline-divider',
                                baseline: balanceSnapshot.baseline,
                                baseline_at: balanceSnapshot.baseline_at,
                              });
                            }
                          }
                        }
                        
                        // Cortes que no calzaron entre receipts:
                        // Solo agregarlos al final si YA NO HAY MAS RECEIPTS para paginar.
                        // Si aun hay mas (hasMoreReceipts=true), los cortes historicos mas
                        // antiguos que el ultimo receipt cargado NO se muestran aun,
                        // porque apareceran intercalados cuando se carguen los receipts faltantes.
                        // Esto evita que la UI muestre una "muralla" de cortes rojos que esconde
                        // el boton "Cargar mas movimientos".
                        const lastReceiptDate = combinedList.length > 0
                          ? combinedList[combinedList.length - 1].sortDate
                          : null;
                        cortesFechas.forEach((corteFecha, ci) => {
                          const corteKey = `corte-${ci}`;
                          if (items.find(it => it.type === 'corte' && it.key === corteKey)) return;

                          // Caso 1: corte mas nuevo que el primer receipt cargado -> insertar al
                          // principio (es un corte "futuro" respecto a lo cargado, raro pero posible)
                          if (combinedList.length > 0 && corteFecha > combinedList[0].sortDate) {
                            items.unshift({
                              type: 'corte',
                              key: corteKey,
                              fecha: cortesEstadoCuenta[ci].fecha,
                              enviado_por: cortesEstadoCuenta[ci].enviado_por,
                              silencioso: !!cortesEstadoCuenta[ci].silencioso,
                              origen: cortesEstadoCuenta[ci].origen
                            });
                            return;
                          }

                          // Caso 2: corte mas antiguo que el ultimo receipt cargado.
                          // Solo mostrarlo si ya terminamos de cargar (no hay mas receipts).
                          if (!hasMoreReceipts) {
                            items.push({
                              type: 'corte',
                              key: corteKey,
                              fecha: cortesEstadoCuenta[ci].fecha,
                              enviado_por: cortesEstadoCuenta[ci].enviado_por,
                              silencioso: !!cortesEstadoCuenta[ci].silencioso,
                              origen: cortesEstadoCuenta[ci].origen
                            });
                          }
                          // Si hay mas receipts por cargar, NO insertamos este corte aun;
                          // aparecera cuando se carguen los receipts de esa fecha.
                        });
                        
                        return groupStatementCuts(items, hasMoreReceipts ? receipts.reduce((oldest, r) => Math.min(oldest, receiptSortValue(r) * 1000), Infinity) : null).map((item) => {
                          // FASE BAL (2026-02): línea divisora del baseline
                          if (item.type === 'baseline_divider') {
                            // CORRECCIÓN ZONA HORARIA: baseline_at viene en UTC ISO.
                            // toLocaleString con timeZone hace la conversión correctamente sin doble resta.
                            const baseDate = new Date(item.baseline_at);
                            const fechaStr = baseDate.toLocaleString('es-AR', {
                              day: '2-digit', month: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit',
                              timeZone: 'America/Argentina/Buenos_Aires',
                              hour12: false,
                            });
                            const baselineStr = `$${Number(item.baseline).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                            return (
                              <tr key={item.key} className="border-0" data-testid="baseline-divider-row">
                                <td colSpan={12} className="py-2 px-0 bg-emerald-50/30">
                                  <div className="flex items-center gap-3 py-1 px-4">
                                    <div className="flex-1 h-px bg-emerald-300 opacity-60"></div>
                                    <span className="text-xs text-emerald-700 whitespace-nowrap font-semibold tracking-wide">
                                      📌 Baseline · {fechaStr} · saldo {baselineStr}
                                    </span>
                                    <div className="flex-1 h-px bg-emerald-300 opacity-60"></div>
                                  </div>
                                </td>
                              </tr>
                            );
                          }
                          if (item.type === 'cut_group') {
                            return <StatementCutRow key={item.key} cuts={item.cuts} />;
                          }

                          // Render de fila GAP (comprobante perdido) inline en orden cronologico
                          if (item.type === 'gap') {
                            const gap = item.data;
                            const gapDate = new Date(gap.timestamp);
                            const fechaStr = gapDate.toLocaleString('es-AR', {
                              day: '2-digit', month: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            });
                            const isRecovering = recoveringGapId === gap.wa_message_id;
                            return (
                              <tr
                                key={`gap-${gap.wa_message_id}`}
                                data-testid={`gap-row-${gap.wa_message_id}`}
                                className="bg-yellow-50 hover:bg-yellow-100 border-l-4 border-yellow-400 transition-colors"
                              >
                                <td colSpan={12} className="py-2 px-4">
                                  <div className="flex flex-wrap items-center gap-3 text-sm">
                                    <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                                    <span className="font-semibold text-yellow-800">Comprobante perdido</span>
                                    <span className="text-gray-600">{fechaStr}</span>
                                    <span className="text-gray-500">• {gap.from_name || 'sin nombre'}</span>
                                    {gap.group_name && (
                                      <span className="text-gray-400 text-xs">({gap.group_name})</span>
                                    )}
                                    <div className="ml-auto flex gap-2">
                                      <Button
                                        data-testid={`recover-gap-btn-${gap.wa_message_id}`}
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-3 text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
                                        disabled={isRecovering || !canWrite}
                                        onClick={() => handleRecoverGap(gap, selectedCliente)}
                                      >
                                        {isRecovering ? (
                                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                        ) : (
                                          <RefreshCw className="w-3 h-3 mr-1" />
                                        )}
                                        Recuperar
                                      </Button>
                                      <Button
                                        data-testid={`placeholder-gap-btn-${gap.wa_message_id}`}
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-3 text-xs border-gray-300 text-gray-700 hover:bg-gray-50"
                                        disabled={isRecovering || !canWrite}
                                        onClick={() => handleCreatePlaceholder(gap, selectedCliente)}
                                      >
                                        <FileQuestion className="w-3 h-3 mr-1" />
                                        Placeholder
                                      </Button>
                                      <Button
                                        data-testid={`dismiss-gap-btn-${gap.wa_message_id}`}
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-3 text-xs border-red-300 text-red-700 hover:bg-red-50"
                                        disabled={isRecovering || !canWrite}
                                        onClick={() => handleDismissGap(gap, selectedCliente)}
                                        title="Descartar - no crea movimiento ni afecta saldos"
                                      >
                                        <Trash2 className="w-3 h-3 mr-1" />
                                        Descartar
                                      </Button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          }

                          const receipt = item.data;
                        const isManual = receipt.entry_type !== 'ocr';
                        const badgeColors = {
                          'ocr': 'bg-blue-100 text-blue-800',
                          'manual_credit': 'bg-green-100 text-green-800',
                          'manual_debit': 'bg-red-100 text-red-800',
                          'manual_payment': 'bg-red-100 text-red-800'
                        };
                        const badgeLabels = {
                          'ocr': '📄 OCR',
                          'manual_credit': '💰 Crédito',
                          'manual_debit': '💸 Débito',
                          'manual_payment': '🧾 Pago'
                        };
                        
                        // Indicador de conciliación (CLICKEABLE para edición manual)
                        const getReconciliationIndicator = () => {
                          const openReconciliationEditor = (e) => {
                            e.stopPropagation();
                            reconciliationTriggerRef.current = e.currentTarget;
                            setReceiptToReconcile(receipt);
                            setManualReconciliationStatus(receipt.reconciliation_status || 'matched');
                            setManualReconciliationComment(receipt.reconciliation_details?.comment || '');
                            setShowManualReconciliationDialog(true);
                          };
                          
                          const isManualOverride = receipt.reconciliation_details?.manual_override;
                          const titleSuffix = isManualOverride ? '\n📝 Verificado manualmente' : '\n👆 Click para editar';
                          
                          if (!receipt.reconciliation_status && androidMatches[receipt.id]?.active) return <AndroidReceiptMatchBadge match={androidMatches[receipt.id]} onClick={openReconciliationEditor} />; if (!receipt.reconciliation_status) {
                            return (
                              <button 
                                onClick={openReconciliationEditor}
                                className="text-gray-300 hover:text-gray-500 text-xl transition-colors cursor-pointer" 
                                title={`Sin conciliar${titleSuffix}`}
                              >
                                ○
                              </button>
                            );
                          }
                          
                          if (receipt.reconciliation_status === 'matched') {
                            return (
                              <button 
                                onClick={openReconciliationEditor}
                                className="text-green-600 hover:text-green-700 text-xl transition-transform hover:scale-110 cursor-pointer" 
                                title={`✓ Conciliado - Extracto: ${receipt.reconciliation_details?.bank_date} $${receipt.reconciliation_details?.bank_amount?.toLocaleString()}${titleSuffix}`}
                              >
                                🟢
                              </button>
                            );
                          }
                          
                          if (receipt.reconciliation_status === 'partial') {
                            return (
                              <button 
                                onClick={openReconciliationEditor}
                                className="text-yellow-600 hover:text-yellow-700 text-xl transition-transform hover:scale-110 cursor-pointer" 
                                title={`⚠ Match parcial - Diferencia de ${receipt.reconciliation_details?.days_diff} día(s)${titleSuffix}`}
                              >
                                🟡
                              </button>
                            );
                          }
                          
                          if (receipt.reconciliation_status === 'not_found') {
                            return (
                              <button 
                                onClick={openReconciliationEditor}
                                className="text-red-600 hover:text-red-700 text-xl transition-transform hover:scale-110 cursor-pointer animate-pulse" 
                                title={`✗ No encontrado en extracto bancario${titleSuffix}`}
                              >
                                🔴
                              </button>
                            );
                          }
                          
                          if (receipt.reconciliation_status === "playwright_matched") {
                            return (
                              <button 
                                onClick={openReconciliationEditor}
                                className="text-blue-600 hover:text-blue-700 text-xl transition-transform hover:scale-110 cursor-pointer" 
                                title={`🔵 Conciliado por Playwright - Score: ${receipt.reconciliation_details?.score || "N/A"}${titleSuffix}`}
                              >
                                🔵
                              </button>
                            );
                          }
                          
                          if (receipt.reconciliation_status === "push_matched") {
                            return (
                              <button 
                                onClick={openReconciliationEditor}
                                className="text-purple-600 hover:text-purple-700 text-xl transition-transform hover:scale-110 cursor-pointer" 
                                title={`🟣 Conciliado por Push Notification (${receipt.reconciliation_details?.app || 'N/A'}) - Score: ${receipt.reconciliation_details?.match_score || "N/A"}/100 - ${receipt.reconciliation_details?.notification_sender || ''}${titleSuffix}`}
                              >
                                🟣
                              </button>
                            );
                          }
                          
                          if (receipt.reconciliation_status === "santander_worker_realtime") {
                            return (
                              <button 
                                onClick={openReconciliationEditor}
                                className="text-purple-600 hover:text-purple-700 text-xl transition-transform hover:scale-110 cursor-pointer" 
                                title={`🟣 Conciliado por Santander Worker - Score: ${receipt.reconciliation_details?.score || "N/A"} - Mov: ${receipt.reconciliation_details?.movement_id || "N/A"}${titleSuffix}`}
                              >
                                🟣
                              </button>
                            );
                          }
                        };
                        
                        return (
                        <tr key={receipt.id} className={`border-b border-gray-100 hover:bg-gray-50 ${receipt.recovered ? 'bg-orange-50 hover:bg-orange-100 border-l-4 border-orange-400' : ''}`} data-testid={`receipt-row-${receipt.id}`}>
                          <td className="py-3 px-2 text-center">
                            {receipt.entry_type === 'manual_credit' || receipt.entry_type === 'manual_debit' || receipt.entry_type === 'manual_payment' ? (
                              <span className="text-gray-300" title="Movimiento manual - no requiere conciliación">-</span>
                            ) : (
                              getReconciliationIndicator()
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badgeColors[receipt.entry_type] || badgeColors.ocr}`}>
                              {badgeLabels[receipt.entry_type] || badgeLabels.ocr}
                            </span>
                            {receipt.recovered && (
                              <span
                                className="ml-1 inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-300"
                                title={`Recuperado el ${receipt.recovered_at ? new Date(receipt.recovered_at).toLocaleString('es-AR') : ''}`}
                                data-testid={`recovered-badge-${receipt.id}`}
                              >
                                🟠 RECUPERADO
                              </span>
                            )}
                          </td>
                          <ReceiptDateCell receipt={receipt} onEdit={editRecoveredTime} canWrite={canWrite} />
                          <td className="py-3 px-4 text-sm text-gray-700">
                            {receipt.status === 'ocr_failed' ? (
                              <span className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-bold">
                                OCR FALLIDO - Reintentar
                              </span>
                            ) : isManual ? (receipt.description || '-') : (receipt.transaction_id || '-')}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-700">{receipt.origin_holder || '-'}</td>
                          <td className="py-3 px-4 text-sm text-gray-600 font-mono text-xs">
                            {receipt.origin_cuil || '-'}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-700">{receipt.destination_holder || '-'}</td>
                          <td className="py-3 px-4 text-sm font-semibold" 
                              style={{ 
                                color: (receipt.entry_type === 'manual_debit' || 
                                        receipt.entry_type === 'manual_payment' ||
                                        receipt.entry_type === 'telegram_pago_financiera' ||
                                        receipt.entry_type === 'whatsapp_payout' ||
                                        (receipt.telegram_user_role && receipt.telegram_user_role !== 'cliente'))
                                  ? '#dc2626' // Rojo para débitos, pagos y uploads de admin/financiera
                                  : receipt.entry_type === 'manual_credit' 
                                    ? '#16a34a' // Verde para créditos
                                    : '#000000' // Negro para depósitos/OCR regulares
                              }}>
                            {/* Determinar el signo a mostrar */}
                            {receipt.entry_type === 'manual_credit' && '+'}
                            {/* Si amount es negativo (telegram_pago_financiera, whatsapp_payout), forzar signo - */}
                            {receipt.amount < 0 ? '-' : 
                              ((receipt.entry_type === 'manual_debit' || 
                                receipt.entry_type === 'manual_payment' ||
                                receipt.entry_type === 'whatsapp_payout' ||
                                (receipt.telegram_user_role && receipt.telegram_user_role !== 'cliente' && receipt.entry_type !== 'telegram_pago_financiera')) 
                                ? '-' : '')}
                            {formatAmount(Math.abs(receipt.amount))}
                          </td>
                          {/* FASE BAL (2026-02): Saldo acumulado (running balance hacia atrás desde el balance actual) */}
                          <td className="py-3 px-4 text-sm text-right font-semibold tabular-nums bg-emerald-50/30">
                            {receipt.__saldoAcum != null ? (
                              <span
                                className={receipt.__saldoAcum < 0 ? 'text-red-700' : 'text-emerald-700'}
                                data-testid={`saldo-acum-${receipt.id}`}
                              >
                                ${formatAmount(receipt.__saldoAcum)}
                              </span>
                            ) : (
                              <span className="text-gray-300 text-xs" title="Sin snapshot de balance">—</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 truncate max-w-[150px]">{receipt.filename}</td>
                          <td className="py-3 px-3 text-center text-sm">
                            {receipt.reconciliation_details?.extracto_renglon ? (
                              <span 
                                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-md font-medium"
                                title={`Renglón ${receipt.reconciliation_details.extracto_renglon} del archivo: ${receipt.reconciliation_details.extracto_archivo || 'extracto bancario'}`}
                              >
                                <span className="text-xs">📄</span>
                                {receipt.reconciliation_details.extracto_renglon}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {/* Mostrar preview/download si es OCR o si es pago manual con archivo */}
                              {(receipt.entry_type === 'ocr' || receipt.entry_type === 'customer_upload' || 
                                receipt.entry_type === 'telegram_comprobante' || receipt.entry_type === 'telegram_pago_financiera' ||
                                receipt.entry_type === 'telegram_bot' ||
                                (receipt.entry_type === 'manual_payment' && receipt.file_data)) ? (
                                <>
                                  <Button
                                    data-testid={`preview-receipt-${receipt.id}`}
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handlePreviewReceipt(receipt)}
                                    className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600"
                                    title="Ver comprobante completo"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    data-testid={`download-backup-${receipt.id}`}
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDownloadReceipt(receipt)}
                                    className="h-8 w-8 p-0 hover:bg-green-50 hover:text-green-600"
                                    title="Descargar archivo original"
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                </>
                              ) : (
                                <span className="text-xs text-gray-400">Sin archivo</span>
                              )}
                              <Button
                                data-testid={`move-up-${receipt.id}`}
                                variant="ghost"
                                size="sm"
                                onClick={() => moveReceiptUp(receipt.id)}
                                className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600"
                                title="Mover arriba"
                              >
                                <ChevronUp className="w-4 h-4" />
                              </Button>
                              <Button
                                data-testid={`move-down-${receipt.id}`}
                                variant="ghost"
                                size="sm"
                                onClick={() => moveReceiptDown(receipt.id)}
                                className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600"
                                title="Mover abajo"
                              >
                                <ChevronDown className="w-4 h-4" />
                              </Button>
                              {/* Boton reintentar OCR - solo comprobantes fallidos */}
                              {receipt.status === 'ocr_failed' && (
                                <Button
                                  data-testid={`retry-ocr-${receipt.id}`}
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => retryOcr(receipt.id)}
                                  disabled={retryingOcr === receipt.id}
                                  className="h-8 w-8 p-0 hover:bg-orange-50 hover:text-orange-600"
                                  title="Reintentar OCR"
                                >
                                  {retryingOcr === receipt.id ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <RefreshCw className="w-4 h-4" />
                                  )}
                                </Button>
                              )}
                              {/* Boton editar - solo movimientos manuales */}
                              {(receipt.entry_type === 'manual_credit' || receipt.entry_type === 'manual_debit' || receipt.entry_type === 'manual_payment') && (
                                <Button
                                  data-testid={`edit-manual-receipt-${receipt.id}`}
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditManualEntry(receipt)}
                                  className="h-8 w-8 p-0 hover:bg-amber-50 hover:text-amber-600"
                                  title="Editar movimiento manual"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                data-testid={`delete-receipt-${receipt.id}`}
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteReceipt(receipt.id)}
                                className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600"
                                title="Eliminar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                        );
                        })
                      })()}
                    </tbody>
                  </table>
                  
                  {/* Indicador de registros y botón Cargar Más */}
                  <div className="mt-3 flex flex-wrap items-center justify-center gap-3 border-t border-gray-200 bg-white px-4 py-3">
                    <p className="text-sm text-gray-500">
                      Mostrando {receipts.length} movimiento{receipts.length !== 1 ? 's' : ''}
                    </p>
                    {hasMoreReceipts && (
                      <Button
                        onClick={() => loadReceiptsForGroup(selectedCliente, activeCartera.id, true)}
                        disabled={loadingMoreReceipts}
                        variant="outline"
                        className="mx-auto"
                      >
                        {loadingMoreReceipts ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Cargando más...
                          </>
                        ) : (
                          <>
                            <ChevronDown className="mr-2 h-4 w-4" />
                            Cargar más movimientos
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Wallet className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 font-medium">Selecciona o crea una cuenta para comenzar</p>
            </CardContent>
          </Card>
        )}
        </>
        )}

        {/* Preview Dialog */}
        <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Vista Previa del Comprobante</DialogTitle>
              <DialogDescription>
                {previewReceipt?.filename}
              </DialogDescription>
            </DialogHeader>
            
            {previewLoading ? (
              <div className="flex items-center justify-center p-12">
                <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
              </div>
            ) : previewReceipt ? (
              <div className="space-y-4">
                {/* Detalles del comprobante */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Fecha</p>
                    <p className="text-sm font-medium">{previewReceipt.date}</p>
                  </div>
                  {previewReceipt.time && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Hora</p>
                      <p className="text-sm font-medium">{previewReceipt.time}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Monto</p>
                    <p className="text-lg font-bold text-green-600">${previewReceipt.amount.toLocaleString()}</p>
                  </div>
                  {previewReceipt.origin_holder && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Origen</p>
                      <p className="text-sm font-medium">{previewReceipt.origin_holder}</p>
                      {previewReceipt.origin_cuil && (
                        <p className="text-xs text-gray-500">CUIL: {previewReceipt.origin_cuil}</p>
                      )}
                    </div>
                  )}
                  {previewReceipt.destination_holder && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Destinatario</p>
                      <p className="text-sm font-medium">{previewReceipt.destination_holder}</p>
                    </div>
                  )}
                  {previewReceipt.bank && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Banco</p>
                      <p className="text-sm font-medium">{previewReceipt.bank}</p>
                    </div>
                  )}
                  {previewReceipt.transaction_id && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">ID Transacción</p>
                      <p className="text-sm font-medium">{previewReceipt.transaction_id}</p>
                    </div>
                  )}
                  {previewReceipt.uploaded_by_customer && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Subido por</p>
                      <p className="text-sm font-medium">{previewReceipt.uploaded_by_customer}</p>
                    </div>
                  )}
                  {previewReceipt.description && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500 mb-1">Descripción</p>
                      <p className="text-sm font-medium">{previewReceipt.description}</p>
                    </div>
                  )}
                </div>
                
                {/* Vista previa del archivo */}
                <div className="border rounded-lg p-4 bg-white dark:bg-gray-900">
                  {previewReceipt.file_type?.startsWith('image/') ? (
                    <img 
                      src={previewReceipt.fileData} 
                      alt="Comprobante" 
                      className="max-w-full h-auto rounded"
                    />
                  ) : previewReceipt.file_type === 'application/pdf' ? (
                    <iframe 
                      src={previewReceipt.fileData} 
                      className="w-full h-[600px] rounded"
                      title="Vista previa PDF"
                    />
                  ) : (
                    <div className="text-center p-8">
                      <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">Vista previa no disponible para este tipo de archivo</p>
                      <Button 
                        onClick={() => handleDownloadReceipt(previewReceipt)}
                        className="mt-4"
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Descargar archivo
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Dialog de Reconciliación Manual */}
        <Dialog open={showManualReconciliationDialog} onOpenChange={setShowManualReconciliationDialog}>
          <DialogContent className="max-w-md" onCloseAutoFocus={e => { e.preventDefault(); reconciliationTriggerRef.current?.focus({ preventScroll: true }); }}>
            <DialogHeader>
              <DialogTitle>Reconciliación Manual</DialogTitle>
              <DialogDescription>
                Actualiza el estado de conciliación de este comprobante basándote en tu verificación manual del extracto bancario.
              </DialogDescription>
            </DialogHeader>
            
            {receiptToReconcile && (
              <div className="space-y-4">
                {/* Info del comprobante */}
                <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Fecha:</span>
                    <span className="font-semibold">{receiptToReconcile.date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monto:</span>
                    <span className="font-semibold text-green-600">${receiptToReconcile.amount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Origen:</span>
                    <span className="font-semibold">{receiptToReconcile.origin_holder || '-'}</span>
                  </div>
                  {receiptToReconcile.origin_cuil && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">CUIL:</span>
                      <span className="font-mono text-xs">{receiptToReconcile.origin_cuil}</span>
                    </div>
                  )}
                </div>
                
                {/* Selector de estado */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nuevo Estado:
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setManualReconciliationStatus('matched')}
                      className={`p-3 border-2 rounded-lg transition-all ${
                        manualReconciliationStatus === 'matched'
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-green-300'
                      }`}
                    >
                      <div className="text-2xl mb-1">🟢</div>
                      <div className="text-xs font-medium">Conciliado</div>
                    </button>
                    <button
                      onClick={() => setManualReconciliationStatus('partial')}
                      className={`p-3 border-2 rounded-lg transition-all ${
                        manualReconciliationStatus === 'partial'
                          ? 'border-yellow-600 bg-yellow-50'
                          : 'border-gray-200 hover:border-yellow-300'
                      }`}
                    >
                      <div className="text-2xl mb-1">🟡</div>
                      <div className="text-xs font-medium">Parcial</div>
                    </button>
                    <button
                      onClick={() => setManualReconciliationStatus('not_found')}
                      className={`p-3 border-2 rounded-lg transition-all ${
                        manualReconciliationStatus === 'not_found'
                          ? 'border-red-600 bg-red-50'
                          : 'border-gray-200 hover:border-red-300'
                      }`}
                    >
                      <div className="text-2xl mb-1">🔴</div>
                      <div className="text-xs font-medium">No encontrado</div>
                    </button>
                  </div>
                </div>
                
                {/* Comentario */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Comentario (opcional):
                  </label>
                  <textarea
                    value={manualReconciliationComment}
                    onChange={(e) => setManualReconciliationComment(e.target.value)}
                    placeholder="Ej: Verificado manualmente en extracto bancario del 15/10/2025"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    rows={3}
                  />
                </div>
                
                {/* Botones */}
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowManualReconciliationDialog(false)}
                    className="flex-1"
                    disabled={updatingReconciliation}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={updateManualReconciliation}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                    disabled={updatingReconciliation}
                  >
                    {updatingReconciliation ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Guardar
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Dialog de Solicitudes de Eliminación Pendientes */}
        <Dialog open={showDeletionRequestsDialog} onOpenChange={setShowDeletionRequestsDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Solicitudes de Eliminación Pendientes</DialogTitle>
              <DialogDescription>
                Revisa y autoriza las solicitudes de eliminación de comprobantes
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {deletionRequests.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No hay solicitudes pendientes</p>
                </div>
              ) : (
                deletionRequests.map((request) => (
                  <div 
                    key={request.id} 
                    className="border rounded-lg p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <User className="w-4 h-4 text-indigo-600" />
                          <span className="font-semibold text-lg">{request.customer_name}</span>
                          <span className="text-xs text-gray-500">({request.group_name})</span>
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-green-600" />
                            <span className="font-bold text-green-600 text-lg">
                              ${request.receipt_amount.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>{request.receipt_date}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            <span className="text-xs">{request.receipt_filename}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-400">
                            <Clock className="w-3 h-3" />
                            <span>Solicitado: {new Date(request.requested_at).toLocaleString('es-AR')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 mt-3 pt-3 border-t">
                      <Button
                        onClick={() => approveDeletionRequest(request.id)}
                        disabled={processingRequest === request.id}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        {processingRequest === request.id ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Procesando...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Aprobar Eliminación
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => {
                          const reason = prompt('¿Motivo del rechazo? (opcional)');
                          if (reason !== null) {
                            rejectDeletionRequest(request.id, reason);
                          }
                        }}
                        disabled={processingRequest === request.id}
                        variant="outline"
                        className="flex-1 border-red-600 text-red-600 hover:bg-red-50"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Rechazar
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

      
      {/* Diálogo de Solicitudes de Crédito */}
      <Dialog open={showCreditRequestsDialog} onOpenChange={setShowCreditRequestsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-purple-600" />
              Solicitudes de Crédito
              {selectedClienteForCredit && ` - ${selectedClienteForCredit.name}`}
            </DialogTitle>
            <DialogDescription>
              Solicitudes de crédito realizadas desde Telegram
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {creditRequests.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <DollarSign className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>No hay solicitudes de crédito pendientes</p>
              </div>
            ) : (
              creditRequests.map((request) => (
                <Card key={request.id} className="border-l-4 border-l-purple-500">
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      {/* Header */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            @{request.telegram_username || 'Usuario'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(request.created_at).toLocaleString('es-AR', {
                              dateStyle: 'short',
                              timeStyle: 'short'
                            })}
                          </p>
                        </div>
                        <Badge variant={
                          request.status === 'pending' ? 'default' :
                          request.status === 'approved' ? 'success' :
                          'destructive'
                        }>
                          {request.status === 'pending' ? 'Pendiente' :
                           request.status === 'approved' ? 'Aprobada' :
                           'Rechazada'}
                        </Badge>
                      </div>
                      
                      {/* Información Financiera */}
                      <div className="grid grid-cols-2 gap-3 bg-gray-50 p-3 rounded-lg">
                        <div>
                          <p className="text-xs text-gray-500">Balance al solicitar</p>
                          <p className="text-sm font-semibold">${request.balance_at_request.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Descubierto</p>
                          <p className="text-sm font-semibold">${request.descubierto.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Crédito disponible</p>
                          <p className="text-sm font-semibold text-green-600">${request.available_credit.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Monto solicitado</p>
                          <p className="text-sm font-semibold text-purple-600">${request.requested_amount.toLocaleString()}</p>
                        </div>
                      </div>
                      
                      {/* Monto Sugerido */}
                      <div className="bg-purple-50 p-3 rounded-lg">
                        <p className="text-xs text-gray-600 mb-1">Monto sugerido por el sistema:</p>
                        <p className="text-lg font-bold text-purple-700">${request.suggested_amount.toLocaleString()}</p>
                      </div>
                      
                      {/* Notas del Admin */}
                      {request.admin_notes && (
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <p className="text-xs text-gray-600 mb-1">Notas del administrador:</p>
                          <p className="text-sm text-gray-800">{request.admin_notes}</p>
                        </div>
                      )}
                      
                      {/* Acciones */}
                      {request.status === 'pending' && (
                        <div className="flex gap-2 pt-2">
                          <Button
                            size="sm"
                            variant="default"
                            className="flex-1 bg-green-600 hover:bg-green-700"
                            onClick={() => updateCreditRequestStatus(request.id, 'approved')}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Aprobar
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="flex-1"
                            onClick={() => updateCreditRequestStatus(request.id, 'rejected')}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Rechazar
                          </Button>
                        </div>
                      )}
                      
                      {/* Info de aprobación */}
                      {request.status !== 'pending' && request.approved_at && (
                        <p className="text-xs text-gray-500 text-center pt-2">
                          {request.status === 'approved' ? 'Aprobada' : 'Rechazada'} el{' '}
                          {new Date(request.approved_at).toLocaleString('es-AR', {
                            dateStyle: 'short',
                            timeStyle: 'short'
                          })}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Configuración de Telegram */}
      {channelClient && <ClientChannelsDialog key={channelClient.id} client={channelClient}
        onClose={() => setChannelClient(null)} onSaved={() => {}} />}
      <Dialog open={showTelegramConfigDialog} onOpenChange={setShowTelegramConfigDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                className="w-5 h-5 text-cyan-600"
              >
                <path d="M21.5 2L2 10.5L11 14L14.5 23L23 3.5L21.5 2Z"/>
                <path d="M11 14L23 2"/>
              </svg>
              Configurar Accesos del Cliente
            </DialogTitle>
            <DialogDescription>
              Configura el grupo de Telegram y/o la miniapp para este cliente. Puedes configurar uno, ambos, o ninguno.
            </DialogDescription>
          </DialogHeader>

          {telegramConfigLoading && !selectedClienteForTelegram ? (
            <div className="flex justify-center items-center p-8">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Cliente Info */}
              {selectedClienteForTelegram && (
                <Alert>
                  <User className="w-4 h-4" />
                  <AlertDescription>
                    <strong>Cliente:</strong> {selectedClienteForTelegram.name}
                  </AlertDescription>
                </Alert>
              )}

              {/* Instrucciones */}
              <Alert>
                <Info className="w-4 h-4" />
                <AlertDescription>
                  <strong>¿Cómo obtener los IDs?</strong>
                  <ul className="mt-2 space-y-1 text-sm list-disc list-inside">
                    <li><strong>Telegram Group ID:</strong> Agrega el bot <code className="bg-gray-100 px-1 rounded">@userinfobot</code> a tu grupo y envía <code className="bg-gray-100 px-1 rounded">/start</code></li>
                    <li><strong>Admin Financiera ID:</strong> El usuario admin financiera debe enviar <code className="bg-gray-100 px-1 rounded">/start</code> al bot <code className="bg-gray-100 px-1 rounded">@userinfobot</code></li>
                  </ul>
                </AlertDescription>
              </Alert>

              {/* SECCIÓN: Configuración de Telegram */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Grupo de Telegram (Opcional)
                </h3>
                
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Telegram Group ID
                  </label>
                  <Input
                    placeholder="-1001234567890"
                    value={telegramConfig.grupo_telegram_id}
                    onChange={(e) => setTelegramConfig({
                      ...telegramConfig,
                      grupo_telegram_id: e.target.value
                    })}
                    className="font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    ID del grupo de Telegram (comienza con -100)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Admin Financiera Telegram ID
                  </label>
                  <Input
                    placeholder="123456789"
                    value={telegramConfig.admin_financiera_telegram_id}
                    onChange={(e) => setTelegramConfig({
                      ...telegramConfig,
                      admin_financiera_telegram_id: e.target.value
                    })}
                    className="font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    ID del usuario que representa pagos de financiera
                  </p>
                </div>

                {/* NUEVO: Usuarios Adicionales Autorizados */}
                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Usuarios Adicionales Autorizados (Empleados)
                  </label>
                  <textarea
                    placeholder="123456789, 987654321, 456789123"
                    value={telegramConfig.telegram_users_autorizados}
                    onChange={(e) => setTelegramConfig({
                      ...telegramConfig,
                      telegram_users_autorizados: e.target.value
                    })}
                    className="w-full px-3 py-2 border rounded-md font-mono text-sm"
                    rows={3}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    IDs de empleados que pueden subir comprobantes (separados por comas). Ejemplo: 123456789, 987654321
                  </p>
                  <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Útil si varios empleados del cliente suben comprobantes al grupo
                  </p>
                </div>
              </div>

              {/* NUEVA SECCIÓN: Configuración de Miniapp */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  Configuración de Miniapp (Opcional)
                </h3>

                <div className="space-y-4">
                  {/* Selector de tipo */}
                  <div>
                    <Label htmlFor="miniapp_tipo">Tipo de visualización en "Mi Club"</Label>
                    <select
                      id="miniapp_tipo"
                      value={telegramConfig.miniapp_tipo || ''}
                      onChange={(e) => {
                        const tipo = e.target.value || null;
                        setTelegramConfig({
                          ...telegramConfig,
                          miniapp_tipo: tipo,
                          club_id: null,
                          agencia_id: null
                        });
                        
                        // Cargar listas según tipo seleccionado
                        if (tipo === 'club') loadClubsList();
                        if (tipo === 'agencia') loadAgenciasList();
                      }}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      <option value="">Ninguno (no mostrar)</option>
                      <option value="club">Dueño de Club</option>
                      <option value="agencia">Dueño de Agencia</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Define qué información verá el cliente en la sección "Mi Club"
                    </p>
                  </div>

                  {/* Dropdown de CLUB (solo si tipo = 'club') */}
                  {telegramConfig.miniapp_tipo === 'club' && (
                    <div>
                      <Label htmlFor="club_id">Selecciona el Club</Label>
                      {loadingClubs ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Cargando clubs...
                        </div>
                      ) : (
                        <select
                          id="club_id"
                          value={telegramConfig.club_id || ''}
                          onChange={(e) => setTelegramConfig({
                            ...telegramConfig,
                            club_id: e.target.value || null
                          })}
                          className="w-full px-3 py-2 border rounded-md font-mono text-sm"
                        >
                          <option value="">-- Selecciona un club --</option>
                          {clubsList.map(club => (
                            <option key={club.club_id} value={club.club_id}>
                              {club.club_name} (ID: {club.club_id})
                            </option>
                          ))}
                        </select>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        El cliente verá todas las agencias de este club
                      </p>
                    </div>
                  )}

                  {/* Dropdown de AGENCIA (solo si tipo = 'agencia') */}
                  {telegramConfig.miniapp_tipo === 'agencia' && (
                    <div>
                      <Label htmlFor="agencia_id">Selecciona la Agencia</Label>
                      {loadingAgencias ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Cargando agencias...
                        </div>
                      ) : (
                        <select
                          id="agencia_id"
                          value={telegramConfig.agencia_id || ''}
                          onChange={(e) => setTelegramConfig({
                            ...telegramConfig,
                            agencia_id: e.target.value || null
                          })}
                          className="w-full px-3 py-2 border rounded-md font-mono text-sm"
                        >
                          <option value="">-- Selecciona una agencia --</option>
                          {agenciasList.map(agencia => (
                            <option key={agencia.agencia_id} value={agencia.agencia_id}>
                              {agencia.agencia_name} - {agencia.club_name} (ID: {agencia.agencia_id})
                            </option>
                          ))}
                        </select>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        El cliente solo verá los jugadores de esta agencia
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Explicación del funcionamiento */}
              <Alert className="bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200">
                <AlertCircle className="w-4 h-4 text-cyan-600" />
                <AlertDescription>
                  <strong className="text-cyan-900">¿Cómo funciona?</strong>
                  <ul className="mt-2 space-y-1 text-sm text-cyan-800 list-disc list-inside">
                    <li>Cuando el <strong>admin financiera</strong> suba un comprobante → Se registra como <strong>PAGO</strong> (resta balance)</li>
                    <li>Cuando cualquier otro usuario suba un comprobante → Se registra como <strong>COMPROBANTE</strong> (suma balance)</li>
                    <li>Los comprobantes aparecerán automáticamente en el portal del cliente</li>
                  </ul>
                </AlertDescription>
              </Alert>

              {/* Botones */}
              <div className="flex justify-between gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={deleteTelegramConfig}
                  disabled={telegramConfigLoading || !telegramConfig.grupo_telegram_id}
                  className="text-red-600 hover:bg-red-50"
                >
                  Eliminar Configuración
                </Button>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowTelegramConfigDialog(false)}
                    disabled={telegramConfigLoading}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={saveTelegramConfig}
                    disabled={telegramConfigLoading}
                    className="bg-cyan-600 hover:bg-cyan-700"
                  >
                    {telegramConfigLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      'Guardar Configuración'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      
      {/* WhatsApp Bot Configuration Dialog - configuración única */}
      <Dialog open={showWhatsAppConfigDialog} onOpenChange={setShowWhatsAppConfigDialog}>
        <DialogContent data-testid="whatsapp-config-dialog" className="sm:max-w-[860px] max-h-[90vh] overflow-y-auto">
          <div className="space-y-4">
            <div className="flex items-center gap-3 pb-3 border-b">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-700">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <DialogTitle data-testid="whatsapp-config-title" className="text-base font-semibold text-green-950">Configuración única de WhatsApp</DialogTitle>
                {selectedClienteForWhatsApp && (
                  <DialogDescription data-testid="whatsapp-config-client-name" className="text-sm text-green-700">Cliente: {selectedClienteForWhatsApp.name}</DialogDescription>
                )}
              </div>
              {whatsappBotStatus && (
                <span data-testid="whatsapp-config-bot-status" className={`px-2 py-1 text-xs rounded-full ${whatsappBotStatus.status === 'connected' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {whatsappBotStatus.status === 'connected' ? 'Conectado' : 'Desconectado'}
                </span>
              )}
            </div>

            {whatsappConfigLoading ? (
              <div data-testid="whatsapp-config-loading" className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-green-600" />
                <span className="ml-2 text-green-600">Cargando...</span>
              </div>
            ) : whatsappBotStatus?.status !== 'connected' ? (
              <div data-testid="whatsapp-config-disconnected-alert" className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">Bot de WhatsApp no conectado. Contacta al administrador.</p>
              </div>
            ) : (
              <>
                <Alert className="bg-emerald-50 border-emerald-200">
                  <Shield className="w-4 h-4 text-emerald-700" />
                  <AlertDescription data-testid="whatsapp-config-scope" className="text-emerald-900 text-sm">
                    Una sola configuración controla recepción de comprobantes, estados de cuenta, recordatorios, flyers y notificaciones. La prueba valida grupo y participantes sin enviar mensajes.
                  </AlertDescription>
                </Alert>

                {whatsappConfig.grupo_id && (
                  <div data-testid="whatsapp-selected-group-summary" className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span className="text-sm font-semibold text-green-900 truncate">
                            Grupo elegido: {whatsappConfig.grupo_name || whatsappSelectedGroup?.name || whatsappConfig.grupo_id}
                          </span>
                        </div>
                        <p className="text-xs text-green-700 mt-1 font-mono break-all">{whatsappConfig.grupo_id}</p>
                      </div>
                      <Badge data-testid="whatsapp-authorized-count" className="bg-green-600 text-white hover:bg-green-600">
                        {(whatsappConfig.numeros_autorizados || []).length} autorizado(s)
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                      {[
                        ['Comprobantes', 'receipts'],
                        ['Estado cuenta', 'statement'],
                        ['Recordatorios', 'reminders'],
                        ['Flyers', 'flyers'],
                        ['Notificaciones', 'notifications']
                      ].map(([label, key]) => (
                        <span key={key} data-testid={`whatsapp-capability-${key}`} className="inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md bg-white border border-green-200 text-green-800">
                          <CheckCircle className="w-3 h-3" /> {label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Grupo operativo único</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      data-testid="whatsapp-group-search-input"
                      type="text"
                      placeholder="Buscar grupo por nombre..."
                      value={whatsappGroupSearch}
                      onChange={(e) => setWhatsappGroupSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
                    />
                  </div>

                  {(() => {
                    const searchLower = whatsappGroupSearch.toLowerCase();
                    const filtered = whatsappGroupSearch.length > 0
                      ? whatsappAllGroups.filter(g => (g.name || '').toLowerCase().includes(searchLower))
                      : whatsappMatchingGroups.length > 0 ? whatsappMatchingGroups : [];

                    if (whatsappGroupSearch.length === 0 && whatsappMatchingGroups.length > 0) {
                      return (
                        <div data-testid="whatsapp-matching-groups-list" className="space-y-1.5 max-h-40 overflow-y-auto">
                          <p className="text-xs text-gray-500">Coincidencias por nombre del cliente:</p>
                          {whatsappMatchingGroups.map((group) => (
                            <button
                              type="button"
                              data-testid={`whatsapp-group-option-${group.id}`}
                              key={group.id}
                              onClick={() => selectWhatsAppGroup(group)}
                              className={`w-full text-left p-2 border rounded-lg cursor-pointer transition-all ${
                                whatsappConfig.grupo_id === group.id
                                  ? 'border-green-500 bg-green-50'
                                  : 'border-gray-200 hover:border-green-300'
                              }`}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <span className="font-medium text-sm truncate">{group.name}</span>
                                <span className="text-xs text-gray-500 flex-shrink-0">{group.participants} participantes</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      );
                    }

                    if (filtered.length === 0 && whatsappGroupSearch.length > 0) {
                      return <p data-testid="whatsapp-group-no-results" className="text-sm text-gray-500 italic py-2">Sin resultados para "{whatsappGroupSearch}"</p>;
                    }

                    if (filtered.length > 0) {
                      return (
                        <div data-testid="whatsapp-filtered-groups-list" className="space-y-1.5 max-h-40 overflow-y-auto">
                          <p className="text-xs text-gray-500">{filtered.length} grupo(s) encontrados:</p>
                          {filtered.map((group) => (
                            <button
                              type="button"
                              data-testid={`whatsapp-group-option-${group.id}`}
                              key={group.id}
                              onClick={() => selectWhatsAppGroup(group)}
                              className={`w-full text-left p-2 border rounded-lg cursor-pointer transition-all ${
                                whatsappConfig.grupo_id === group.id
                                  ? 'border-green-500 bg-green-50'
                                  : 'border-gray-200 hover:border-green-300'
                              }`}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <span className="font-medium text-sm truncate">{group.name}</span>
                                <span className="text-xs text-gray-500 flex-shrink-0">{group.participants} part.</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      );
                    }

                    return whatsappGroupSearch.length === 0 ? (
                      <p data-testid="whatsapp-group-search-hint" className="text-xs text-gray-400 italic">Escribí para ver todos los grupos disponibles</p>
                    ) : null;
                  })()}
                </div>

                {whatsappConfig.grupo_id && (whatsappGroupClients.length > 0 || whatsappStaffMembers.length > 0) && (
                  <div data-testid="whatsapp-participants-section" className="space-y-3">
                    {whatsappStaffMembers.length > 0 && (
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Staff / Bot ({whatsappStaffMembers.length})</label>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {whatsappStaffMembers.map((s) => (
                            <span key={s.phone || s.lid} data-testid={`whatsapp-staff-member-${s.phone || s.lid}`} className="inline-flex flex-col px-2.5 py-1 rounded-lg text-xs bg-gray-200 text-gray-700">
                              <span>{s.is_admin ? 'ADMIN' : 'STAFF'} {s.phone_display}</span>
                              {s.lid && <span className="text-[10px] text-gray-500 font-mono">LID {s.lid.slice(0, 12)}...</span>}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Números autorizados del cliente ({whatsappGroupClients.length})</label>
                        {whatsappGroupClients.length > 0 && (
                          <Button data-testid="whatsapp-select-all-clients-button" type="button" variant="ghost" size="sm" onClick={selectAllClientNumbers} className="text-xs text-green-600 h-6 px-2">
                            Seleccionar todos con teléfono
                          </Button>
                        )}
                      </div>
                      {whatsappGroupClients.length === 0 ? (
                        <p data-testid="whatsapp-no-client-participants" className="text-sm text-amber-600 italic mt-1">No se detectaron números de cliente (solo staff)</p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {whatsappGroupClients.map((c) => (
                            <button
                              type="button"
                              data-testid={`whatsapp-client-number-${c.phone || c.lid}`}
                              key={c.phone || c.lid}
                              onClick={() => toggleClientNumber(c.phone)}
                              disabled={!c.phone}
                              className={`px-2.5 py-1 rounded-lg cursor-pointer transition-all text-xs font-medium text-left ${
                                !c.phone ? 'bg-amber-50 text-amber-700 border border-amber-200 cursor-not-allowed' :
                                whatsappConfig.numeros_autorizados?.includes(c.phone)
                                  ? 'bg-green-500 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              <span className="block">{c.phone_display}</span>
                              {c.lid && <span className="block text-[10px] opacity-75 font-mono">LID {c.lid.slice(0, 14)}...</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {whatsappConfig.grupo_id && whatsappConfig.numeros_autorizados?.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p data-testid="whatsapp-config-preflight-title" className="font-semibold">Prueba de configuración sin enviar mensajes</p>
                        <p className="text-xs text-blue-700 mt-1">Valida bot, grupo, conflicto de cliente y números autorizados. No toca WhatsApp real.</p>
                      </div>
                      <Button
                        data-testid="whatsapp-config-test-button"
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => testWhatsAppConfig()}
                        disabled={whatsappConfigTesting || whatsappConfigLoading}
                        className="border-blue-400 text-blue-700 hover:bg-blue-100"
                      >
                        {whatsappConfigTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Activity className="w-4 h-4 mr-2" />}
                        Probar
                      </Button>
                    </div>
                    {whatsappConfigPreflight && (() => {
                      const checks = whatsappConfigPreflight.checks || [];
                      const hasWarning = checks.some(c => c.ok && c.warning);
                      const isOk = whatsappConfigPreflight.ok && !hasWarning;
                      const isFail = !whatsappConfigPreflight.ok;
                      const containerClass = isFail
                        ? 'bg-red-50 border-red-200 text-red-800'
                        : hasWarning
                          ? 'bg-amber-50 border-amber-200 text-amber-900'
                          : 'bg-green-50 border-green-200 text-green-800';
                      const headerLabel = isFail
                        ? 'Hay puntos a corregir'
                        : hasWarning
                          ? 'Configuración válida con advertencias'
                          : 'Configuración lista para guardar';
                      const HeaderIcon = isFail ? AlertTriangle : (hasWarning ? AlertCircle : CheckCircle);
                      return (
                        <div data-testid="whatsapp-config-preflight-result" className={`rounded-lg border p-2 ${containerClass}`}>
                          <div className="flex items-center gap-2 font-semibold text-sm">
                            <HeaderIcon className="w-4 h-4" />
                            {headerLabel}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 mt-2">
                            {checks.map(check => {
                              const isCheckWarn = check.ok && check.warning;
                              const Icon = !check.ok ? AlertCircle : (isCheckWarn ? AlertTriangle : CheckCircle);
                              const iconColor = !check.ok ? 'text-red-600' : (isCheckWarn ? 'text-amber-600' : 'text-green-600');
                              return (
                                <div key={check.key} data-testid={`whatsapp-config-check-${check.key}`} className="flex items-start gap-2 text-xs">
                                  <Icon className={`w-3 h-3 mt-0.5 shrink-0 ${iconColor}`} />
                                  <div className="min-w-0">
                                    <span className="font-medium">{check.label}: </span>
                                    <span className="break-words">{check.detail}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                <div className="flex justify-between gap-3 pt-3 border-t">
                  <Button
                    data-testid="whatsapp-config-delete-button"
                    variant="outline"
                    onClick={deleteWhatsAppConfig}
                    disabled={whatsappConfigLoading || !whatsappConfig.grupo_id}
                    className="text-red-600 hover:bg-red-50"
                  >
                    Eliminar
                  </Button>
                  <div className="flex gap-2">
                    <Button data-testid="whatsapp-config-cancel-button" variant="outline" onClick={() => setShowWhatsAppConfigDialog(false)}>
                      Cancelar
                    </Button>
                    <Button
                      data-testid="whatsapp-config-save-button"
                      onClick={saveWhatsAppConfig}
                      disabled={whatsappConfigLoading || whatsappConfigTesting || !whatsappConfig.grupo_id}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {whatsappConfigLoading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</>
                      ) : (
                        'Guardar configuración única'
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* === DIALOG CONFIGURAR CRON FLYERS === */}
      <Dialog open={showFlyerCronDialog} onOpenChange={setShowFlyerCronDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-orange-500" />
              Envio Automatico de Flyers
            </DialogTitle>
            <DialogDescription>
              {flyerCronTarget ? flyerCronTarget.name : ""}
            </DialogDescription>
          </DialogHeader>

          {flyerCronLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
              <span className="ml-2 text-sm text-gray-500">Cargando...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Estado actual */}
              {flyerCronConfig ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-800">
                      Activo: todos los dias a las {String(flyerCronConfig.hour).padStart(2,"0")}:{String(flyerCronConfig.minute).padStart(2,"0")} ARG
                    </span>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <span className="text-sm text-gray-500">Sin programacion activa</span>
                </div>
              )}

              {/* Selector de horario */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Horario de envio (Argentina)</label>
                <div className="flex items-center gap-2">
                  <select
                    value={flyerCronHour}
                    onChange={(e) => { setFlyerCronHour(parseInt(e.target.value)); setFlyerCronConflict(null); }}
                    className="border rounded-md px-3 py-2 text-sm w-20"
                  >
                    {Array.from({length: 24}, (_, i) => (
                      <option key={i} value={i}>{String(i).padStart(2,"0")}</option>
                    ))}
                  </select>
                  <span className="text-lg font-bold">:</span>
                  <select
                    value={flyerCronMinute}
                    onChange={(e) => { setFlyerCronMinute(parseInt(e.target.value)); setFlyerCronConflict(null); }}
                    className="border rounded-md px-3 py-2 text-sm w-20"
                  >
                    {Array.from({length: 60}, (_, i) => (
                      <option key={i} value={i}>{String(i).padStart(2,"0")}</option>
                    ))}
                  </select>
                  <span className="text-xs text-gray-400 ml-1">hs ARG</span>
                </div>
              </div>

              {/* Conflicto */}
              {flyerCronConflict && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700 font-medium">{flyerCronConflict.message}</p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2 text-xs border-red-300 text-red-600 hover:bg-red-100"
                    onClick={() => {
                      setFlyerCronHour(flyerCronConflict.suggested_hour);
                      setFlyerCronMinute(flyerCronConflict.suggested_minute);
                      setFlyerCronConflict(null);
                    }}
                  >
                    Usar sugerido: {flyerCronConflict.suggested_time}
                  </Button>
                </div>
              )}

              {/* Otros crons activos */}
              {allFlyerCrons.filter(c => c.cliente_id !== (flyerCronTarget?.id)).length > 0 && (
                <div className="border rounded-lg p-3 bg-gray-50">
                  <p className="text-xs font-medium text-gray-600 mb-2">Horarios ocupados:</p>
                  <div className="flex flex-wrap gap-1">
                    {allFlyerCrons
                      .filter(c => c.cliente_id !== (flyerCronTarget?.id))
                      .sort((a, b) => (a.hour * 60 + a.minute) - (b.hour * 60 + b.minute))
                      .map((c, i) => (
                        <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                          {String(c.hour).padStart(2,"0")}:{String(c.minute).padStart(2,"0")} - {c.cliente_name}
                        </span>
                      ))}
                  </div>
                </div>
              )}

              {/* Botones */}
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={saveFlyerCron}
                  disabled={flyerCronSaving}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                >
                  {flyerCronSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Clock className="w-4 h-4 mr-1" />}
                  {flyerCronConfig ? "Actualizar Cron" : "Activar Cron"}
                </Button>
                {flyerCronConfig && (
                  <Button
                    onClick={deleteFlyerCron}
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* Separador + Enviar ahora / Adelantar mañana */}
              <div className="border-t pt-3 space-y-2">
                <Button
                  data-testid="flyer-send-today-btn"
                  onClick={() => enviarFlyersAhora(0)}
                  disabled={enviandoFlyers[flyerCronTarget?.id] || enviandoFlyers[`${flyerCronTarget?.id}_manana`]}
                  variant="outline"
                  className="w-full border-orange-300 text-orange-600 hover:bg-orange-50"
                >
                  {enviandoFlyers[flyerCronTarget?.id] ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Enviando...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-1" /> Enviar flyers de HOY (manual)</>
                  )}
                </Button>
                <Button
                  data-testid="flyer-send-tomorrow-btn"
                  onClick={() => enviarFlyersAhora(1)}
                  disabled={enviandoFlyers[flyerCronTarget?.id] || enviandoFlyers[`${flyerCronTarget?.id}_manana`]}
                  variant="outline"
                  className="w-full border-blue-300 text-blue-600 hover:bg-blue-50"
                >
                  {enviandoFlyers[`${flyerCronTarget?.id}_manana`] ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Enviando...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-1" /> ⏩ Adelantar flyers de MAÑANA</>
                  )}
                </Button>
                <p className="text-[11px] text-gray-500 text-center">
                  Útil cuando querés enviar la promoción del día siguiente con anticipación.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal configurar PPPoker por cliente */}
      <Dialog open={showPppokerConfigModal} onOpenChange={setShowPppokerConfigModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-violet-600" />
              Configurar PPPoker - {pppokerConfigTarget?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Club ID</label>
              <input
                type="number"
                value={pppokerConfig.club_id}
                onChange={e => setPppokerConfig(p => ({...p, club_id: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm"
                placeholder="Ej: 2534409"
              />
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Agentes</label>
                <button
                  onClick={() => setPppokerConfig(p => ({...p, agentes: [...p.agentes, { uid: '', nombre: '', rakeback_pct: '15', spin_pct: '10' }]}))}
                  className="text-xs px-2 py-1 bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 rounded hover:bg-violet-200 dark:hover:bg-violet-900/50"
                >
                  + Agregar agente
                </button>
              </div>
              {pppokerConfig.agentes && pppokerConfig.agentes.map((ag, idx) => (
                <div key={idx} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 mb-2 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-500">Agente {idx + 1}</span>
                    {pppokerConfig.agentes.length > 1 && (
                      <button
                        onClick={() => setPppokerConfig(p => ({...p, agentes: p.agentes.filter((_, i) => i !== idx)}))}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        Eliminar
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <input
                      type="number"
                      value={ag.uid}
                      onChange={e => { const a = [...pppokerConfig.agentes]; a[idx] = {...a[idx], uid: e.target.value}; setPppokerConfig(p => ({...p, agentes: a})); }}
                      className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm"
                      placeholder="UID"
                    />
                    <input
                      type="text"
                      value={ag.nombre}
                      onChange={e => { const a = [...pppokerConfig.agentes]; a[idx] = {...a[idx], nombre: e.target.value}; setPppokerConfig(p => ({...p, agentes: a})); }}
                      className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm"
                      placeholder="Nombre (ej: NLH)"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-500">% RB cuota</label>
                      <input
                        type="number"
                        step="0.1"
                        value={ag.rakeback_pct}
                        onChange={e => { const a = [...pppokerConfig.agentes]; a[idx] = {...a[idx], rakeback_pct: e.target.value}; setPppokerConfig(p => ({...p, agentes: a})); }}
                        className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm"
                        placeholder="15"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">% SpinUp</label>
                      <input
                        type="number"
                        step="0.1"
                        value={ag.spin_pct}
                        onChange={e => { const a = [...pppokerConfig.agentes]; a[idx] = {...a[idx], spin_pct: e.target.value}; setPppokerConfig(p => ({...p, agentes: a})); }}
                        className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm"
                        placeholder="10"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <button
              onClick={() => setShowPppokerConfigModal(false)}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancelar
            </button>
            <button
              onClick={() => savePppokerConfig(false)}
              disabled={pppokerConfigSaving || !pppokerConfig.club_id || !pppokerConfig.agentes?.some(a => a.uid)}
              className="px-4 py-2 text-sm text-white bg-gray-600 hover:bg-gray-700 rounded-lg disabled:opacity-50"
            >
              Solo guardar
            </button>
            <button
              onClick={() => savePppokerConfig(true)}
              disabled={pppokerConfigSaving || !pppokerConfig.club_id || !pppokerConfig.agentes?.some(a => a.uid)}
              className="px-4 py-2 text-sm text-white bg-violet-600 hover:bg-violet-700 rounded-lg disabled:opacity-50"
            >
              {pppokerConfigSaving ? 'Guardando...' : 'Guardar y enviar'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal preview estado de cuenta + PPPoker */}
      <Dialog open={showPppokerPreview} onOpenChange={setShowPppokerPreview}>
        <DialogContent className="max-w-lg p-0" style={{maxHeight: '85vh', display: 'flex', flexDirection: 'column', gap: 0}}>
          <DialogHeader className="p-6 pb-3" style={{flexShrink: 0}}>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-violet-600" />
              Preview - {pppokerPreview?.clienteName}
            </DialogTitle>
          </DialogHeader>
          <div style={{flex: 1, overflowY: 'auto', minHeight: 0}} className="px-6">
          {pppokerPreviewLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
              <span className="ml-3 text-gray-500">Consultando datos PPPoker...</span>
            </div>
          ) : pppokerPreview ? (
            <div className="space-y-3">
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 font-mono text-sm whitespace-pre-wrap border border-gray-200 dark:border-gray-700">
                {pppokerPreview.mensaje}
              </div>
              <div className="bg-violet-50 dark:bg-violet-900/20 rounded-lg p-3 text-xs space-y-2">
                <p className="font-semibold text-violet-700 dark:text-violet-300">Detalle por agente:</p>
                {pppokerPreview.pppoker?.agentes?.map((ag, i) => (
                  <div key={i} className="border-b border-violet-200 dark:border-violet-700 pb-1 mb-1 last:border-0">
                    <p className="font-medium">{ag.nombre} (UID {ag.uid}):</p>
                    <p className="pl-2">Ganancias: {ag.ganancias?.toLocaleString('es-AR', {minimumFractionDigits: 2})} PP</p>
                    {ag.rakeback_pct > 0 && <p className="pl-2">RB ({ag.rakeback_pct}%): {ag.rakeback?.toLocaleString('es-AR', {minimumFractionDigits: 2})} PP</p>}
                    {ag.spin_pct > 0 && <p className="pl-2">Spin ({ag.spin_pct}%): {ag.spin?.toLocaleString('es-AR', {minimumFractionDigits: 2})} PP</p>}
                  </div>
                ))}
                <p className="font-bold pt-1 border-t border-violet-200 dark:border-violet-700">Total estimado: {pppokerPreview.pppoker?.total_estimado?.toLocaleString('es-AR', {minimumFractionDigits: 2})} PP</p>
              </div>
            </div>
          ) : null}
          </div>
          <div className="flex gap-2 justify-end p-4 border-t border-gray-200 dark:border-gray-700 bg-background" style={{flexShrink: 0}}>
            <button
              onClick={() => setShowPppokerPreview(false)}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancelar
            </button>
            <button
              onClick={() => pppokerPreview && editPppokerConfig(pppokerPreview.clienteId, pppokerPreview.clienteName)}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1"
            >
              <Settings className="w-3.5 h-3.5" /> Editar config
            </button>
            <button
              onClick={confirmarEnvioPppoker}
              disabled={!pppokerPreview || pppokerPreviewLoading}
              className="px-4 py-2 text-sm text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50 flex items-center gap-1"
            >
              <Send className="w-3.5 h-3.5" /> Enviar por WhatsApp
            </button>
          </div>
        </DialogContent>
      </Dialog>
      {/* WhatsApp dialog removed */}
      {/* WhatsApp dialog removed for preview compatibility */}

      {/* ======= MODAL DE COMPROBANTES PERDIDOS / RECUPERADOS ======= */}
      <Dialog
        open={!!gapsModalCliente}
        onOpenChange={(open) => { if (!open) setGapsModalCliente(null); }}
      >
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="gaps-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Comprobantes perdidos y recuperados
              {gapsModalCliente && (
                <span className="text-sm font-normal text-gray-500">
                  • {gapsModalCliente.name}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              Mensajes de WhatsApp con comprobante que el bot no pudo procesar. Recuperá el archivo o creá un placeholder para completar manualmente.
            </DialogDescription>
          </DialogHeader>

          {gapsModalCliente && (() => {
            const clienteId = gapsModalCliente.id;
            const data = gapsByClient[clienteId] || {};
            const gaps = data.gaps || [];
            const recoveredReceipts = receipts.filter(r =>
              r.recovered === true && (selectedCliente === clienteId || true)
            );

            return (
              <div className="space-y-4">
                {/* SECCION GAPS PENDIENTES */}
                <div>
                  <h3 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-600" />
                    Pendientes de recuperar ({gaps.length})
                  </h3>
                  {gaps.length === 0 ? (
                    <p className="text-sm text-gray-500 italic py-2">
                      Sin comprobantes perdidos pendientes. 🎉
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {gaps.map(gap => {
                        const isRecovering = recoveringGapId === gap.wa_message_id;
                        const fechaStr = new Date(gap.timestamp).toLocaleString('es-AR', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        });
                        return (
                          <div
                            key={gap.wa_message_id}
                            className="border border-yellow-300 bg-yellow-50 rounded-lg p-3 flex flex-wrap items-center gap-2"
                            data-testid={`modal-gap-${gap.wa_message_id}`}
                          >
                            <div className="flex-1 min-w-[200px]">
                              <div className="text-sm font-medium text-gray-800">
                                {fechaStr}
                              </div>
                              <div className="text-xs text-gray-600">
                                {gap.from_name || 'sin nombre'} {gap.group_name && `· ${gap.group_name}`}
                              </div>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs border-orange-300 text-orange-700 hover:bg-orange-100"
                                disabled={isRecovering || !canWrite}
                                onClick={() => handleRecoverGap(gap, clienteId)}
                              >
                                {isRecovering
                                  ? <Loader2 className="w-3 h-3 animate-spin" />
                                  : <RefreshCw className="w-3 h-3 mr-1" />}
                                Recuperar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs border-gray-300"
                                disabled={isRecovering || !canWrite}
                                onClick={() => handleCreatePlaceholder(gap, clienteId)}
                              >
                                <FileQuestion className="w-3 h-3 mr-1" />
                                Placeholder
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs border-red-300 text-red-700 hover:bg-red-50"
                                disabled={isRecovering || !canWrite}
                                onClick={() => handleDismissGap(gap, clienteId)}
                                title="Descartar - no crea movimiento"
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                Descartar
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* SECCION RECUPERADOS (historial) */}
                <div className="pt-3 border-t">
                  <h3 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                    <span className="text-orange-600">🟠</span>
                    Recuperados ({recoveredReceipts.length})
                  </h3>
                  {recoveredReceipts.length === 0 ? (
                    <p className="text-xs text-gray-500 italic py-1">
                      Aún no se recuperó ningún comprobante para este cliente.
                    </p>
                  ) : (
                    <div className="space-y-1 max-h-60 overflow-y-auto">
                      {recoveredReceipts.slice(0, 50).map(r => {
                        const fechaStr = new Date(r.created_at || r.date).toLocaleString('es-AR', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        });
                        return (
                          <div
                            key={r.id}
                            className="text-xs flex items-center gap-2 py-1 px-2 bg-orange-50 rounded"
                            data-testid={`modal-recovered-${r.id}`}
                          >
                            <span className="text-orange-600 flex-shrink-0">🟠</span>
                            <span className="font-medium">{fechaStr}</span>
                            <span className="text-gray-600">{formatAmount(r.amount || 0)}</span>
                            <span className="text-gray-500 truncate">
                              {r.status === 'placeholder' ? '📝 Placeholder' : (r.origin_holder || '')}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {data.since && (
                  <div className="text-xs text-gray-400 pt-2 border-t">
                    Solo se muestran gaps desde {new Date(data.since).toLocaleString('es-AR')}
                  </div>
                )}
              </div>
            );
          })()}

          <div className="flex justify-end pt-4">
            <Button
              variant="outline"
              onClick={() => setGapsModalCliente(null)}
              data-testid="gaps-modal-close"
            >
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* FASE 4 (2026-02): Modal de rechazo con motivo libre */}
      <Dialog open={rejectModalOpen} onOpenChange={(open) => { if (!open) { setRejectModalOpen(false); setPendienteToReject(null); setRejectMotivo(''); } }}>
        <DialogContent className="sm:max-w-md" data-testid="reject-pendiente-modal">
          <DialogHeader>
            <DialogTitle>Rechazar pedido de retiro</DialogTitle>
            <DialogDescription>
              {pendienteToReject && (
                <>
                  <strong>${Number(pendienteToReject.monto).toLocaleString('es-AR')}</strong>
                  {' → '}
                  {pendienteToReject.titular_destino || '—'}
                  <span className="block text-xs text-gray-500 mt-1">{pendienteToReject.destino}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Motivo del rechazo</label>
            <textarea
              value={rejectMotivo}
              onChange={(e) => setRejectMotivo(e.target.value)}
              placeholder="Ej: destino sospechoso, monto excesivo, etc."
              className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              autoFocus
              data-testid="reject-pendiente-motivo"
            />
            <p className="text-[11px] text-gray-500">El cliente verá este motivo en WhatsApp.</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setRejectModalOpen(false)} disabled={rejectingPendiente}>
              Cancelar
            </Button>
            <Button
              onClick={submitReject}
              disabled={rejectingPendiente || !rejectMotivo.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="reject-pendiente-submit"
            >
              {rejectingPendiente ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />Rechazando…</> : 'Rechazar pedido'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      </div>
  );
}
